/**-----------------------------------------------------------------------------------------
* Copyright © 2026 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import * as i0 from '@angular/core';
import { EventEmitter, Injectable, Optional, Directive, Input, HostBinding, Component, forwardRef, isDevMode, ViewContainerRef, ContentChild, Output, ViewChild, ChangeDetectionStrategy, Host, NgModule } from '@angular/core';
import { isDocumentAvailable, Keys, normalizeKeys, anyChanged, hasObservers, KENDO_WEBMCP_HOST, isChanged, guid, ResizeBatchService } from '@progress/kendo-angular-common';
import * as i1 from '@progress/kendo-angular-l10n';
import { ComponentMessages, LocalizationService, L10N_PREFIX } from '@progress/kendo-angular-l10n';
import { Subject, Subscription, of, EMPTY, BehaviorSubject, merge } from 'rxjs';
import { validatePackage } from '@progress/kendo-licensing';
import { getter, setter } from '@progress/kendo-common';
import { chevronDownIcon, chevronRightIcon, chevronLeftIcon, searchIcon, cancelIcon, insertMiddleIcon, insertBottomIcon, insertTopIcon, plusIcon } from '@progress/kendo-svg-icons';
import { CheckBoxComponent, TextBoxComponent, TextBoxPrefixTemplateDirective } from '@progress/kendo-angular-inputs';
import { trigger, transition, style, animate } from '@angular/animations';
import { filter, tap, switchMap, delay, takeUntil, catchError, finalize, take, map } from 'rxjs/operators';
import { NgTemplateOutlet } from '@angular/common';
import { IconWrapperComponent, IconsService } from '@progress/kendo-angular-icons';
import { Draggable } from '@progress/kendo-draggable';
import { DialogContainerService, DialogService, WindowService, WindowContainerService } from '@progress/kendo-angular-dialog';
import { PopupService } from '@progress/kendo-angular-popup';

/**
 * @hidden
 */
const _c0 = ["kendoTreeViewGroup", ""];
const _c1 = (a0, a1) => ({
  $implicit: a0,
  index: a1
});
const _c2 = a0 => ({
  index: a0
});
function _forTrack0($index, $item) {
  return this.trackBy ? this.trackBy($index, $item) : $index;
}
function TreeViewGroupComponent_For_1_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 11);
    i0.ɵɵlistener("click", function TreeViewGroupComponent_For_1_Conditional_2_Template_span_click_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      const node_r3 = ctx_r1.$implicit;
      const ɵ$index_1_r4 = ctx_r1.$index;
      const ctx_r4 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r4.expandNode(ctx_r4.nodeIndex(ɵ$index_1_r4), node_r3, !ctx_r4.isExpanded(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4))));
    });
    i0.ɵɵelement(1, "kendo-icon-wrapper", 12);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const node_r3 = ctx_r1.$implicit;
    const ɵ$index_1_r4 = ctx_r1.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("kendoTreeViewLoading", ctx_r4.nodeIndex(ɵ$index_1_r4));
    i0.ɵɵadvance();
    i0.ɵɵproperty("name", ctx_r4.getFontIcon(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)))("svgIcon", ctx_r4.getSvgIcon(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)));
  }
}
function TreeViewGroupComponent_For_1_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "kendo-checkbox", 13, 0);
    i0.ɵɵlistener("checkedStateChange", function TreeViewGroupComponent_For_1_Conditional_3_Template_kendo_checkbox_checkedStateChange_0_listener() {
      i0.ɵɵrestoreView(_r6);
      const checkbox_r7 = i0.ɵɵreference(1);
      const ɵ$index_1_r4 = i0.ɵɵnextContext().$index;
      const ctx_r4 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r4.checkNode(ctx_r4.nodeIndex(ɵ$index_1_r4), checkbox_r7));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const node_r3 = ctx_r1.$implicit;
    const ɵ$index_1_r4 = ctx_r1.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("disabled", ctx_r4.isItemDisabled(node_r3, ɵ$index_1_r4))("size", ctx_r4.size)("checkedState", ctx_r4.getCheckBoxState(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)))("tabindex", -1)("inputAttributes", ctx_r4.getCheckboxAttributes(node_r3));
  }
}
function TreeViewGroupComponent_For_1_Case_6_ng_template_0_Template(rf, ctx) {}
function TreeViewGroupComponent_For_1_Case_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, TreeViewGroupComponent_For_1_Case_6_ng_template_0_Template, 0, 0, "ng-template", 9);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const node_r3 = ctx_r1.$implicit;
    const ɵ$index_1_r4 = ctx_r1.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r4.nodeTemplateRef)("ngTemplateOutletContext", i0.ɵɵpureFunction2(2, _c1, node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)));
  }
}
function TreeViewGroupComponent_For_1_Case_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const node_r3 = i0.ɵɵnextContext().$implicit;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵtextInterpolate1(" ", ctx_r4.nodeText(node_r3), " ");
  }
}
function TreeViewGroupComponent_For_1_Conditional_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "ul", 10);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    const node_r3 = ctx_r1.$implicit;
    const ɵ$index_1_r4 = ctx_r1.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("size", ctx_r4.size)("nodes", ctx_r4.fetchChildren)("loadOnDemand", ctx_r4.loadOnDemand)("checkboxes", ctx_r4.checkboxes)("expandIcons", ctx_r4.expandIcons)("selectable", ctx_r4.selectable)("touchActions", ctx_r4.touchActions)("children", ctx_r4.children)("hasChildren", ctx_r4.hasChildren)("isChecked", ctx_r4.isChecked)("isDisabled", ctx_r4.isDisabled)("hasCheckbox", ctx_r4.hasCheckbox)("disabled", ctx_r4.isItemDisabled(node_r3, ɵ$index_1_r4))("expandDisabledNodes", ctx_r4.expandDisabledNodes)("isExpanded", ctx_r4.isExpanded)("isSelected", ctx_r4.isSelected)("isVisible", ctx_r4.isVisible)("nodeTemplateRef", ctx_r4.nodeTemplateRef)("loadMoreButtonTemplateRef", ctx_r4.loadMoreButtonTemplateRef)("parentIndex", ctx_r4.nodeIndex(ɵ$index_1_r4))("parentDataItem", node_r3)("textField", ctx_r4.nextFields)("loadMoreService", ctx_r4.loadMoreService)("@toggle", true)("trackBy", ctx_r4.trackBy)("disableParentNodesOnly", ctx_r4.disableParentNodesOnly);
  }
}
function TreeViewGroupComponent_For_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 3)(1, "div", 4);
    i0.ɵɵconditionalCreate(2, TreeViewGroupComponent_For_1_Conditional_2_Template, 2, 3, "span", 5);
    i0.ɵɵconditionalCreate(3, TreeViewGroupComponent_For_1_Conditional_3_Template, 2, 5, "kendo-checkbox", 6);
    i0.ɵɵelementStart(4, "span", 7)(5, "span", 8);
    i0.ɵɵconditionalCreate(6, TreeViewGroupComponent_For_1_Case_6_Template, 1, 5, null, 9)(7, TreeViewGroupComponent_For_1_Case_7_Template, 1, 1);
    i0.ɵɵelementEnd()()();
    i0.ɵɵconditionalCreate(8, TreeViewGroupComponent_For_1_Conditional_8_Template, 1, 26, "ul", 10);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    let tmp_37_0;
    const node_r3 = ctx.$implicit;
    const ɵ$index_1_r4 = ctx.$index;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("k-hidden", !ctx_r4.isVisible(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)));
    i0.ɵɵproperty("dataItem", node_r3)("index", ctx_r4.nodeIndex(ɵ$index_1_r4))("parentDataItem", ctx_r4.parentDataItem)("parentIndex", ctx_r4.parentIndex)("loadOnDemand", ctx_r4.loadOnDemand)("checkable", ctx_r4.checkboxes)("isChecked", ctx_r4.isChecked(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)))("isDisabled", ctx_r4.isItemDisabled(node_r3, ɵ$index_1_r4))("isVisible", ctx_r4.isVisible(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)))("expandable", ctx_r4.expandIcons && ctx_r4.hasChildren(node_r3))("isExpanded", ctx_r4.isExpanded(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)))("selectable", ctx_r4.selectable)("isSelected", ctx_r4.isSelected(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)));
    i0.ɵɵattribute("aria-setsize", ctx_r4.totalNodesCount)("data-treeindex", ctx_r4.nodeIndex(ɵ$index_1_r4));
    i0.ɵɵadvance();
    i0.ɵɵclassProp("k-disabled", ctx_r4.isContentDisabled(node_r3, ɵ$index_1_r4));
    i0.ɵɵproperty("dataItem", node_r3)("index", ctx_r4.nodeIndex(ɵ$index_1_r4))("initialSelection", ctx_r4.isSelected(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)))("isSelected", ctx_r4.isSelected);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r4.expandIcons && ctx_r4.hasChildren(node_r3) ? 2 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r4.checkboxes && ctx_r4.hasCheckbox(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)) ? 3 : -1);
    i0.ɵɵadvance();
    i0.ɵɵstyleProp("touch-action", ctx_r4.touchActions ? "" : "none");
    i0.ɵɵproperty("id", ctx_r4.nodeIndex(ɵ$index_1_r4));
    i0.ɵɵattribute("data-treeindex", ctx_r4.nodeIndex(ɵ$index_1_r4));
    i0.ɵɵadvance(2);
    i0.ɵɵconditional((tmp_37_0 = ctx_r4.hasTemplate) === true ? 6 : 7);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r4.isExpanded(node_r3, ctx_r4.nodeIndex(ɵ$index_1_r4)) && ctx_r4.hasChildren(node_r3) ? 8 : -1);
  }
}
function TreeViewGroupComponent_Conditional_2_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 16);
  }
}
function TreeViewGroupComponent_Conditional_2_Conditional_5_ng_template_0_Template(rf, ctx) {}
function TreeViewGroupComponent_Conditional_2_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, TreeViewGroupComponent_Conditional_2_Conditional_5_ng_template_0_Template, 0, 0, "ng-template", 9);
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r4.loadMoreButtonTemplateRef)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c2, ctx_r4.loadMoreButtonIndex));
  }
}
function TreeViewGroupComponent_Conditional_2_Conditional_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r4.loadMoreTitle, " ");
  }
}
function TreeViewGroupComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 14)(1, "div", 15);
    i0.ɵɵconditionalCreate(2, TreeViewGroupComponent_Conditional_2_Conditional_2_Template, 1, 0, "span", 16);
    i0.ɵɵelementStart(3, "span", 17)(4, "span", 8);
    i0.ɵɵconditionalCreate(5, TreeViewGroupComponent_Conditional_2_Conditional_5_Template, 1, 4, null, 9);
    i0.ɵɵconditionalCreate(6, TreeViewGroupComponent_Conditional_2_Conditional_6_Template, 1, 1);
    i0.ɵɵelementEnd()()()();
  }
  if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("k-treeview-load-more-checkboxes-container", ctx_r4.checkboxes);
    i0.ɵɵproperty("selectable", false)("checkable", false)("expandable", false)("index", ctx_r4.loadMoreButtonIndex)("parentDataItem", ctx_r4.parentDataItem)("parentIndex", ctx_r4.parentIndex);
    i0.ɵɵattribute("data-treeindex", ctx_r4.loadMoreButtonIndex);
    i0.ɵɵadvance();
    i0.ɵɵproperty("index", ctx_r4.loadMoreButtonIndex);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r4.loadingMoreNodes ? 2 : -1);
    i0.ɵɵadvance();
    i0.ɵɵattribute("data-treeindex", ctx_r4.loadMoreButtonIndex);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r4.loadMoreButtonTemplateRef ? 5 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!ctx_r4.loadMoreButtonTemplateRef ? 6 : -1);
  }
}
const _c3 = ["assetsContainer"];
const _c4 = ["searchbox"];
function TreeViewComponent_Conditional_1_ng_template_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon-wrapper", 7);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("svgIcon", ctx_r1.searchIcon);
  }
}
function TreeViewComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 3)(1, "kendo-textbox", 5, 1);
    i0.ɵɵlistener("valueChange", function TreeViewComponent_Conditional_1_Template_kendo_textbox_valueChange_1_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.filterChange.emit($event));
    });
    i0.ɵɵtemplate(3, TreeViewComponent_Conditional_1_ng_template_3_Template, 1, 1, "ng-template", 6);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("size", ctx_r1.size)("value", ctx_r1.filter)("clearButton", true)("placeholder", ctx_r1.filterInputPlaceholder);
  }
}
const _c5 = (a0, a1, a2, a3) => ({
  text: a0,
  action: a1,
  sourceItem: a2,
  destinationItem: a3
});
function DragClueComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "kendo-icon-wrapper", 1);
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("name", ctx_r0.statusIconClass)("svgIcon", ctx_r0.statusSVGIcon);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx_r0.text);
  }
}
function DragClueComponent_Conditional_1_ng_template_0_Template(rf, ctx) {}
function DragClueComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DragClueComponent_Conditional_1_ng_template_0_Template, 0, 0, "ng-template", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.template)("ngTemplateOutletContext", i0.ɵɵpureFunction4(2, _c5, ctx_r0.text, ctx_r0.action, ctx_r0.sourceItem, ctx_r0.destinationItem));
  }
}
const _c6 = (a0, a1, a2) => ({
  action: a0,
  sourceItem: a1,
  destinationItem: a2
});
function DropHintComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 0);
    i0.ɵɵelement(1, "div", 2)(2, "div", 3);
    i0.ɵɵelementEnd();
  }
}
function DropHintComponent_Conditional_1_ng_template_0_Template(rf, ctx) {}
function DropHintComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DropHintComponent_Conditional_1_ng_template_0_Template, 0, 0, "ng-template", 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.template)("ngTemplateOutletContext", i0.ɵɵpureFunction3(2, _c6, ctx_r0.action, ctx_r0.sourceItem, ctx_r0.destinationItem));
  }
}
const packageMetadata = {
  name: '@progress/kendo-angular-treeview',
  productName: 'Kendo UI for Angular',
  productCode: 'KENDOUIANGULAR',
  productCodes: ['KENDOUIANGULAR'],
  publishDate: 1780593787,
  version: '24.1.0',
  licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/'
};

/**
 * @hidden
 */
class DataChangeNotificationService {
  changes = new EventEmitter();
  notify() {
    this.changes.emit();
  }
}

/**
 * @hidden
 */
const hasChildren = () => false;
/**
 * @hidden
 */
const isChecked = () => 'none';
/**
 * @hidden
 */
const isDisabled = () => false;
/**
 * @hidden
 */
const hasCheckbox = () => true;
/**
 * @hidden
 */
const isExpanded = () => true;
/**
 * @hidden
 */
const isSelected = () => false;
/**
 * @hidden
 */
const isVisible = () => true;
/**
 * @hidden
 */
const trackBy = (_, item) => item;

/**
 * @hidden
 */
class ExpandStateService {
  changes = new Subject();
  expand(index, dataItem) {
    this.changes.next({
      dataItem,
      index,
      expand: true
    });
  }
  collapse(index, dataItem) {
    this.changes.next({
      dataItem,
      index,
      expand: false
    });
  }
  static ɵfac = function ExpandStateService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ExpandStateService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ExpandStateService,
    factory: ExpandStateService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExpandStateService, [{
    type: Injectable
  }], null, null);
})();

/**
 * @hidden
 */
class IndexBuilderService {
  INDEX_SEPARATOR = '_';
  nodeIndex(index = '', parentIndex = '') {
    return `${parentIndex}${parentIndex ? this.INDEX_SEPARATOR : ''}${index}`;
  }
  indexForLevel(index, level) {
    return index.split(this.INDEX_SEPARATOR).slice(0, level).join(this.INDEX_SEPARATOR);
  }
  lastLevelIndex(index = '') {
    const parts = index.split(this.INDEX_SEPARATOR);
    if (!parts.length) {
      return NaN;
    }
    return parseInt(parts[parts.length - 1], 10);
  }
  level(index) {
    return index.split(this.INDEX_SEPARATOR).length;
  }
  static ɵfac = function IndexBuilderService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || IndexBuilderService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: IndexBuilderService,
    factory: IndexBuilderService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(IndexBuilderService, [{
    type: Injectable
  }], null, null);
})();

/**
 * @hidden
 */
class LoadingNotificationService {
  changes = new Subject();
  notifyLoaded(index) {
    this.changes.next(index);
  }
  static ɵfac = function LoadingNotificationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LoadingNotificationService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: LoadingNotificationService,
    factory: LoadingNotificationService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoadingNotificationService, [{
    type: Injectable
  }], null, null);
})();
const focusableRegex = /^(?:a|input|select|option|textarea|button|object)$/i;
/**
 * @hidden
 */
const match = (element, selector) => {
  const matcher = element.matches || element.msMatchesSelector || element.webkitMatchesSelector;
  if (!matcher) {
    return false;
  }
  return matcher.call(element, selector.toLowerCase());
};
/**
 * @hidden
 */
const closestWithMatch = (element, selector) => {
  if (!document.documentElement.contains(element)) {
    return null;
  }
  let parent = element;
  while (parent !== null && parent.nodeType === 1) {
    if (match(parent, selector)) {
      return parent;
    }
    parent = parent.parentElement || parent.parentNode;
  }
  return null;
};
/**
 * @hidden
 */
const noop = () => {};
/**
 * @hidden
 */
const isPresent = value => value !== null && value !== undefined;
/**
 * @hidden
 */
const isBlank = value => value === null || value === undefined;
/**
 * @hidden
 */
const isArray = value => Array.isArray(value);
/**
 * @hidden
 */
const isNullOrEmptyString = value => isBlank(value) || value.trim().length === 0;
/**
 * @hidden
 */
const isBoolean = value => typeof value === 'boolean';
/**
 * @hidden
 */
const closestNode = element => {
  const selector = 'li.k-treeview-item';
  if (!isDocumentAvailable()) {
    return null;
  }
  if (element.closest) {
    return element.closest(selector);
  } else {
    return closestWithMatch(element, selector);
  }
};
/**
 * @hidden
 */
const isFocusable = element => {
  if (element.tagName) {
    const tagName = element.tagName.toLowerCase();
    const tabIndex = element.getAttribute('tabIndex');
    const skipTab = tabIndex === '-1';
    let focusable = tabIndex !== null && !skipTab;
    if (focusableRegex.test(tagName)) {
      focusable = !element.disabled && !skipTab;
    }
    return focusable;
  }
  return false;
};
/**
 * @hidden
 */
const isContent = element => {
  const scopeSelector = '.k-treeview-leaf:not(.k-treeview-load-more-button),.k-treeview-item-content,.k-treeview-item,.k-treeview';
  if (!isDocumentAvailable()) {
    return null;
  }
  if (closestWithMatch(element, '.k-treeview-toggle')) {
    return false;
  }
  if (closestWithMatch(element, 'kendo-checkbox') || element.tagName?.toLowerCase() === 'input' && element.type === 'checkbox') {
    return false;
  }
  let node = element;
  while (node && !match(node, scopeSelector)) {
    node = node.parentNode;
  }
  if (node) {
    if (isLoadMoreButton(element)) {
      return false;
    }
    return match(node, '.k-treeview-leaf:not(.k-treeview-load-more-button)') || match(node, '.k-treeview-item-content');
  }
};
/**
 * @hidden
 *
 * Returns the nested .k-treeview-leaf:not(.k-treeview-load-more-button) element.
 * If the passed parent item is itself a content node, it is returned.
 */
const getContentElement = parent => {
  if (!isPresent(parent)) {
    return null;
  }
  const selector = '.k-treeview-leaf:not(.k-treeview-load-more-button)';
  if (match(parent, selector)) {
    return parent;
  }
  return parent.querySelector(selector);
};
/**
 * @hidden
 */
const isLoadMoreButton = element => {
  return isPresent(closestWithMatch(element, '.k-treeview-leaf.k-treeview-load-more-button'));
};
/**
 * @hidden
 */
const closest = (node, predicate) => {
  while (node && !predicate(node)) {
    node = node.parentNode;
  }
  return node;
};
/**
 * @hidden
 */
const hasParent = (element, container) => {
  return Boolean(closest(element, node => node === container));
};
/**
 * @hidden
 */
const focusableNode = element => element.nativeElement.querySelector('li[tabindex="0"]');
/**
 * @hidden
 */
const hasActiveNode = (target, node) => {
  const closestItem = node || closestNode(target);
  return closestItem && (closestItem === target || target.tabIndex < 0);
};
/**
 * @hidden
 */
const nodeId = node => node ? node.getAttribute('data-treeindex') : '';
/**
 * @hidden
 */
const nodeIndex = item => (item || {}).index;
/**
 * @hidden
 */
const dataItemsEqual = (first, second) => {
  if (!isPresent(first) && !isPresent(second)) {
    return true;
  }
  return isPresent(first) && isPresent(second) && first.item.dataItem === second.item.dataItem;
};
/**
 * @hidden
 */
const getDataItem = lookup => {
  if (!isPresent(lookup)) {
    return lookup;
  }
  return lookup.item.dataItem;
};
/**
 * @hidden
 */
const isArrayWithAtLeastOneItem = v => v && Array.isArray(v) && v.length !== 0;
/**
 * @hidden
 * A recursive tree-filtering algorithm that returns:
 * - all child nodes of matching nodes
 * - a chain parent nodes from the match to the root node
 */
const filterTree = (items, term, {
  operator,
  ignoreCase,
  mode
}, textField, depth = 0) => {
  const field = typeof textField === "string" ? textField : textField[depth];
  items.forEach(wrapper => {
    const matcher = typeof operator === "string" ? matchByFieldAndCase(field, operator, ignoreCase) : operator;
    const isMatch = matcher(wrapper.dataItem, term);
    wrapper.isMatch = isMatch;
    wrapper.visible = isMatch;
    wrapper.containsMatches = false;
    if (isMatch) {
      setParentChain(wrapper.parent);
    }
    if (wrapper.children && wrapper.children.length > 0) {
      if (mode === "strict" || !isMatch) {
        filterTree(wrapper.children, term, {
          operator,
          ignoreCase,
          mode
        }, textField, depth + 1);
      } else {
        makeAllVisible(wrapper.children);
      }
    }
  });
};
const setParentChain = node => {
  if (!isPresent(node)) {
    return;
  }
  node.containsMatches = true;
  node.visible = true;
  if (isPresent(node.parent) && !node.parent.containsMatches) {
    setParentChain(node.parent);
  }
};
const makeAllVisible = nodes => {
  nodes.forEach(node => {
    node.visible = true;
    if (node.children) {
      makeAllVisible(node.children);
    }
  });
};
const operators = {
  contains: (a, b) => a.indexOf(b) >= 0,
  doesnotcontain: (a, b) => a.indexOf(b) === -1,
  startswith: (a, b) => a.lastIndexOf(b, 0) === 0,
  doesnotstartwith: (a, b) => a.lastIndexOf(b, 0) === -1,
  endswith: (a, b) => a.indexOf(b, a.length - b.length) >= 0,
  doesnotendwith: (a, b) => a.indexOf(b, a.length - b.length) < 0
};
const matchByCase = (matcher, ignoreCase) => (a, b) => {
  if (ignoreCase) {
    return matcher(a.toLowerCase(), b.toLowerCase());
  }
  return matcher(a, b);
};
const matchByFieldAndCase = (field, operator, ignoreCase) => (dataItem, term) => matchByCase(operators[operator], ignoreCase)(getter(field)(dataItem), term);
/**
 * @hidden
 */
const buildTreeIndex = (parentIndex, itemIndex) => {
  return [parentIndex, itemIndex].filter(part => isPresent(part)).join('_');
};
/**
 * @hidden
 */
const buildTreeItem = (dataItem, currentLevelIndex, parentIndex) => {
  if (!isPresent(dataItem)) {
    return null;
  }
  return {
    dataItem,
    index: buildTreeIndex(parentIndex, currentLevelIndex)
  };
};
/**
 * @hidden
 *
 * Retrieves all descendant nodes' lookups which are currently registered in the provided lookup item as a flat array.
 */
const fetchLoadedDescendants = (lookup, filterExpression) => {
  if (!isPresent(lookup) || lookup.children.length === 0) {
    return [];
  }
  let descendants = lookup.children;
  if (isPresent(filterExpression)) {
    descendants = descendants.filter(filterExpression);
  }
  descendants.forEach(child => descendants = descendants.concat(fetchLoadedDescendants(child, filterExpression)));
  return descendants;
};
/**
 * @hidden
 *
 * Compares two Seets to determine whether all unique elements in one, are present in the other.
 * Important:
 *  - it disregards the element order
 */
const sameValues = (as, bs) => {
  if (as.size !== bs.size) {
    return false;
  }
  return Array.from(as).every(v => bs.has(v));
};
/**
 * @hidden
 * Returns the size class based on the component and size input.
 */
const getSizeClass = (component, size) => {
  const SIZE_CLASSES = {
    'small': `k-${component}-sm`,
    'medium': `k-${component}-md`,
    'large': `k-${component}-lg`
  };
  return SIZE_CLASSES[size];
};
const safe = node => node || {};
const safeChildren = node => safe(node).children || [];
const lastVisibleNode = nodes => {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return null;
  }
  const nodesCount = nodes.length;
  const lastIndex = nodesCount - 1;
  for (let index = lastIndex; index >= 0; index -= 1) {
    const node = nodes[index];
    if (node.visible) {
      return node;
    }
  }
  return null;
};
/**
 * @hidden
 */
class NavigationModel {
  ib = new IndexBuilderService();
  nodes = [];
  firstVisibleNode() {
    return (this.nodes || []).find(node => node.visible);
  }
  lastVisibleNode() {
    let node = lastVisibleNode(this.nodes);
    while (isPresent(node) && safeChildren(node).length > 0) {
      const children = safeChildren(node);
      const lastVisibleChild = lastVisibleNode(children);
      if (!isPresent(lastVisibleChild)) {
        return node;
      }
      node = lastVisibleChild;
    }
    return node;
  }
  closestNode(index) {
    const {
      prev
    } = safe(this.findNode(index));
    const sibling = prev || this.firstVisibleNode();
    return safe(sibling).index === index ? this.visibleSibling(sibling, 1) : sibling;
  }
  firstFocusableNode() {
    return this.nodes.find(node => {
      return !node.disabled && node.visible;
    });
  }
  findNode(index) {
    return this.find(index, this.nodes);
  }
  findParent(index) {
    const parentLevel = this.ib.level(index) - 1;
    return this.findNode(this.ib.indexForLevel(index, parentLevel));
  }
  findVisibleChild(index) {
    const node = this.findNode(index);
    const children = safeChildren(node);
    return children.find(child => child.visible);
  }
  findVisiblePrev(item) {
    const index = item.index;
    const parent = this.findParent(index);
    const levelIndex = this.ib.lastLevelIndex(index);
    const prevNodes = this.container(parent).slice(0, levelIndex);
    const prevNodesHidden = prevNodes.every(node => !node.visible);
    if (levelIndex === 0 || prevNodesHidden) {
      return parent;
    }
    const currentNode = this.findNode(index);
    let prev = this.visibleSibling(currentNode, -1);
    if (prev) {
      let children = this.container(prev);
      while (children.length > 0 && children.some(node => node.visible)) {
        prev = lastVisibleNode(children);
        children = this.container(prev);
      }
    }
    return prev;
  }
  findVisibleNext(item) {
    const children = this.container(item);
    const hasVisibleChildren = children.some(child => child.visible);
    if (children.length === 0 || !hasVisibleChildren) {
      return this.visibleSibling(item, 1);
    }
    return children.find(child => child.visible);
  }
  registerItem(id, index, disabled, loadMoreButton = false, visible = true) {
    const children = [];
    const level = this.ib.level(index);
    const parent = this.findParent(index);
    if (parent || level === 1) {
      const node = {
        id,
        children,
        index,
        parent,
        disabled,
        loadMoreButton,
        visible
      };
      this.insert(node, parent);
    }
  }
  unregisterItem(id, index) {
    const node = this.find(index, this.nodes);
    if (!node || node.id !== id) {
      return;
    }
    const children = this.container(node.parent);
    children.splice(children.indexOf(node), 1);
  }
  childLevel(nodes) {
    const children = nodes.filter(node => isPresent(node));
    if (!children || !children.length) {
      return 1;
    }
    return this.ib.level(children[0].index);
  }
  container(node) {
    return node ? node.children : this.nodes;
  }
  find(index, nodes) {
    const childLevel = this.childLevel(nodes);
    const indexToMatch = this.ib.indexForLevel(index, childLevel);
    const isLeaf = childLevel === this.ib.level(index);
    const node = nodes.find(n => n && n.index === indexToMatch);
    if (!node) {
      return null;
    }
    return isLeaf ? node : this.find(index, node.children);
  }
  insert(node, parent) {
    const nodes = this.container(parent);
    nodes.splice(this.ib.lastLevelIndex(node.index), 0, node);
  }
  visibleSibling(node, offset) {
    if (!node) {
      return null;
    }
    const parent = this.findParent(node.index);
    const container = this.container(parent);
    let nextItemIndex = container.indexOf(node) + offset;
    let nextItem = container[nextItemIndex];
    while (isPresent(nextItem)) {
      if (nextItem.visible) {
        return nextItem;
      }
      nextItemIndex += offset;
      nextItem = container[nextItemIndex];
    }
    return this.visibleSibling(parent, offset);
  }
}

/**
 * @hidden
 */
class NavigationService {
  localization;
  expands = new Subject();
  moves = new Subject();
  checks = new Subject();
  selects = new Subject();
  deselectAllButCurrentItem = new Subject();
  loadMore = new Subject();
  navigable = true;
  selection = 'single';
  isTreeViewActive = false;
  get model() {
    return this._model;
  }
  set model(model) {
    this._model = model;
  }
  actions = {
    [Keys.ArrowUp]: () => this.activate(this.model.findVisiblePrev(this.focusableItem), true),
    [Keys.ArrowDown]: () => this.activate(this.model.findVisibleNext(this.focusableItem), true),
    [Keys.ArrowLeft]: () => !this.isLoadMoreButton && this.expand({
      expand: this.localization.rtl,
      intercept: this.localization.rtl ? this.moveToFirstVisibleChild : this.moveToParent
    }),
    [Keys.ArrowRight]: () => !this.isLoadMoreButton && this.expand({
      expand: !this.localization.rtl,
      intercept: this.localization.rtl ? this.moveToParent : this.moveToFirstVisibleChild
    }),
    [Keys.Home]: () => this.activate(this.model.firstVisibleNode(), true),
    [Keys.End]: () => this.activate(this.model.lastVisibleNode(), true),
    [Keys.Enter]: e => this.handleEnter(e),
    [Keys.NumpadEnter]: e => this.handleEnter(e),
    [Keys.Space]: () => this.handleSpace()
  };
  activeItem;
  isFocused = false;
  shouldScroll = false;
  shouldFocus = true;
  _model = new NavigationModel();
  get activeIndex() {
    return nodeIndex(this.activeItem) || null;
  }
  get isActiveExpanded() {
    return this.activeItem && this.activeItem.children.length > 0;
  }
  get isLoadMoreButton() {
    return this.activeItem && this.activeItem.loadMoreButton;
  }
  get focusableItem() {
    return this.activeItem || this.model.firstFocusableNode();
  }
  constructor(localization) {
    this.localization = localization;
    this.moveToFirstVisibleChild = this.moveToFirstVisibleChild.bind(this);
    this.moveToParent = this.moveToParent.bind(this);
  }
  activate(item, shouldScroll = false, shouldFocus = true) {
    if (!this.navigable || !item || this.isActive(nodeIndex(item))) {
      return;
    }
    this.isFocused = true;
    this.activeItem = item || this.activeItem;
    this.shouldScroll = shouldScroll;
    this.shouldFocus = shouldFocus;
    this.notifyMove();
  }
  activateParent(index, shouldFocus = true) {
    this.activate(this.model.findParent(index), false, shouldFocus);
  }
  activateIndex(index, shouldFocus = true) {
    if (!index) {
      return;
    }
    this.activate(this.model.findNode(index), false, shouldFocus);
  }
  activateClosest(index) {
    if (!index || nodeIndex(this.focusableItem) !== index) {
      return;
    }
    this.activeItem = this.model.closestNode(index);
    this.shouldFocus = this.isTreeViewActive;
    this.notifyMove();
  }
  activateFocusable() {
    if (this.activeItem) {
      return;
    }
    this.activeItem = this.model.firstVisibleNode();
    this.shouldFocus = this.isTreeViewActive;
    this.notifyMove();
  }
  setFocusableItem(index) {
    const item = this.model.findNode(index);
    if (!item || item.disabled) {
      return;
    }
    this.activeItem = item;
    this.shouldFocus = this.isTreeViewActive;
    this.notifyMove();
  }
  deactivate() {
    if (!this.navigable || !this.isFocused) {
      return;
    }
    this.isFocused = false;
    this.shouldFocus = false;
    this.notifyMove();
  }
  checkIndex(index) {
    if (!this.isDisabled(index)) {
      this.checks.next(index);
    }
  }
  selectIndex(index) {
    if (!this.isDisabled(index)) {
      this.selects.next(index);
    }
  }
  notifyLoadMore(index) {
    if (!isPresent(index)) {
      return;
    }
    this.loadMore.next(index);
  }
  isActive(index) {
    if (!index) {
      return false;
    }
    return this.isFocused && this.activeIndex === index;
  }
  isFocusable(index) {
    return nodeIndex(this.focusableItem) === index;
  }
  isDisabled(index) {
    if (!index) {
      return false;
    }
    return this.model.findNode(index).disabled;
  }
  registerItem(id, index, disabled, loadMoreButton = false, visible = true) {
    const itemAtIndex = this.model.findNode(index);
    if (isPresent(itemAtIndex)) {
      this.model.unregisterItem(itemAtIndex.id, itemAtIndex.index);
      if (this.isActive(index)) {
        this.deactivate();
      }
    }
    this.model.registerItem(id, index, disabled, loadMoreButton, visible);
  }
  updateItem(index, disabled, visible = true) {
    const itemAtIndex = this.model.findNode(index);
    if (isPresent(itemAtIndex)) {
      if (this.isActive(index)) {
        this.deactivate();
      }
    }
    itemAtIndex.disabled = disabled;
    itemAtIndex.visible = visible;
  }
  unregisterItem(id, index) {
    if (this.isActive(index)) {
      this.activateParent(index, false);
    }
    this.model.unregisterItem(id, index);
  }
  move(e) {
    if (!this.navigable) {
      return;
    }
    // on some keyboards arrow keys, PageUp/Down, and Home/End are mapped to Numpad keys
    const code = normalizeKeys(e);
    const moveAction = this.actions[code];
    if (!moveAction) {
      return;
    }
    moveAction(e);
    e.preventDefault();
  }
  expand({
    expand,
    intercept
  }) {
    const index = nodeIndex(this.activeItem);
    if (!index || intercept(index)) {
      return;
    }
    this.notifyExpand(expand);
  }
  moveToParent() {
    if (this.isActiveExpanded) {
      return false;
    }
    this.activate(this.model.findParent(nodeIndex(this.activeItem)));
    return true;
  }
  moveToFirstVisibleChild() {
    if (!this.isActiveExpanded) {
      return false;
    }
    this.activate(this.model.findVisibleChild(nodeIndex(this.activeItem)));
    return true;
  }
  notifyExpand(expand) {
    this.expands.next(this.navigationState(expand));
  }
  notifyMove() {
    this.moves.next(this.navigationState());
  }
  navigationState(expand = false) {
    return {
      expand,
      index: this.activeIndex,
      isFocused: this.isFocused,
      shouldScroll: this.shouldScroll,
      shouldFocus: this.shouldFocus
    };
  }
  handleEnter(event) {
    if (!this.navigable) {
      return;
    }
    if (this.isLoadMoreButton) {
      this.notifyLoadMore(this.activeIndex);
    } else {
      const isCtrlPressed = event.ctrlKey || event.metaKey;
      if (isCtrlPressed) {
        this.selectIndex(this.activeIndex);
      } else {
        if (this.selection === 'multiple') {
          this.deselectAllButCurrentItem.next({
            dataItem: this.activeItem,
            index: this.activeIndex
          });
        } else {
          this.selectIndex(this.activeIndex);
        }
      }
    }
  }
  handleSpace() {
    if (!this.navigable) {
      return;
    }
    if (this.isLoadMoreButton) {
      this.notifyLoadMore(this.activeIndex);
    } else {
      this.checkIndex(this.activeIndex);
    }
  }
  static ɵfac = function NavigationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NavigationService)(i0.ɵɵinject(i1.LocalizationService));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: NavigationService,
    factory: NavigationService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NavigationService, [{
    type: Injectable
  }], () => [{
    type: i1.LocalizationService
  }], null);
})();

/**
 * @hidden
 */
class NodeChildrenService {
  changes = new Subject();
  childrenLoaded(item, children) {
    this.changes.next({
      item,
      children
    });
  }
  static ɵfac = function NodeChildrenService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NodeChildrenService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: NodeChildrenService,
    factory: NodeChildrenService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NodeChildrenService, [{
    type: Injectable
  }], null, null);
})();

/**
 * Represents the template for TreeView nodes ([more information and example](https://www.telerik.com/kendo-angular-ui/components/treeview/node-template)).
 *
 * Use this template to customize the content of the nodes. To define the node template, nest an `<ng-template>`
 * tag with the `kendoTreeViewNodeTemplate` directive inside a `<kendo-treeview>` tag.
 *
 * The template context provides the node data item and its hierarchical index as variables:
 *
 * - `let-dataItem`&mdash;The data item for the current node.
 * - `let-index="index"`&mdash;The hierarchical index of the current node.
 *
 * @example
 * ```html
 * <kendo-treeview>
 *   <ng-template kendoTreeViewNodeTemplate let-dataItem let-index="index">
 *      <span [style.fontWeight]="dataItem.items ? 'bolder': 'normal' ">
 *        {{ index }}: {{ dataItem.text }}
 *      </span>
 *    </ng-template>
 * </kendo-treeview>
 * ```
 */
class NodeTemplateDirective {
  templateRef;
  constructor(templateRef) {
    this.templateRef = templateRef;
  }
  static ɵfac = function NodeTemplateDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || NodeTemplateDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef, 8));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: NodeTemplateDirective,
    selectors: [["", "kendoTreeViewNodeTemplate", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NodeTemplateDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewNodeTemplate]',
      standalone: true
    }]
  }], () => [{
    type: i0.TemplateRef,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Represents a directive for customizing the load more button in the TreeView.
 *
 * To define the template, nest an `<ng-template>` tag with the `kendoTreeViewLoadMoreButtonTemplate` directive inside a `<kendo-treeview>` tag
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button#button-template)).
 *
 * The template context provides the following variable:
 * - `let-index="index"`&mdash;The hierarchical index of the load more button node.
 *
 * @example
 * ```html
 * <kendo-treeview>
 *   <ng-template kendoTreeViewLoadMoreButtonTemplate let-index="index">
 *     Load more at {{ index }}
 *   </ng-template>
 * </kendo-treeview>
 * ```
 */
class LoadMoreButtonTemplateDirective {
  templateRef;
  constructor(templateRef) {
    this.templateRef = templateRef;
  }
  static ɵfac = function LoadMoreButtonTemplateDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LoadMoreButtonTemplateDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef, 8));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: LoadMoreButtonTemplateDirective,
    selectors: [["", "kendoTreeViewLoadMoreButtonTemplate", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoadMoreButtonTemplateDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewLoadMoreButtonTemplate]',
      standalone: true
    }]
  }], () => [{
    type: i0.TemplateRef,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * @hidden
 *
 * An injection token used by the data binding directives to interface with
 * the TreeView or the DropDownTree components.
 */
class DataBoundComponent {
  static ɵfac = function DataBoundComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DataBoundComponent)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DataBoundComponent,
    factory: DataBoundComponent.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DataBoundComponent, [{
    type: Injectable
  }], null, null);
})();

/**
 * @hidden
 *
 * An injection token used by the expand-directive to interface with
 * the TreeView or the DropDownTree components.
 */
class ExpandableComponent {
  static ɵfac = function ExpandableComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ExpandableComponent)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ExpandableComponent,
    factory: ExpandableComponent.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExpandableComponent, [{
    type: Injectable
  }], null, null);
})();

/**
 * @hidden
 */
class SelectionService {
  changes = new Subject();
  firstIndex;
  isFirstSelected(index) {
    return this.firstIndex === index;
  }
  setFirstSelected(index, selected) {
    if (this.firstIndex === index && selected === false) {
      this.firstIndex = null;
    } else if (!this.firstIndex && selected) {
      this.firstIndex = index;
    }
  }
  select(index, dataItem) {
    this.changes.next({
      dataItem,
      index
    });
  }
  static ɵfac = function SelectionService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SelectionService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: SelectionService,
    factory: SelectionService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SelectionService, [{
    type: Injectable
  }], null, null);
})();
const INDEX_REGEX = /\d+$/;
/**
 * @hidden
 */
class TreeViewLookupService {
  map = new Map();
  reset() {
    this.map.clear();
  }
  registerItem(item, parent) {
    const currentLookup = {
      children: [],
      item,
      parent: this.item(nodeIndex(parent))
    };
    this.map.set(item.index, currentLookup);
  }
  registerChildren(index, children) {
    const item = this.item(index);
    if (!item) {
      return;
    }
    item.children = children;
  }
  unregisterItem(index, dataItem) {
    const current = this.item(index);
    if (current && current.item.dataItem === dataItem) {
      this.map.delete(index);
      if (current.parent && current.parent.children) {
        current.parent.children = current.parent.children.filter(item => item.dataItem !== dataItem);
      }
    }
  }
  replaceItem(index, item, parent) {
    if (!item) {
      return;
    }
    this.unregisterItem(index, item.dataItem);
    this.registerItem(item, parent);
    this.addToParent(item, parent);
  }
  itemLookup(index) {
    const item = this.item(index);
    if (!item) {
      return null;
    }
    return {
      children: this.mapChildren(item.children),
      item: item.item,
      parent: item.parent
    };
  }
  hasItem(index) {
    return this.map.has(index);
  }
  item(index) {
    return this.map.get(index) || null;
  }
  addToParent(item, parent) {
    if (parent) {
      const parentItem = this.item(parent.index);
      const index = parseInt(INDEX_REGEX.exec(item.index)[0], 10);
      parentItem.children = parentItem.children || [];
      parentItem.children.splice(index, 0, item);
    }
  }
  mapChildren(children = []) {
    return children.map(c => {
      const {
        item,
        parent,
        children
      } = this.item(c.index);
      return {
        children: this.mapChildren(children),
        item,
        parent
      };
    });
  }
  static ɵfac = function TreeViewLookupService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TreeViewLookupService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: TreeViewLookupService,
    factory: TreeViewLookupService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewLookupService, [{
    type: Injectable
  }], null, null);
})();

/**
 * @hidden
 *
 * A directive which manages the states of the TreeView items.
 */
class TreeViewItemContentWrapperDirective {
  element;
  navigationService;
  selectionService;
  renderer;
  dataItem;
  index;
  initialSelection = false;
  isSelected = isSelected;
  subscriptions = new Subscription();
  constructor(element, navigationService, selectionService, renderer) {
    this.element = element;
    this.navigationService = navigationService;
    this.selectionService = selectionService;
    this.renderer = renderer;
    this.subscriptions.add(this.navigationService.moves.subscribe(this.updateFocusClass.bind(this)));
    this.subscriptions.add(this.navigationService.selects.pipe(filter(index => index === this.index)).subscribe(index => this.selectionService.select(index, this.dataItem)));
    this.subscriptions.add(this.selectionService.changes.subscribe(() => {
      this.updateSelectionClass(this.isSelected(this.dataItem, this.index));
    }));
  }
  ngOnChanges(changes) {
    if (changes['initialSelection']) {
      this.updateSelectionClass(this.initialSelection);
    }
    if (changes['index']) {
      this.updateFocusClass();
    }
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  updateFocusClass() {
    this.render(this.navigationService.isActive(this.index), 'k-focus');
  }
  updateSelectionClass(selected) {
    this.render(selected, 'k-selected');
  }
  render(addClass, className) {
    const action = addClass ? 'addClass' : 'removeClass';
    this.renderer[action](this.element.nativeElement, className);
  }
  static ɵfac = function TreeViewItemContentWrapperDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TreeViewItemContentWrapperDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(NavigationService), i0.ɵɵdirectiveInject(SelectionService), i0.ɵɵdirectiveInject(i0.Renderer2));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: TreeViewItemContentWrapperDirective,
    selectors: [["", "kendoTreeViewItemContentWrapper", ""]],
    inputs: {
      dataItem: "dataItem",
      index: "index",
      initialSelection: "initialSelection",
      isSelected: "isSelected"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewItemContentWrapperDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewItemContentWrapper]',
      standalone: true
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: NavigationService
  }, {
    type: SelectionService
  }, {
    type: i0.Renderer2
  }], {
    dataItem: [{
      type: Input
    }],
    index: [{
      type: Input
    }],
    initialSelection: [{
      type: Input
    }],
    isSelected: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class LoadingIndicatorDirective {
  expandService;
  loadingService;
  cd;
  get loading() {
    return this._loading;
  }
  set loading(value) {
    this._loading = value;
    this.cd.markForCheck();
  }
  index;
  _loading = false;
  subscription;
  constructor(expandService, loadingService, cd) {
    this.expandService = expandService;
    this.loadingService = loadingService;
    this.cd = cd;
  }
  ngOnInit() {
    const loadingNotifications = this.loadingService.changes.pipe(filter(index => index === this.index));
    this.subscription = this.expandService.changes.pipe(filter(({
      index
    }) => index === this.index), tap(({
      expand
    }) => {
      if (!expand && this.loading) {
        this.loading = false;
      }
    }), filter(({
      expand
    }) => expand), switchMap(x => of(x).pipe(delay(100), takeUntil(loadingNotifications)))).subscribe(() => this.loading = true);
    this.subscription.add(loadingNotifications.subscribe(() => this.loading = false));
  }
  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  static ɵfac = function LoadingIndicatorDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LoadingIndicatorDirective)(i0.ɵɵdirectiveInject(ExpandStateService), i0.ɵɵdirectiveInject(LoadingNotificationService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: LoadingIndicatorDirective,
    selectors: [["", "kendoTreeViewLoading", ""]],
    hostVars: 2,
    hostBindings: function LoadingIndicatorDirective_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵclassProp("k-i-loading", ctx.loading);
      }
    },
    inputs: {
      index: [0, "kendoTreeViewLoading", "index"]
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoadingIndicatorDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewLoading]',
      standalone: true
    }]
  }], () => [{
    type: ExpandStateService
  }, {
    type: LoadingNotificationService
  }, {
    type: i0.ChangeDetectorRef
  }], {
    loading: [{
      type: HostBinding,
      args: ["class.k-i-loading"]
    }],
    index: [{
      type: Input,
      args: ["kendoTreeViewLoading"]
    }]
  });
})();
const buildItem = (index, dataItem) => ({
  dataItem,
  index
});
let id = 0;
const TREE_ITEM_ROLE = 'treeitem';
const BUTTON_ROLE = 'button';
/**
 * @hidden
 *
 * A directive which manages the expanded state of the TreeView.
 */
class TreeViewItemDirective {
  element;
  expandService;
  navigationService;
  selectionService;
  lookupService;
  renderer;
  ib;
  dataItem;
  index;
  parentDataItem;
  parentIndex;
  role = TREE_ITEM_ROLE;
  loadOnDemand = true;
  checkable;
  selectable;
  expandable;
  set isChecked(checked) {
    if (checked === 'checked') {
      this.ariaChecked = 'true';
    } else if (checked === 'indeterminate') {
      this.ariaChecked = 'mixed';
    } else {
      this.ariaChecked = 'false';
    }
  }
  isDisabled = false;
  isVisible = true;
  get isExpanded() {
    return this._isExpanded || false;
  }
  set isExpanded(isExpanded) {
    this._isExpanded = isExpanded;
  }
  get isSelected() {
    return this._isSelected || false;
  }
  set isSelected(isSelected) {
    this._isSelected = isSelected;
  }
  get isButton() {
    return this.role === BUTTON_ROLE;
  }
  get treeItem() {
    return buildItem(this.index, this.dataItem);
  }
  get parentTreeItem() {
    return this.parentDataItem ? buildItem(this.parentIndex, this.parentDataItem) : null;
  }
  ariaChecked = 'false';
  id = id++;
  _isExpanded;
  _isSelected;
  isInitialized = false;
  subscriptions = [];
  constructor(element, expandService, navigationService, selectionService, lookupService, renderer, ib) {
    this.element = element;
    this.expandService = expandService;
    this.navigationService = navigationService;
    this.selectionService = selectionService;
    this.lookupService = lookupService;
    this.renderer = renderer;
    this.ib = ib;
    this.subscribe();
  }
  ngOnInit() {
    if (this.loadOnDemand && !this.isButton) {
      this.lookupService.registerItem(this.treeItem, this.parentTreeItem);
    }
    this.registerNavigationItem();
    this.isInitialized = true;
    this.setAttribute('role', this.role);
    this.setAriaAttributes();
    this.updateTabIndex();
  }
  ngOnChanges(changes) {
    const {
      index
    } = changes;
    if (anyChanged(['index', 'checkable', 'isChecked', 'expandable', 'isExpanded', 'selectable', 'isSelected'], changes)) {
      this.setAriaAttributes();
    }
    if (this.loadOnDemand && !this.isButton) {
      this.moveLookupItem(changes);
    }
    this.moveNavigationItem(index);
    if (anyChanged(['isDisabled', 'isVisible'], changes)) {
      this.updateNodeAvailability();
    }
  }
  ngOnDestroy() {
    this.navigationService.unregisterItem(this.id, this.index);
    if (this.loadOnDemand && !this.isButton) {
      this.lookupService.unregisterItem(this.index, this.dataItem);
    }
    this.subscriptions = this.subscriptions.reduce((list, callback) => (callback.unsubscribe(), list), []);
  }
  subscribe() {
    this.subscriptions = [this.navigationService.moves.subscribe(navState => {
      this.updateTabIndex();
      this.focusItem(navState.shouldScroll, navState.shouldFocus);
    }), this.navigationService.expands.pipe(filter(({
      index
    }) => index === this.index && !this.isDisabled)).subscribe(({
      expand
    }) => this.expand(expand))];
  }
  registerNavigationItem() {
    this.navigationService.registerItem(this.id, this.index, this.isDisabled, this.isButton, this.isVisible);
    this.activateItem();
  }
  activateItem() {
    if (this.isDisabled) {
      return;
    }
    const navigationService = this.navigationService;
    const selectionService = this.selectionService;
    const index = this.index;
    selectionService.setFirstSelected(index, this.isSelected);
    if (!navigationService.isActive(index) && selectionService.isFirstSelected(index)) {
      navigationService.activateIndex(index, false);
    }
  }
  expand(shouldExpand) {
    this.expandService[shouldExpand ? 'expand' : 'collapse'](this.index, this.dataItem);
  }
  isFocusable() {
    return !this.isDisabled && this.navigationService.isFocusable(this.index);
  }
  focusItem(scrollIntoView = false, shouldFocus = true) {
    if (this.isInitialized && this.navigationService.isActive(this.index) && shouldFocus) {
      this.element.nativeElement.focus({
        preventScroll: !scrollIntoView
      });
    }
  }
  moveLookupItem(changes = {}) {
    const {
      dataItem,
      index,
      parentDataItem,
      parentIndex
    } = changes;
    if (index && index.firstChange ||
    //skip first change
    !dataItem && !index && !parentDataItem && !parentIndex) {
      return;
    }
    const oldIndex = (index || {}).previousValue || this.index;
    this.lookupService.replaceItem(oldIndex, this.treeItem, this.parentTreeItem);
  }
  moveNavigationItem(indexChange = {}) {
    const {
      currentValue,
      firstChange,
      previousValue
    } = indexChange;
    if (!firstChange && isPresent(currentValue) && isPresent(previousValue)) {
      this.navigationService.unregisterItem(this.id, previousValue);
      this.navigationService.registerItem(this.id, currentValue, this.isDisabled, this.isButton);
    }
  }
  updateNodeAvailability() {
    const service = this.navigationService;
    if (this.isDisabled || !this.isVisible) {
      service.activateClosest(this.index); // activate before updating the item
    } else if (this.isSelected) {
      service.setFocusableItem(this.index);
    } else {
      service.activateFocusable();
    }
    service.updateItem(this.index, this.isDisabled, this.isVisible);
  }
  setAriaAttributes() {
    // using renderer.setStyle is not suitable here as it does not add custom styles such as --kendo-treeview-level
    this.element.nativeElement.style?.setProperty('--kendo-treeview-level', this.ib.level(this.index).toString());
    this.setAttribute('aria-level', this.ib.level(this.index).toString());
    // don't render attributes when the component configuration doesn't allow the specified state
    this.setAttribute('aria-expanded', this.expandable ? this.isExpanded.toString() : null);
    this.setAttribute('aria-selected', this.selectable ? this.isSelected.toString() : null);
    this.setAttribute('aria-checked', this.checkable ? this.ariaChecked : null);
  }
  updateTabIndex() {
    this.setAttribute('tabIndex', this.isFocusable() ? '0' : '-1');
  }
  setAttribute(attr, value) {
    if (!isPresent(value)) {
      this.renderer.removeAttribute(this.element.nativeElement, attr);
      return;
    }
    this.renderer.setAttribute(this.element.nativeElement, attr, value);
  }
  static ɵfac = function TreeViewItemDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TreeViewItemDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(ExpandStateService), i0.ɵɵdirectiveInject(NavigationService), i0.ɵɵdirectiveInject(SelectionService), i0.ɵɵdirectiveInject(TreeViewLookupService), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(IndexBuilderService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: TreeViewItemDirective,
    selectors: [["", "kendoTreeViewItem", ""]],
    inputs: {
      dataItem: "dataItem",
      index: "index",
      parentDataItem: "parentDataItem",
      parentIndex: "parentIndex",
      role: "role",
      loadOnDemand: "loadOnDemand",
      checkable: "checkable",
      selectable: "selectable",
      expandable: "expandable",
      isChecked: "isChecked",
      isDisabled: "isDisabled",
      isVisible: "isVisible",
      isExpanded: "isExpanded",
      isSelected: "isSelected"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewItemDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewItem]',
      standalone: true
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: ExpandStateService
  }, {
    type: NavigationService
  }, {
    type: SelectionService
  }, {
    type: TreeViewLookupService
  }, {
    type: i0.Renderer2
  }, {
    type: IndexBuilderService
  }], {
    dataItem: [{
      type: Input
    }],
    index: [{
      type: Input
    }],
    parentDataItem: [{
      type: Input
    }],
    parentIndex: [{
      type: Input
    }],
    role: [{
      type: Input
    }],
    loadOnDemand: [{
      type: Input
    }],
    checkable: [{
      type: Input
    }],
    selectable: [{
      type: Input
    }],
    expandable: [{
      type: Input
    }],
    isChecked: [{
      type: Input
    }],
    isDisabled: [{
      type: Input
    }],
    isVisible: [{
      type: Input
    }],
    isExpanded: [{
      type: Input
    }],
    isSelected: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class TreeViewGroupComponent {
  expandService;
  loadingService;
  indexBuilder;
  treeViewLookupService;
  navigationService;
  nodeChildrenService;
  dataChangeNotification;
  changeDetectorRef;
  localization;
  renderer;
  /**
   * @hidden
   */
  chevronDownIcon = chevronDownIcon;
  /**
   * @hidden
   */
  chevronRightIcon = chevronRightIcon;
  /**
   * @hidden
   */
  chevronLeftIcon = chevronLeftIcon;
  /**
   * @hidden
   */
  loadMoreTitle;
  kGroupClass = true;
  get role() {
    return this.parentIndex ? 'group' : 'tree';
  }
  checkboxes;
  expandIcons;
  disabled;
  selectable;
  touchActions;
  disableParentNodesOnly;
  loadOnDemand = true;
  trackBy;
  nodes;
  textField = "";
  parentDataItem;
  parentIndex;
  nodeTemplateRef;
  loadMoreButtonTemplateRef;
  loadMoreService;
  size = undefined;
  expandDisabledNodes;
  initialNodesLoaded = false;
  loadingMoreNodes = false;
  isItemExpandable = (node, index) => this.expandDisabledNodes || !this.isItemDisabled(node, index);
  isContentDisabled(node, index) {
    return this.expandIcons && this.hasChildren(node) ? !this.isItemExpandable(node, index) : this.isItemDisabled(node, index);
  }
  getFontIcon(node, index) {
    if (this.isExpanded(node, index)) {
      return 'chevron-down';
    }
    return this.localization.rtl ? 'chevron-left' : 'chevron-right';
  }
  getSvgIcon(node, index) {
    if (this.isExpanded(node, index)) {
      return chevronDownIcon;
    }
    return this.localization.rtl ? chevronLeftIcon : chevronRightIcon;
  }
  get moreNodesAvailable() {
    if (!isPresent(this.loadMoreService) || this.data.length === 0) {
      return false;
    }
    return this.pageSize < this.totalNodesCount || this.data.length < this.totalNodesCount && this.data.length <= this.pageSize;
  }
  get pageSize() {
    if (!isPresent(this.loadMoreService)) {
      return null;
    }
    return this.loadMoreService.getGroupSize(this.parentDataItem);
  }
  set pageSize(pageSize) {
    this.loadMoreService.setGroupSize(this.parentDataItem, pageSize);
  }
  get data() {
    if (isPresent(this.pageSize)) {
      const normalizedSizeValue = this.pageSize > 0 ? this.pageSize : 0;
      return this._data.slice(0, normalizedSizeValue);
    }
    return this._data;
  }
  set data(data) {
    this._data = data;
    this.registerLoadedNodes(this.data);
  }
  get loadMoreButtonIndex() {
    if (!this.loadMoreService) {
      return null;
    }
    return this.nodeIndex(this.data.length);
  }
  /**
   * Represents the total number of nodes for the current level.
   */
  get totalNodesCount() {
    if (!this.loadMoreService) {
      return this.data.length;
    }
    return this.loadMoreService.getTotalNodesCount(this.parentDataItem, this._data.length);
  }
  _data = [];
  nodesSubscription;
  loadMoreNodesSubscription;
  singleRecordSubscriptions = new Subscription();
  localizationSubscriptions = new Subscription();
  constructor(expandService, loadingService, indexBuilder, treeViewLookupService, navigationService, nodeChildrenService, dataChangeNotification, changeDetectorRef, localization, renderer) {
    this.expandService = expandService;
    this.loadingService = loadingService;
    this.indexBuilder = indexBuilder;
    this.treeViewLookupService = treeViewLookupService;
    this.navigationService = navigationService;
    this.nodeChildrenService = nodeChildrenService;
    this.dataChangeNotification = dataChangeNotification;
    this.changeDetectorRef = changeDetectorRef;
    this.localization = localization;
    this.renderer = renderer;
  }
  isChecked = () => 'none';
  isDisabled = () => false;
  hasCheckbox = () => true;
  isExpanded = () => false;
  isVisible = () => true;
  isSelected = () => false;
  children = () => of([]);
  hasChildren = () => false;
  get hasTemplate() {
    return isPresent(this.nodeTemplateRef);
  }
  expandNode(index, dataItem, expand) {
    if (expand) {
      this.expandService.expand(index, dataItem);
    } else {
      this.expandService.collapse(index, dataItem);
    }
  }
  checkNode(index, checkBox) {
    this.navigationService.checkIndex(index);
    this.navigationService.activateIndex(index);
    if (checkBox?.input) {
      this.renderer.removeClass(checkBox.input.nativeElement, 'k-focus');
    }
  }
  nodeIndex(index) {
    return this.indexBuilder.nodeIndex(index.toString(), this.parentIndex);
  }
  nodeText(dataItem) {
    const textField = isArray(this.textField) ? this.textField[0] : this.textField;
    return getter(textField)(dataItem);
  }
  getCheckBoxState(item, index) {
    const state = this.isChecked(item, index);
    if (state === 'indeterminate') {
      return state;
    }
    return state === 'checked';
  }
  getCheckboxAttributes(node) {
    return {
      'aria-label': this.nodeText(node)
    };
  }
  ngOnDestroy() {
    if (isPresent(this.nodesSubscription)) {
      this.nodesSubscription.unsubscribe();
    }
    if (isPresent(this.loadMoreNodesSubscription)) {
      this.loadMoreNodesSubscription.unsubscribe();
    }
    this.singleRecordSubscriptions.unsubscribe();
    this.localizationSubscriptions.unsubscribe();
  }
  ngOnInit() {
    this.subscribeToNodesChange();
    this.singleRecordSubscriptions.add(this.dataChangeNotification.changes.subscribe(this.subscribeToNodesChange.bind(this)));
    this.singleRecordSubscriptions.add(this.navigationService.loadMore.pipe(filter(index => index === this.loadMoreButtonIndex)).subscribe(this.loadMoreNodes.bind(this)));
    this.localizationSubscriptions.add(this.localization.changes.subscribe(this.l10nChange.bind(this)));
  }
  ngOnChanges(changes) {
    if (changes.parentIndex && this.loadOnDemand) {
      this.setNodeChildren(this.mapToTreeItem(this.data));
    }
    if (this.localization.get('loadMore')) {
      this.loadMoreTitle = this.localization.get('loadMore');
    }
  }
  l10nChange() {
    if (this.localization.get('loadMore')) {
      this.loadMoreTitle = this.localization.get('loadMore');
    }
    this.changeDetectorRef.markForCheck();
  }
  fetchChildren(node, index) {
    return this.children(node).pipe(catchError(() => {
      this.loadingService.notifyLoaded(index);
      return EMPTY;
    }), tap(() => this.loadingService.notifyLoaded(index)));
  }
  get nextFields() {
    if (isArray(this.textField)) {
      return this.textField.length > 1 ? this.textField.slice(1) : this.textField;
    }
    return [this.textField];
  }
  loadMoreNodes() {
    if (isPresent(this.loadMoreService.loadMoreNodes)) {
      this.fetchMoreNodes();
    } else {
      this.loadMoreLocalNodes();
    }
  }
  /**
   * @hidden
   */
  isItemDisabled(node, index) {
    return this.disabled && !this.disableParentNodesOnly || this.isDisabled(node, this.nodeIndex(index));
  }
  loadMoreLocalNodes() {
    const initialLoadMoreButtonIndex = this.loadMoreButtonIndex;
    this.pageSize += this.loadMoreService.getInitialPageSize(this.parentDataItem);
    this.registerLoadedNodes(this.data);
    // forces the new items to be registered before the focus is changed
    this.changeDetectorRef.detectChanges();
    this.reselectItemAt(initialLoadMoreButtonIndex);
  }
  fetchMoreNodes() {
    if (this.loadingMoreNodes) {
      return;
    }
    this.loadingMoreNodes = true;
    if (isPresent(this.loadMoreNodesSubscription)) {
      this.loadMoreNodesSubscription.unsubscribe();
    }
    this.loadMoreNodesSubscription = this.loadMoreService.loadMoreNodes({
      dataItem: this.parentDataItem,
      skip: this.data.length,
      take: this.loadMoreService.getInitialPageSize(this.parentDataItem)
    }).pipe(finalize(() => this.loadingMoreNodes = false)).subscribe(items => {
      if (!(Array.isArray(items) && items.length > 0)) {
        return;
      }
      const initialLoadMoreButtonIndex = this.loadMoreButtonIndex;
      this.pageSize += items.length;
      this.data = this.data.concat(items);
      if (this.navigationService.isActive(initialLoadMoreButtonIndex)) {
        // forces the new items to be registered before the focus is changed
        this.changeDetectorRef.detectChanges();
        this.reselectItemAt(initialLoadMoreButtonIndex);
      }
    });
  }
  setNodeChildren(children) {
    this.treeViewLookupService.registerChildren(this.parentIndex, children);
  }
  mapToTreeItem(data) {
    if (!this.parentIndex) {
      return [];
    }
    return data.map((dataItem, idx) => ({
      dataItem,
      index: this.nodeIndex(idx)
    }));
  }
  emitChildrenLoaded(children) {
    if (!this.parentIndex) {
      return;
    }
    // ignores the registered load-more button
    const contentChildren = children.filter(item => item.dataItem);
    this.nodeChildrenService.childrenLoaded({
      dataItem: this.parentDataItem,
      index: this.parentIndex
    }, contentChildren);
  }
  subscribeToNodesChange() {
    if (this.nodesSubscription) {
      this.nodesSubscription.unsubscribe();
    }
    this.nodesSubscription = this.nodes(this.parentDataItem, this.parentIndex).subscribe(data => {
      this.data = data;
      this.initialNodesLoaded = true;
    });
  }
  reselectItemAt(index) {
    if (!isPresent(index)) {
      return;
    }
    // make sure the old index is cleared first
    this.navigationService.deactivate();
    this.navigationService.activateIndex(index);
  }
  registerLoadedNodes(nodes = []) {
    const mappedChildren = this.mapToTreeItem(nodes);
    if (this.loadOnDemand) {
      this.setNodeChildren(mappedChildren);
    }
    this.emitChildrenLoaded(mappedChildren);
  }
  static ɵfac = function TreeViewGroupComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TreeViewGroupComponent)(i0.ɵɵdirectiveInject(ExpandStateService), i0.ɵɵdirectiveInject(LoadingNotificationService), i0.ɵɵdirectiveInject(IndexBuilderService), i0.ɵɵdirectiveInject(TreeViewLookupService), i0.ɵɵdirectiveInject(NavigationService), i0.ɵɵdirectiveInject(NodeChildrenService), i0.ɵɵdirectiveInject(DataChangeNotificationService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.LocalizationService), i0.ɵɵdirectiveInject(i0.Renderer2));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: TreeViewGroupComponent,
    selectors: [["", "kendoTreeViewGroup", ""]],
    hostVars: 3,
    hostBindings: function TreeViewGroupComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵattribute("role", ctx.role);
        i0.ɵɵclassProp("k-treeview-group", ctx.kGroupClass);
      }
    },
    inputs: {
      checkboxes: "checkboxes",
      expandIcons: "expandIcons",
      disabled: "disabled",
      selectable: "selectable",
      touchActions: "touchActions",
      disableParentNodesOnly: "disableParentNodesOnly",
      loadOnDemand: "loadOnDemand",
      trackBy: "trackBy",
      nodes: "nodes",
      textField: "textField",
      parentDataItem: "parentDataItem",
      parentIndex: "parentIndex",
      nodeTemplateRef: "nodeTemplateRef",
      loadMoreButtonTemplateRef: "loadMoreButtonTemplateRef",
      loadMoreService: "loadMoreService",
      size: "size",
      expandDisabledNodes: "expandDisabledNodes",
      isChecked: "isChecked",
      isDisabled: "isDisabled",
      hasCheckbox: "hasCheckbox",
      isExpanded: "isExpanded",
      isVisible: "isVisible",
      isSelected: "isSelected",
      children: "children",
      hasChildren: "hasChildren"
    },
    features: [i0.ɵɵNgOnChangesFeature],
    attrs: _c0,
    decls: 3,
    vars: 1,
    consts: [["checkbox", ""], ["kendoTreeViewItem", "", 1, "k-treeview-item", 3, "k-hidden", "dataItem", "index", "parentDataItem", "parentIndex", "loadOnDemand", "checkable", "isChecked", "isDisabled", "isVisible", "expandable", "isExpanded", "selectable", "isSelected"], ["kendoTreeViewItem", "", "role", "button", 1, "k-treeview-item", 3, "k-treeview-load-more-checkboxes-container", "selectable", "checkable", "expandable", "index", "parentDataItem", "parentIndex"], ["kendoTreeViewItem", "", 1, "k-treeview-item", 3, "dataItem", "index", "parentDataItem", "parentIndex", "loadOnDemand", "checkable", "isChecked", "isDisabled", "isVisible", "expandable", "isExpanded", "selectable", "isSelected"], ["kendoTreeViewItemContentWrapper", "", 1, "k-treeview-item-content", 3, "dataItem", "index", "initialSelection", "isSelected"], [1, "k-treeview-toggle", 3, "kendoTreeViewLoading"], [3, "disabled", "size", "checkedState", "tabindex", "inputAttributes"], [1, "k-treeview-leaf", 3, "id"], [1, "k-treeview-leaf-text"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], ["kendoTreeViewGroup", "", "role", "group", 3, "size", "nodes", "loadOnDemand", "checkboxes", "expandIcons", "selectable", "touchActions", "children", "hasChildren", "isChecked", "isDisabled", "hasCheckbox", "disabled", "expandDisabledNodes", "isExpanded", "isSelected", "isVisible", "nodeTemplateRef", "loadMoreButtonTemplateRef", "parentIndex", "parentDataItem", "textField", "loadMoreService", "trackBy", "disableParentNodesOnly"], [1, "k-treeview-toggle", 3, "click", "kendoTreeViewLoading"], [3, "name", "svgIcon"], [3, "checkedStateChange", "disabled", "size", "checkedState", "tabindex", "inputAttributes"], ["kendoTreeViewItem", "", "role", "button", 1, "k-treeview-item", 3, "selectable", "checkable", "expandable", "index", "parentDataItem", "parentIndex"], ["kendoTreeViewItemContentWrapper", "", 1, "k-treeview-item-content", 3, "index"], [1, "k-icon", "k-i-loading"], [1, "k-treeview-leaf", "k-treeview-load-more-button"]],
    template: function TreeViewGroupComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵrepeaterCreate(0, TreeViewGroupComponent_For_1_Template, 9, 31, "li", 1, _forTrack0, true);
        i0.ɵɵconditionalCreate(2, TreeViewGroupComponent_Conditional_2_Template, 7, 14, "li", 2);
      }
      if (rf & 2) {
        i0.ɵɵrepeater(ctx.data);
        i0.ɵɵadvance(2);
        i0.ɵɵconditional(ctx.initialNodesLoaded && ctx.moreNodesAvailable ? 2 : -1);
      }
    },
    dependencies: [TreeViewGroupComponent, TreeViewItemDirective, LoadingIndicatorDirective, IconWrapperComponent, CheckBoxComponent, TreeViewItemContentWrapperDirective, NgTemplateOutlet],
    encapsulation: 2,
    data: {
      animation: [trigger('toggle', [transition('void => *', [style({
        height: 0
      }), animate('0.1s ease-in', style({
        height: "*"
      }))]), transition('* => void', [style({
        height: "*"
      }), animate('0.1s ease-in', style({
        height: 0
      }))])])]
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewGroupComponent, [{
    type: Component,
    args: [{
      animations: [trigger('toggle', [transition('void => *', [style({
        height: 0
      }), animate('0.1s ease-in', style({
        height: "*"
      }))]), transition('* => void', [style({
        height: "*"
      }), animate('0.1s ease-in', style({
        height: 0
      }))])])],
      selector: '[kendoTreeViewGroup]',
      template: `
        @for (node of data; track trackBy ? trackBy(index, node) : index; let index = $index) {
          <li
            class="k-treeview-item"
            [class.k-hidden]="!isVisible(node, nodeIndex(index))"
            kendoTreeViewItem
            [attr.aria-setsize]="totalNodesCount"
            [dataItem]="node"
            [index]="nodeIndex(index)"
            [parentDataItem]="parentDataItem"
            [parentIndex]="parentIndex"
            [loadOnDemand]="loadOnDemand"
            [checkable]="checkboxes"
            [isChecked]="isChecked(node, nodeIndex(index))"
            [isDisabled]="isItemDisabled(node, index)"
            [isVisible]="isVisible(node, nodeIndex(index))"
            [expandable]="expandIcons && hasChildren(node)"
            [isExpanded]="isExpanded(node, nodeIndex(index))"
            [selectable]="selectable"
            [isSelected]="isSelected(node, nodeIndex(index))"
            [attr.data-treeindex]="nodeIndex(index)"
            >

            <div kendoTreeViewItemContentWrapper
                [dataItem]="node"
                [index]="nodeIndex(index)"
                [initialSelection]="isSelected(node, nodeIndex(index))"
                [isSelected]="isSelected"
                class="k-treeview-item-content"
                [class.k-disabled]="isContentDisabled(node, index)">
              @if (expandIcons && hasChildren(node)) {
                <span
                  class="k-treeview-toggle"
                  [kendoTreeViewLoading]="nodeIndex(index)"
                  (click)="expandNode(nodeIndex(index), node, !isExpanded(node, nodeIndex(index)))"
                  >
                  <kendo-icon-wrapper
                    [name]="getFontIcon(node, nodeIndex(index))"
                    [svgIcon]="getSvgIcon(node, nodeIndex(index))">
                  </kendo-icon-wrapper>
                </span>
              }
              @if (checkboxes && hasCheckbox(node, nodeIndex(index))) {
                <kendo-checkbox
                  #checkbox
                  [disabled]="isItemDisabled(node, index)"
                  [size]="size"
                  [checkedState]="getCheckBoxState(node, nodeIndex(index))"
                  (checkedStateChange)="checkNode(nodeIndex(index), checkbox)"
                  [tabindex]="-1"
                  [inputAttributes]="getCheckboxAttributes(node)"
                ></kendo-checkbox>
              }
              <span
                [id]="nodeIndex(index)"
                [attr.data-treeindex]="nodeIndex(index)"
                class="k-treeview-leaf"
                [style.touch-action]="touchActions ? '' : 'none'"
                >
                <span class="k-treeview-leaf-text">
                  @switch (hasTemplate) {
                    @case (true) {
                      <ng-template
                        [ngTemplateOutlet]="nodeTemplateRef"
                                    [ngTemplateOutletContext]="{
                                        $implicit: node,
                                        index: nodeIndex(index)
                                    }"
                        >
                      </ng-template>
                    }
                    @default {
                      {{nodeText(node)}}
                    }
                  }
                </span>
              </span>
            </div>
            @if (isExpanded(node, nodeIndex(index)) && hasChildren(node)) {
              <ul
                kendoTreeViewGroup
                role="group"
                [size]="size"
                [nodes]="fetchChildren"
                [loadOnDemand]="loadOnDemand"
                [checkboxes]="checkboxes"
                [expandIcons]="expandIcons"
                [selectable]="selectable"
                [touchActions]="touchActions"
                [children]="children"
                [hasChildren]="hasChildren"
                [isChecked]="isChecked"
                [isDisabled]="isDisabled"
                [hasCheckbox]="hasCheckbox"
                [disabled]="isItemDisabled(node, index)"
                [expandDisabledNodes]="expandDisabledNodes"
                [isExpanded]="isExpanded"
                [isSelected]="isSelected"
                [isVisible]="isVisible"
                [nodeTemplateRef]="nodeTemplateRef"
                [loadMoreButtonTemplateRef]="loadMoreButtonTemplateRef"
                [parentIndex]="nodeIndex(index)"
                [parentDataItem]="node"
                [textField]="nextFields"
                [loadMoreService]="loadMoreService"
                [@toggle]="true"
                [trackBy]="trackBy"
                [disableParentNodesOnly]="disableParentNodesOnly"
                >
              </ul>
            }
          </li>
        }
        @if (initialNodesLoaded && moreNodesAvailable) {
          <li
            class="k-treeview-item"
            [class.k-treeview-load-more-checkboxes-container]="checkboxes"
            kendoTreeViewItem
            role="button"
            [selectable]="false"
            [checkable]="false"
            [expandable]="false"
            [index]="loadMoreButtonIndex"
            [parentDataItem]="parentDataItem"
            [parentIndex]="parentIndex"
            [attr.data-treeindex]="loadMoreButtonIndex"
            >
            <div 
                kendoTreeViewItemContentWrapper
                [index]="loadMoreButtonIndex"
                class="k-treeview-item-content">
              @if (loadingMoreNodes) {
                <span
                  class="k-icon k-i-loading"
                  >
                </span>
              }
              <span
                class="k-treeview-leaf k-treeview-load-more-button"
                [attr.data-treeindex]="loadMoreButtonIndex"
                >
                <span class="k-treeview-leaf-text">
                  @if (loadMoreButtonTemplateRef) {
                    <ng-template
                      [ngTemplateOutlet]="loadMoreButtonTemplateRef"
                            [ngTemplateOutletContext]="{
                                index: loadMoreButtonIndex
                            }"
                      >
                    </ng-template>
                  }
                  @if (!loadMoreButtonTemplateRef) {
                    {{ loadMoreTitle }}
                  }
                </span>
              </span>
            </div>
          </li>
        }
        `,
      standalone: true,
      imports: [TreeViewItemDirective, LoadingIndicatorDirective, IconWrapperComponent, CheckBoxComponent, TreeViewItemContentWrapperDirective, NgTemplateOutlet]
    }]
  }], () => [{
    type: ExpandStateService
  }, {
    type: LoadingNotificationService
  }, {
    type: IndexBuilderService
  }, {
    type: TreeViewLookupService
  }, {
    type: NavigationService
  }, {
    type: NodeChildrenService
  }, {
    type: DataChangeNotificationService
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i1.LocalizationService
  }, {
    type: i0.Renderer2
  }], {
    kGroupClass: [{
      type: HostBinding,
      args: ["class.k-treeview-group"]
    }],
    role: [{
      type: HostBinding,
      args: ["attr.role"]
    }],
    checkboxes: [{
      type: Input
    }],
    expandIcons: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    selectable: [{
      type: Input
    }],
    touchActions: [{
      type: Input
    }],
    disableParentNodesOnly: [{
      type: Input
    }],
    loadOnDemand: [{
      type: Input
    }],
    trackBy: [{
      type: Input
    }],
    nodes: [{
      type: Input
    }],
    textField: [{
      type: Input
    }],
    parentDataItem: [{
      type: Input
    }],
    parentIndex: [{
      type: Input
    }],
    nodeTemplateRef: [{
      type: Input
    }],
    loadMoreButtonTemplateRef: [{
      type: Input
    }],
    loadMoreService: [{
      type: Input
    }],
    size: [{
      type: Input
    }],
    expandDisabledNodes: [{
      type: Input
    }],
    isChecked: [{
      type: Input
    }],
    isDisabled: [{
      type: Input
    }],
    hasCheckbox: [{
      type: Input
    }],
    isExpanded: [{
      type: Input
    }],
    isVisible: [{
      type: Input
    }],
    isSelected: [{
      type: Input
    }],
    children: [{
      type: Input
    }],
    hasChildren: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class TreeViewMessages extends ComponentMessages {
  /**
   * Sets the text for the `Load More` button title.
   */
  loadMore;
  static ɵfac = /* @__PURE__ */(() => {
    let ɵTreeViewMessages_BaseFactory;
    return function TreeViewMessages_Factory(__ngFactoryType__) {
      return (ɵTreeViewMessages_BaseFactory || (ɵTreeViewMessages_BaseFactory = i0.ɵɵgetInheritedFactory(TreeViewMessages)))(__ngFactoryType__ || TreeViewMessages);
    };
  })();
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: TreeViewMessages,
    selectors: [["kendo-treeview-messages-base"]],
    inputs: {
      loadMore: "loadMore"
    },
    features: [i0.ɵɵInheritDefinitionFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewMessages, [{
    type: Directive,
    args: [{
      selector: 'kendo-treeview-messages-base'
    }]
  }], null, {
    loadMore: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class LocalizedMessagesDirective extends TreeViewMessages {
  service;
  constructor(service) {
    super();
    this.service = service;
  }
  static ɵfac = function LocalizedMessagesDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LocalizedMessagesDirective)(i0.ɵɵdirectiveInject(i1.LocalizationService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: LocalizedMessagesDirective,
    selectors: [["", "kendoTreeViewLocalizedMessages", ""]],
    features: [i0.ɵɵProvidersFeature([{
      provide: TreeViewMessages,
      useExisting: forwardRef(() => LocalizedMessagesDirective)
    }]), i0.ɵɵInheritDefinitionFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocalizedMessagesDirective, [{
    type: Directive,
    args: [{
      providers: [{
        provide: TreeViewMessages,
        useExisting: forwardRef(() => LocalizedMessagesDirective)
      }],
      selector: '[kendoTreeViewLocalizedMessages]',
      standalone: true
    }]
  }], () => [{
    type: i1.LocalizationService
  }], null);
})();
let nextId = 0;
const LOAD_MORE_DOC_LINK$1 = 'https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button';
const providers = [ExpandStateService, IndexBuilderService, TreeViewLookupService, LoadingNotificationService, NodeChildrenService, NavigationService, SelectionService, DataChangeNotificationService, LocalizationService, {
  provide: L10N_PREFIX,
  useValue: 'kendo.treeview'
}, {
  provide: DataBoundComponent,
  useExisting: forwardRef(() => TreeViewComponent)
}, {
  provide: ExpandableComponent,
  useExisting: forwardRef(() => TreeViewComponent)
}];
/**
 * Represents the [Kendo UI TreeView component for Angular](https://www.telerik.com/kendo-angular-ui/components/treeview).
 *
 * @example
 * ```html
 * <kendo-treeview
 *    kendoTreeViewExpandable
 *    [nodes]="data"
 *    textField="text"
 *    [children]="fetchChildren"
 *    [hasChildren]="hasChildren">
 * </kendo-treeview>
 * ```
 *
 * @remarks
 * Supported children components are: {@link CustomMessagesComponent}
 */
class TreeViewComponent {
  element;
  changeDetectorRef;
  expandService;
  navigationService;
  nodeChildrenService;
  selectionService;
  treeViewLookupService;
  ngZone;
  renderer;
  dataChangeNotification;
  localization;
  /**
   * @hidden
   */
  searchIcon = searchIcon;
  classNames = true;
  /** @hidden */
  get direction() {
    return this.localization.rtl ? 'rtl' : 'ltr';
  }
  /**
   * @hidden
   */
  assetsContainer;
  /**
   * @hidden
   */
  searchbox;
  /**
   * Sets the placeholder text for the filter input when the component is empty.
   */
  filterInputPlaceholder = "";
  /**
   * Allows expanding disabled nodes.
   *
   * @default false
   */
  expandDisabledNodes;
  /**
   * Enables or disables content animation.
   */
  set animate(value) {
    this._animate = value;
  }
  get animate() {
    return !this._animate;
  }
  /** @hidden */
  fetchNodes = () => this.data;
  /**
   * Fires when the children of the expanded node are loaded.
   */
  childrenLoaded = new EventEmitter();
  /**
    * Fires when the component loses focus.
    */
  onBlur = new EventEmitter();
  /**
   * Fires when the component receives focus.
   */
  onFocus = new EventEmitter();
  /**
   * Fires when a TreeView node expands.
   */
  expand = new EventEmitter();
  /**
   * Fires when a TreeView node collapses.
   */
  collapse = new EventEmitter();
  /**
   * Fires just before node dragging starts ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#setup)). This event is preventable.
   * Prevent the default event to stop drag hint creation and further drag events.
   */
  nodeDragStart = new EventEmitter();
  /**
   * Fires when an item is being dragged ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#setup)).
   */
  nodeDrag = new EventEmitter();
  /**
   * Emits when the built-in filtering mechanism updates node visibility.
   * Used for the built-in auto-expand functionalities. Handle this event for custom implementations.
   */
  filterStateChange = new EventEmitter();
  /**
   * Fires on the target TreeView when a dragged item is dropped ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#setup)).
   *
   * Prevent the default event (`event.preventDefault()`) or set the event as invalid (`event.setValid(false)`) to stop the `addItem` and `removeItem` events from triggering.
   *
   * Use `preventDefault` to handle add/remove manually, or `setValid(false)` to indicate an unsuccessful operation. While `setValid(false)` animates the drag clue to its original position, `event.preventDefault()` simply removes the clue.
   */
  nodeDrop = new EventEmitter();
  /**
   * Fires on the source TreeView after the dragged item is dropped ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#setup)).
   */
  nodeDragEnd = new EventEmitter();
  /**
   * Fires after a dragged item is dropped ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#setup)). Called on the TreeView where the item is dropped.
   */
  addItem = new EventEmitter();
  /**
   * Fires after a dragged item is dropped ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#setup)). Called on the TreeView from where the item is dragged.
   */
  removeItem = new EventEmitter();
  /**
   * Fires when a TreeView node checkbox is selected ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/checkboxes#modifying-the-checked-state)).
   */
  checkedChange = new EventEmitter();
  /**
   * Fires when a TreeView node is selected ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/selection#modifying-the-selection-behavior)).
   */
  selectionChange = new EventEmitter();
  /**
   * Fires when the value of the built-in filter input changes.
   */
  filterChange = new EventEmitter();
  /**
   * Fires when a TreeView node is clicked.
   */
  nodeClick = new EventEmitter();
  /**
   * Fires when a TreeView node is double-clicked.
   */
  nodeDblClick = new EventEmitter();
  /**
   * @hidden
   *
   * Queries the template for a node template declaration.
   * Ignored if a `[nodeTemplate]` value is explicitly provided.
   */
  nodeTemplateQuery;
  /**
   * @hidden
   *
   * Defines the template for each node.
   * Takes precedence over nested templates in the TreeView tag.
   */
  set nodeTemplateRef(template) {
    this._nodeTemplateRef = template;
  }
  get nodeTemplateRef() {
    return this._nodeTemplateRef || this.nodeTemplateQuery;
  }
  /**
   * @hidden
   *
   * Queries the template for a load-more button template declaration.
   * Ignored if a `[loadMoreButtonTemplate]` value is explicitly provided.
   */
  loadMoreButtonTemplateQuery;
  /**
   * @hidden
   *
   * Defines the template for each load-more button.
   * Takes precedence over nested templates in the TreeView tag.
   */
  set loadMoreButtonTemplateRef(template) {
    this._loadMoreButtonTemplateRef = template;
  }
  get loadMoreButtonTemplateRef() {
    return this._loadMoreButtonTemplateRef || this.loadMoreButtonTemplateQuery;
  }
  /**
   * A function that defines how to track node changes.
   * By default, the TreeView tracks the nodes by data item object reference.
   */
  trackBy = trackBy;
  /**
   * The nodes displayed by the TreeView ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/data-binding)).
   */
  set nodes(value) {
    this.data.next(value || []);
    this.dataChangeNotification.notify();
  }
  get nodes() {
    return this.data.value;
  }
  /**
   * The fields of the data item that provide the text content of the nodes ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/data-binding)).
   * If set to an array, each level uses the field at the same index or the last item in the array.
   */
  textField;
  /**
   * A function that determines if a node has child nodes ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/data-binding)).
   */
  get hasChildren() {
    return this._hasChildren || hasChildren;
  }
  set hasChildren(callback) {
    this._hasChildren = callback;
    this.expandIcons = Boolean(this._isExpanded && this._hasChildren);
  }
  /**
   * A function that determines if a node is checked ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/checkboxes#modifying-the-checked-state)).
   */
  get isChecked() {
    return this._isChecked || isChecked;
  }
  set isChecked(callback) {
    this._isChecked = callback;
    this.checkboxes = Boolean(this._isChecked);
  }
  /**
   * A function that determines if a node is disabled.
   */
  isDisabled = isDisabled;
  /**
   * A function that determines if a node has a checkbox. If there is no checkbox, the node is not checkable and is excluded from built-in check functionality.
   */
  hasCheckbox = hasCheckbox;
  /**
   * A function that determines if a node is expanded.
   */
  get isExpanded() {
    return this._isExpanded || isExpanded;
  }
  set isExpanded(callback) {
    this._isExpanded = callback;
    this.expandIcons = Boolean(this._isExpanded && this._hasChildren);
  }
  /**
   * A function that determines if a node is selected ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/selection#modifying-the-selection-behavior)).
   */
  get isSelected() {
    return this._isSelected || isSelected;
  }
  set isSelected(callback) {
    this._isSelected = callback;
    this.selectable = Boolean(this._isSelected);
  }
  /**
   * A callback that determines whether a TreeView node should be rendered as hidden. Uses the `.k-hidden` utility class. Useful for custom filtering.
   *
   */
  isVisible = isVisible;
  /**
   * Enables keyboard navigation for the TreeView.
   *
   * @default true
   *
   * @remarks
   * This property is related to accessibility.
   */
  navigable = true;
  /**
   * A function that provides the child nodes for a given parent node ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/data-binding)).
   */
  children = () => of([]);
  /**
   * Indicates whether child nodes are fetched on expand or initally prefetched.
   *
   * @default true
   */
  loadOnDemand = true;
  /**
   * Renders the built-in input element for filtering. If `true`, emits the `filterChange` event that can be handled for [manual filtering](https://www.telerik.com/kendo-angular-ui/components/treeview/filtering#manual-filtering).
   * Built-in filtering is available with [`kendoTreeViewHierarchyBinding`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/hierarchybindingdirective) and [`kendoTreeViewFlatDataBinding`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/flatdatabindingdirective) directives.
   *
   * @default false
   */
  filterable = false;
  /**
   * Sets the initial value of the built-in filter input.
   */
  filter = '';
  /**
   * Sets the size of the component.
   *
   * @default undefined
   */
  set size(size) {
    if (isPresent(this.size)) {
      this.renderer.removeClass(this.element.nativeElement, getSizeClass('treeview', this.size));
    }
    if (isPresent(size)) {
      this.renderer.addClass(this.element.nativeElement, getSizeClass('treeview', size));
    }
    this._size = size;
  }
  get size() {
    return this._size;
  }
  get isActive() {
    return this.navigationService.isTreeViewActive;
  }
  /**
   * @hidden
   */
  get treeviewId() {
    return `treeview_${this._nextId}`;
  }
  /**
   * Indicates whether only parent nodes should be disabled or their child nodes as well.
   *
   * @default false
   */
  disableParentNodesOnly = false;
  /**
   * @hidden
   */
  loadMoreService;
  /**
   * @hidden
   */
  editService;
  checkboxes = false;
  expandIcons = false;
  selectable = false;
  touchActions = true;
  data = new BehaviorSubject([]);
  _animate = true;
  _isChecked;
  _isExpanded;
  _isSelected;
  _hasChildren;
  _nodeTemplateRef;
  _loadMoreButtonTemplateRef;
  _size = undefined;
  subscriptions = new Subscription();
  domSubscriptions = [];
  _nextId = nextId;
  constructor(element, changeDetectorRef, expandService, navigationService, nodeChildrenService, selectionService, treeViewLookupService, ngZone, renderer, dataChangeNotification, localization) {
    this.element = element;
    this.changeDetectorRef = changeDetectorRef;
    this.expandService = expandService;
    this.navigationService = navigationService;
    this.nodeChildrenService = nodeChildrenService;
    this.selectionService = selectionService;
    this.treeViewLookupService = treeViewLookupService;
    this.ngZone = ngZone;
    this.renderer = renderer;
    this.dataChangeNotification = dataChangeNotification;
    this.localization = localization;
    validatePackage(packageMetadata);
    nextId++;
  }
  ngOnChanges(changes) {
    this.navigationService.navigable = Boolean(this.navigable);
    // TODO: should react to changes.loadOnDemand as well - should preload the data or clear the already cached items
    if (anyChanged(['nodes', 'children', 'hasChildren', 'loadOnDemand'], changes, false) && !this.loadOnDemand) {
      this.preloadChildNodes();
    }
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.domSubscriptions.forEach(subscription => subscription());
  }
  ngOnInit() {
    this.subscriptions.add(this.nodeChildrenService.changes.subscribe(x => this.childrenLoaded.emit(x)));
    this.subscriptions.add(this.expandService.changes.subscribe(({
      index,
      dataItem,
      expand
    }) => expand ? this.expand.emit({
      index,
      dataItem
    }) : this.collapse.emit({
      index,
      dataItem
    })));
    this.subscriptions.add(this.navigationService.checks.subscribe(x => this.checkedChange.emit(this.treeViewLookupService.itemLookup(x))));
    this.subscriptions.add(this.selectionService.changes.subscribe(x => {
      if (hasObservers(this.selectionChange)) {
        this.ngZone.run(() => {
          this.selectionChange.emit(x);
        });
      }
    }));
    if (this.element) {
      this.ngZone.runOutsideAngular(() => {
        this.attachDomHandlers();
      });
    }
    if (this.size) {
      this.renderer.addClass(this.element.nativeElement, getSizeClass('treeview', this.size));
    }
  }
  ngAfterViewInit() {
    if (this.searchbox) {
      this.renderer.setAttribute(this.searchbox.input.nativeElement, 'role', 'searchbox');
      this.renderer.setAttribute(this.searchbox.input.nativeElement, 'aria-controls', this.treeviewId);
      this.renderer.setAttribute(this.searchbox.input.nativeElement, 'aria-label', 'searchbar');
    }
  }
  /**
   * Blurs the focused TreeView item.
   */
  blur() {
    if (!isDocumentAvailable()) {
      return;
    }
    const target = focusableNode(this.element);
    if (document.activeElement === target) {
      target.blur();
    }
  }
  /**
   * Focuses the first focusable item in the TreeView if no hierarchical index is provided.
   */
  focus(index) {
    const focusIndex = index || nodeIndex(this.navigationService.focusableItem);
    this.navigationService.activateIndex(focusIndex);
    const target = focusableNode(this.element);
    if (target) {
      target.focus();
    }
  }
  /**
   * Returns the `TreeItemLookup` node for the specified index.
   *
   * @param index - The index of the node.
   * @returns {TreeItemLookup} - The looked up item.
   */
  itemLookup(index) {
    return this.treeViewLookupService.itemLookup(index);
  }
  /**
   * Calls the [`children`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#children) function for every expanded node and fetches all rendered child nodes again.
   */
  rebindChildren() {
    this.dataChangeNotification.notify();
  }
  /**
   * Triggers the `expand` event for the provided node and shows its loading indicator.
   */
  expandNode(item, index) {
    this.expandService.expand(index, item);
  }
  /**
   * Triggers the `collapse` event for the provided node.
   */
  collapseNode(item, index) {
    this.expandService.collapse(index, item);
  }
  /**
   * Gets the current page size of the data item's children collection ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button#managing-page-sizes)).
   * Pass `null` as `dataItem` to get the root collection's page size.
   *
   * @param dataItem {any} - The parent data item of the targeted collection.
   * @returns {number} - The page size of the data item's children collection.
   */
  getNodePageSize(dataItem) {
    this.verifyLoadMoreService();
    return this.loadMoreService.getGroupSize(dataItem);
  }
  /**
   * Sets the page size of the data item's children collection ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button#managing-page-sizes)).
   * Pass `null` as `dataItem` to set the root collection's page size.
   *
   * @param dataItem {any} - The parent data item of the targeted collection.
   * @param pageSize {number} - The new page size.
   */
  setNodePageSize(dataItem, pageSize) {
    this.verifyLoadMoreService();
    this.loadMoreService.setGroupSize(dataItem, pageSize);
  }
  /**
   * @hidden
   *
   * Clears the current TreeViewLookupService node map and re-registers all nodes anew.
   * Child nodes are acquired through the provided `children` callback.
   */
  preloadChildNodes() {
    this.treeViewLookupService.reset();
    this.registerLookupItems(this.nodes);
  }
  attachDomHandlers() {
    const element = this.element.nativeElement;
    this.clickHandler = this.clickHandler.bind(this);
    this.domSubscriptions.push(this.renderer.listen(element, 'contextmenu', this.clickHandler), this.renderer.listen(element, 'click', this.clickHandler), this.renderer.listen(element, 'dblclick', this.clickHandler), this.renderer.listen(element, 'focusin', this.focusHandler.bind(this)), this.renderer.listen(element, 'focusout', this.blurHandler.bind(this)), this.renderer.listen(element, 'keydown', this.keydownHandler.bind(this)));
  }
  focusHandler(e) {
    let focusItem;
    if (match(e.target, '.k-treeview-item')) {
      focusItem = e.target;
    } else if (!isFocusable(e.target)) {
      // with compliments to IE
      focusItem = closestNode(e.target);
    }
    if (focusItem) {
      const nodeIndex = nodeId(e.target);
      if (this.navigationService.isDisabled(nodeIndex)) {
        return;
      }
      this.navigationService.activateIndex(nodeIndex);
      if (!this.isActive && hasObservers(this.onFocus)) {
        this.ngZone.run(() => {
          this.onFocus.emit();
        });
      }
      this.navigationService.isTreeViewActive = true;
    }
  }
  blurHandler(e) {
    if (this.isActive && match(e.target, '.k-treeview-item') && (!e.relatedTarget || !match(e.relatedTarget, '.k-treeview-item') || !hasParent(e.relatedTarget, this.element.nativeElement))) {
      this.navigationService.deactivate();
      this.navigationService.isTreeViewActive = false;
      if (hasObservers(this.onBlur)) {
        this.ngZone.run(() => {
          this.onBlur.emit();
        });
      }
    }
  }
  clickHandler(e) {
    const target = e.target;
    if (e.type === 'contextmenu' && !hasObservers(this.nodeClick) || e.type === 'click' && !hasObservers(this.nodeClick) && !hasObservers(this.selectionChange) && !isLoadMoreButton(target) || e.type === 'dblclick' && !hasObservers(this.nodeDblClick) || isFocusable(target) || !isContent(target) && !isLoadMoreButton(target) || !hasParent(target, this.element.nativeElement)) {
      return;
    }
    const index = nodeId(closestNode(target));
    // the disabled check is probably not needed due to the k-disabled styles
    if (!index || this.navigationService.isDisabled(index)) {
      return;
    }
    this.ngZone.run(() => {
      // record this value before emitting selectionChange (`this.navigationService.selectIndex`), as the treeview state may be changed on its emission
      const lookup = this.treeViewLookupService.itemLookup(index);
      if (e.type === 'click') {
        const loadMoreButton = this.navigationService.model.findNode(index).loadMoreButton;
        if (loadMoreButton) {
          this.navigationService.notifyLoadMore(index);
          return;
        } else {
          this.navigationService.selectIndex(index);
        }
      }
      const emitter = e.type === 'dblclick' ? this.nodeDblClick : this.nodeClick;
      emitter.emit({
        item: lookup.item,
        originalEvent: e,
        type: e.type
      });
    });
  }
  keydownHandler(e) {
    if (this.isActive && this.navigable) {
      this.ngZone.run(() => {
        this.navigationService.move(e);
      });
    }
  }
  verifyLoadMoreService() {
    if (isDevMode() && !isPresent(this.loadMoreService)) {
      throw new Error(`To use the TreeView paging functionality, you need to assign the \`kendoTreeViewLoadMore\` directive. See ${LOAD_MORE_DOC_LINK$1}.`);
    }
  }
  registerLookupItems(data, parentItem = null) {
    if (!isPresent(data) || data.length === 0) {
      return;
    }
    const parentIndex = nodeIndex(parentItem);
    const treeItems = data.map((node, index) => buildTreeItem(node, index, parentIndex));
    if (isPresent(parentItem)) {
      this.treeViewLookupService.registerChildren(parentIndex, treeItems);
    }
    treeItems.forEach(item => {
      this.treeViewLookupService.registerItem(item, parentItem);
      if (this.hasChildren(item.dataItem)) {
        this.children(item.dataItem).subscribe(children => this.registerLookupItems(children, item));
      }
    });
  }
  static ɵfac = function TreeViewComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TreeViewComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(ExpandStateService), i0.ɵɵdirectiveInject(NavigationService), i0.ɵɵdirectiveInject(NodeChildrenService), i0.ɵɵdirectiveInject(SelectionService), i0.ɵɵdirectiveInject(TreeViewLookupService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(DataChangeNotificationService), i0.ɵɵdirectiveInject(i1.LocalizationService));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: TreeViewComponent,
    selectors: [["kendo-treeview"]],
    contentQueries: function TreeViewComponent_ContentQueries(rf, ctx, dirIndex) {
      if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, NodeTemplateDirective, 5)(dirIndex, LoadMoreButtonTemplateDirective, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.nodeTemplateQuery = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.loadMoreButtonTemplateQuery = _t.first);
      }
    },
    viewQuery: function TreeViewComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c3, 7, ViewContainerRef)(_c4, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.assetsContainer = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.searchbox = _t.first);
      }
    },
    hostVars: 4,
    hostBindings: function TreeViewComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵsyntheticHostProperty("@.disabled", ctx.animate);
        i0.ɵɵattribute("dir", ctx.direction);
        i0.ɵɵclassProp("k-treeview", ctx.classNames);
      }
    },
    inputs: {
      filterInputPlaceholder: "filterInputPlaceholder",
      expandDisabledNodes: "expandDisabledNodes",
      animate: "animate",
      nodeTemplateRef: [0, "nodeTemplate", "nodeTemplateRef"],
      loadMoreButtonTemplateRef: [0, "loadMoreButtonTemplate", "loadMoreButtonTemplateRef"],
      trackBy: "trackBy",
      nodes: "nodes",
      textField: "textField",
      hasChildren: "hasChildren",
      isChecked: "isChecked",
      isDisabled: "isDisabled",
      hasCheckbox: "hasCheckbox",
      isExpanded: "isExpanded",
      isSelected: "isSelected",
      isVisible: "isVisible",
      navigable: "navigable",
      children: "children",
      loadOnDemand: "loadOnDemand",
      filterable: "filterable",
      filter: "filter",
      size: "size",
      disableParentNodesOnly: "disableParentNodesOnly"
    },
    outputs: {
      childrenLoaded: "childrenLoaded",
      onBlur: "blur",
      onFocus: "focus",
      expand: "expand",
      collapse: "collapse",
      nodeDragStart: "nodeDragStart",
      nodeDrag: "nodeDrag",
      filterStateChange: "filterStateChange",
      nodeDrop: "nodeDrop",
      nodeDragEnd: "nodeDragEnd",
      addItem: "addItem",
      removeItem: "removeItem",
      checkedChange: "checkedChange",
      selectionChange: "selectionChange",
      filterChange: "filterChange",
      nodeClick: "nodeClick",
      nodeDblClick: "nodeDblClick"
    },
    exportAs: ["kendoTreeView"],
    features: [i0.ɵɵProvidersFeature([...providers, {
      provide: KENDO_WEBMCP_HOST,
      useExisting: forwardRef(() => TreeViewComponent)
    }]), i0.ɵɵNgOnChangesFeature],
    decls: 5,
    vars: 24,
    consts: () => {
      let i18n_0;
      if (typeof ngI18nClosureMode !== "undefined" && ngI18nClosureMode) {
        /**
         * @desc The title of the Load More button
         * @meaning kendo.treeview.loadMore
         */
        const MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_TREEVIEW_FESM2022_PROGRESS_KENDO_ANGULAR_TREEVIEW_MJS_0 = goog.getMsg("Load more...");
        i18n_0 = MSG_D__PROJECTS_FOREVER_MEMORIES_PROJ_FOREVER_MEMORIES_FOREVER_WEB_NODE_MODULES__PROGRESS_KENDO_ANGULAR_TREEVIEW_FESM2022_PROGRESS_KENDO_ANGULAR_TREEVIEW_MJS_0;
      } else {
        i18n_0 = $localize`:kendo.treeview.loadMore|The title of the Load More button:Load more...`;
      }
      return [["assetsContainer", ""], ["searchbox", ""], ["kendoTreeViewLocalizedMessages", "", "loadMore", i18n_0], [1, "k-treeview-filter"], ["kendoTreeViewGroup", "", "role", "tree", 1, "k-treeview-lines", 3, "size", "loadOnDemand", "checkboxes", "expandIcons", "selectable", "touchActions", "children", "hasChildren", "isChecked", "isDisabled", "hasCheckbox", "disableParentNodesOnly", "isExpanded", "isSelected", "isVisible", "nodeTemplateRef", "loadMoreButtonTemplateRef", "textField", "nodes", "loadMoreService", "trackBy", "expandDisabledNodes"], [3, "valueChange", "size", "value", "clearButton", "placeholder"], ["kendoTextBoxPrefixTemplate", ""], ["innerCssClass", "k-input-icon", "name", "search", 3, "svgIcon"]];
    },
    template: function TreeViewComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelementContainer(0, 2);
        i0.ɵɵconditionalCreate(1, TreeViewComponent_Conditional_1_Template, 4, 4, "span", 3);
        i0.ɵɵelement(2, "ul", 4);
        i0.ɵɵelementContainer(3, null, 0);
      }
      if (rf & 2) {
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.filterable ? 1 : -1);
        i0.ɵɵadvance();
        i0.ɵɵproperty("size", ctx.size)("loadOnDemand", ctx.loadOnDemand)("checkboxes", ctx.checkboxes)("expandIcons", ctx.expandIcons)("selectable", ctx.selectable)("touchActions", ctx.touchActions)("children", ctx.children)("hasChildren", ctx.hasChildren)("isChecked", ctx.isChecked)("isDisabled", ctx.isDisabled)("hasCheckbox", ctx.hasCheckbox)("disableParentNodesOnly", ctx.disableParentNodesOnly)("isExpanded", ctx.isExpanded)("isSelected", ctx.isSelected)("isVisible", ctx.isVisible)("nodeTemplateRef", ctx.nodeTemplateRef == null ? null : ctx.nodeTemplateRef.templateRef)("loadMoreButtonTemplateRef", ctx.loadMoreButtonTemplateRef == null ? null : ctx.loadMoreButtonTemplateRef.templateRef)("textField", ctx.textField)("nodes", ctx.fetchNodes)("loadMoreService", ctx.loadMoreService)("trackBy", ctx.trackBy)("expandDisabledNodes", ctx.expandDisabledNodes);
        i0.ɵɵattribute("id", ctx.treeviewId);
      }
    },
    dependencies: [LocalizedMessagesDirective, TextBoxComponent, TextBoxPrefixTemplateDirective, IconWrapperComponent, TreeViewGroupComponent],
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewComponent, [{
    type: Component,
    args: [{
      changeDetection: ChangeDetectionStrategy.Default,
      exportAs: 'kendoTreeView',
      providers: [...providers, {
        provide: KENDO_WEBMCP_HOST,
        useExisting: forwardRef(() => TreeViewComponent)
      }],
      selector: 'kendo-treeview',
      template: `
        <ng-container kendoTreeViewLocalizedMessages
          i18n-loadMore="kendo.treeview.loadMore|The title of the Load More button"
          loadMore="Load more..."
          >
        </ng-container>
        @if (filterable) {
          <span
            class="k-treeview-filter"
            >
            <kendo-textbox
              #searchbox
              [size]="size"
              [value]="filter"
              [clearButton]="true"
              (valueChange)="filterChange.emit($event)"
              [placeholder]="filterInputPlaceholder"
              >
              <ng-template kendoTextBoxPrefixTemplate>
                <kendo-icon-wrapper
                  innerCssClass="k-input-icon"
                  name="search"
                  [svgIcon]="searchIcon"
                  >
                </kendo-icon-wrapper>
              </ng-template>
            </kendo-textbox>
          </span>
        }
        <ul class="k-treeview-lines"
          kendoTreeViewGroup
          [attr.id]="treeviewId"
          role="tree"
          [size]="size"
          [loadOnDemand]="loadOnDemand"
          [checkboxes]="checkboxes"
          [expandIcons]="expandIcons"
          [selectable]="selectable"
          [touchActions]="touchActions"
          [children]="children"
          [hasChildren]="hasChildren"
          [isChecked]="isChecked"
          [isDisabled]="isDisabled"
          [hasCheckbox]="hasCheckbox"
          [disableParentNodesOnly]="disableParentNodesOnly"
          [isExpanded]="isExpanded"
          [isSelected]="isSelected"
          [isVisible]="isVisible"
          [nodeTemplateRef]="nodeTemplateRef?.templateRef"
          [loadMoreButtonTemplateRef]="loadMoreButtonTemplateRef?.templateRef"
          [textField]="textField"
          [nodes]="fetchNodes"
          [loadMoreService]="loadMoreService"
          [trackBy]="trackBy"
          [expandDisabledNodes]="expandDisabledNodes"
          >
        </ul>
        <ng-container #assetsContainer></ng-container>
        `,
      standalone: true,
      imports: [LocalizedMessagesDirective, TextBoxComponent, TextBoxPrefixTemplateDirective, IconWrapperComponent, TreeViewGroupComponent]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: ExpandStateService
  }, {
    type: NavigationService
  }, {
    type: NodeChildrenService
  }, {
    type: SelectionService
  }, {
    type: TreeViewLookupService
  }, {
    type: i0.NgZone
  }, {
    type: i0.Renderer2
  }, {
    type: DataChangeNotificationService
  }, {
    type: i1.LocalizationService
  }], {
    classNames: [{
      type: HostBinding,
      args: ["class.k-treeview"]
    }],
    direction: [{
      type: HostBinding,
      args: ["attr.dir"]
    }],
    assetsContainer: [{
      type: ViewChild,
      args: ['assetsContainer', {
        read: ViewContainerRef,
        static: true
      }]
    }],
    searchbox: [{
      type: ViewChild,
      args: ['searchbox']
    }],
    filterInputPlaceholder: [{
      type: Input
    }],
    expandDisabledNodes: [{
      type: Input
    }],
    animate: [{
      type: Input
    }, {
      type: HostBinding,
      args: ['@.disabled']
    }],
    childrenLoaded: [{
      type: Output
    }],
    onBlur: [{
      type: Output,
      args: ['blur']
    }],
    onFocus: [{
      type: Output,
      args: ['focus']
    }],
    expand: [{
      type: Output
    }],
    collapse: [{
      type: Output
    }],
    nodeDragStart: [{
      type: Output
    }],
    nodeDrag: [{
      type: Output
    }],
    filterStateChange: [{
      type: Output
    }],
    nodeDrop: [{
      type: Output
    }],
    nodeDragEnd: [{
      type: Output
    }],
    addItem: [{
      type: Output
    }],
    removeItem: [{
      type: Output
    }],
    checkedChange: [{
      type: Output
    }],
    selectionChange: [{
      type: Output
    }],
    filterChange: [{
      type: Output
    }],
    nodeClick: [{
      type: Output
    }],
    nodeDblClick: [{
      type: Output
    }],
    nodeTemplateQuery: [{
      type: ContentChild,
      args: [NodeTemplateDirective, {
        static: false
      }]
    }],
    nodeTemplateRef: [{
      type: Input,
      args: ['nodeTemplate']
    }],
    loadMoreButtonTemplateQuery: [{
      type: ContentChild,
      args: [LoadMoreButtonTemplateDirective, {
        static: false
      }]
    }],
    loadMoreButtonTemplateRef: [{
      type: Input,
      args: ['loadMoreButtonTemplate']
    }],
    trackBy: [{
      type: Input
    }],
    nodes: [{
      type: Input
    }],
    textField: [{
      type: Input
    }],
    hasChildren: [{
      type: Input
    }],
    isChecked: [{
      type: Input
    }],
    isDisabled: [{
      type: Input
    }],
    hasCheckbox: [{
      type: Input
    }],
    isExpanded: [{
      type: Input
    }],
    isSelected: [{
      type: Input
    }],
    isVisible: [{
      type: Input
    }],
    navigable: [{
      type: Input
    }],
    children: [{
      type: Input
    }],
    loadOnDemand: [{
      type: Input
    }],
    filterable: [{
      type: Input
    }],
    filter: [{
      type: Input
    }],
    size: [{
      type: Input
    }],
    disableParentNodesOnly: [{
      type: Input
    }]
  });
})();
const indexChecked = (keys, index) => keys.filter(k => k === index).length > 0;
const matchKey = index => k => {
  if (index === k) {
    return true;
  }
  if (!k.split) {
    return false;
  }
  return k.split('_').reduce(({
    key,
    result
  }, part) => {
    key += part;
    if (index === key || result) {
      return {
        result: true
      };
    }
    key += "_";
    return {
      key,
      result: false
    };
  }, {
    key: "",
    result: false
  }).result;
};
/**
 * Represents a directive that manages the in-memory checked state of the TreeView node
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/checkboxes)).
 *
 * @example
 * ```html
 * <kendo-treeview
 *      kendoTreeViewCheckable
 *      checkBy="id"
 *      [(checkedKeys)]="checkedKeys"
 *      [nodes]="data">
 *  </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class CheckDirective {
  treeView;
  zone;
  /**
   * @hidden
   */
  set isChecked(value) {
    this.treeView.isChecked = value;
  }
  /**
   * Defines the item key stored in the `checkedKeys` collection.
   */
  checkKey;
  /**
   * Defines the collection that stores the checked keys
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/checkboxes)).
   */
  checkedKeys;
  /**
   * Defines the checkable settings
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/checkboxes#setup)).
   * If you do not provide a value, the default [`CheckableSettings`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/checkablesettings) apply.
   */
  checkable;
  /**
   * Fires when the `checkedKeys` collection updates.
   */
  checkedKeysChange = new EventEmitter();
  subscriptions = new Subscription();
  get options() {
    const defaultOptions = {
      checkChildren: true,
      checkParents: true,
      enabled: true,
      mode: "multiple",
      uncheckCollapsedChildren: false
    };
    if (!isPresent(this.checkable) || typeof this.checkable === 'string') {
      return defaultOptions;
    }
    const checkSettings = isBoolean(this.checkable) ? {
      enabled: this.checkable
    } : this.checkable;
    return Object.assign(defaultOptions, checkSettings);
  }
  checkActions = {
    'multiple': e => this.checkMultiple(e),
    'single': e => this.checkSingle(e)
  };
  /**
   * Reflects the internal `checkedKeys` state.
   */
  state = new Set();
  clickSubscription;
  /**
   * Holds the last emitted `checkedKeys` collection.
   */
  lastChange;
  constructor(treeView, zone) {
    this.treeView = treeView;
    this.zone = zone;
    this.subscriptions.add(this.treeView.checkedChange.subscribe(e => this.check(e)));
    const expandedItems = [];
    this.subscriptions.add(this.treeView.childrenLoaded.pipe(filter(() => this.options.checkChildren && this.treeView.loadOnDemand), tap(item => expandedItems.push(item)), switchMap(() => this.zone.onStable.pipe(take(1)))).subscribe(() => this.addCheckedItemsChildren(expandedItems)));
    this.treeView.isChecked = this.isItemChecked.bind(this);
  }
  ngOnChanges(changes) {
    if (changes.checkable) {
      this.treeView.checkboxes = this.options.enabled;
      this.toggleCheckOnClick();
    }
    if (isChanged('checkedKeys', changes, false) && changes.checkedKeys.currentValue !== this.lastChange) {
      this.state = new Set(changes.checkedKeys.currentValue);
    }
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.unsubscribeClick();
  }
  isItemChecked(dataItem, index) {
    if (!this.checkKey) {
      return this.isIndexChecked(index);
    }
    const hasKey = this.state.has(this.itemKey({
      dataItem,
      index
    }));
    return hasKey ? 'checked' : 'none';
  }
  isIndexChecked(index) {
    const checkedKeys = Array.from(this.state).filter(matchKey(index));
    if (indexChecked(checkedKeys, index)) {
      return 'checked';
    }
    const {
      mode,
      checkParents
    } = this.options;
    if (mode === 'multiple' && checkParents && checkedKeys.length) {
      return 'indeterminate';
    }
    return 'none';
  }
  itemKey(item) {
    if (!isPresent(this.checkKey)) {
      return item.index;
    }
    if (typeof this.checkKey === "string" && isPresent(item.dataItem)) {
      return item.dataItem[this.checkKey];
    }
    if (typeof this.checkKey === "function") {
      return this.checkKey(item);
    }
  }
  check(e) {
    const {
      enabled,
      mode
    } = this.options;
    const performSelection = this.checkActions[mode] || noop;
    if (!enabled) {
      return;
    }
    performSelection(e);
  }
  checkSingle(node) {
    const key = this.itemKey(node.item);
    const hasKey = this.state.has(key);
    this.state.clear();
    if (!hasKey) {
      this.state.add(key);
    }
    this.notify();
  }
  checkMultiple(node) {
    this.checkNode(node);
    if (this.options.checkParents) {
      this.checkParents(node.parent);
    }
    this.notify();
  }
  toggleCheckOnClick() {
    this.unsubscribeClick();
    if (this.options.checkOnClick) {
      this.clickSubscription = this.treeView.nodeClick.subscribe(args => {
        if (args.type === 'click') {
          const lookup = this.treeView.itemLookup(args.item.index);
          this.check(lookup);
        }
      });
    }
  }
  unsubscribeClick() {
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
      this.clickSubscription = null;
    }
  }
  checkNode(node) {
    if (!isPresent(node.item.dataItem) || this.treeView.isDisabled(node.item.dataItem, node.item.index) || !this.treeView.hasCheckbox(node.item.dataItem, node.item.index)) {
      return;
    }
    const currentKey = this.itemKey(node.item);
    if (!isPresent(currentKey)) {
      return;
    }
    const pendingCheck = [currentKey];
    if (this.options.checkChildren) {
      const descendants = fetchLoadedDescendants(node, ({
        item
      }) => this.treeView.disableParentNodesOnly || this.options.checkDisabledChildren ? this.treeView.isVisible(item.dataItem, item.index) : this.treeView.isVisible(item.dataItem, item.index) && !this.treeView.isDisabled(item.dataItem, item.index) && this.treeView.hasCheckbox(item.dataItem, item.index));
      pendingCheck.push(...descendants.filter(item => this.options.checkDisabledChildren || !this.treeView.isDisabled(item.item.dataItem, item.item.index) || this.treeView.hasCheckbox(item.item.dataItem, item.item.index)).map(({
        item
      }) => this.itemKey(item)));
    }
    const shouldCheck = !this.state.has(currentKey);
    pendingCheck.forEach(key => {
      if (shouldCheck) {
        this.state.add(key);
      } else {
        this.state.delete(key);
        if (this.options.uncheckCollapsedChildren && this.options.mode === 'multiple' && this.treeView.loadOnDemand) {
          if (this.checkKey && this.treeView.hasChildren(node.item.dataItem)) {
            this.uncheckChildren(node.item.dataItem, node.item.index);
            return;
          }
          const checkedKeys = Array.from(this.state).filter(matchKey(node.item.index));
          checkedKeys.forEach(key => this.state.delete(key));
        }
      }
    });
  }
  uncheckChildren(dataItem, parentNodeIndex) {
    this.treeView.children(dataItem).subscribe(children => children.forEach((item, index) => {
      const nodeIndex = `${parentNodeIndex}_${index}`;
      this.state.delete(this.itemKey({
        dataItem: item,
        index: nodeIndex
      }));
      if (this.treeView.hasChildren(item)) {
        this.uncheckChildren(item, nodeIndex);
      }
    }));
  }
  checkParents(parent) {
    if (!isPresent(parent)) {
      return;
    }
    let currentParent = parent;
    while (currentParent) {
      const parentKey = this.itemKey(currentParent.item);
      const isDisabled = this.treeView.isDisabled(currentParent.item.dataItem, currentParent.item.index);
      const allChildrenSelected = currentParent.children.every(item => this.state.has(this.itemKey(item)));
      const hasCheckbox = this.treeView.hasCheckbox(currentParent.item.dataItem, currentParent.item.index);
      if (hasCheckbox && (!isDisabled || this.options.checkDisabledChildren) && allChildrenSelected) {
        this.state.add(parentKey);
      } else {
        this.state.delete(parentKey);
      }
      currentParent = currentParent.parent;
    }
  }
  allChildrenSelected(children) {
    return children.every(item => {
      const childrenSel = this.allChildrenSelected(item.children);
      return this.state.has(this.itemKey(item.item)) && childrenSel;
    });
  }
  notify() {
    this.lastChange = Array.from(this.state);
    this.checkedKeysChange.emit(this.lastChange);
  }
  addCheckedItemsChildren(lookups) {
    if (!isPresent(lookups) || lookups.length === 0) {
      return;
    }
    const initiallyCheckedItemsCount = this.state.size;
    const disabledItems = new Set();
    lookups.forEach(lookup => {
      const itemKey = this.itemKey(lookup.item);
      if (!this.state.has(itemKey)) {
        return;
      }
      lookup.children.forEach(item => {
        // ensure both the parent item and each child node is enabled
        if (!this.treeView.isDisabled(lookup.item.dataItem, lookup.item.index) && !this.treeView.isDisabled(item.dataItem, item.index) && this.treeView.hasCheckbox(lookup.item.dataItem, lookup.item.index) && this.treeView.hasCheckbox(item.dataItem, item.index) || this.treeView.disableParentNodesOnly || this.options.checkDisabledChildren) {
          this.state.add(this.itemKey(item));
        }
        if (this.treeView.disableParentNodesOnly && !this.options.checkDisabledChildren && (this.treeView.isDisabled(item.dataItem, item.index) || !this.treeView.hasCheckbox(item.dataItem, item.index))) {
          disabledItems.add(this.itemKey(item));
        }
      });
    });
    disabledItems.forEach(item => this.state.delete(item));
    const hasNewlyCheckedItems = initiallyCheckedItemsCount !== this.state.size;
    if (hasNewlyCheckedItems) {
      this.zone.run(() => this.notify());
    }
  }
  static ɵfac = function CheckDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CheckDirective)(i0.ɵɵdirectiveInject(TreeViewComponent), i0.ɵɵdirectiveInject(i0.NgZone));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: CheckDirective,
    selectors: [["", "kendoTreeViewCheckable", ""]],
    inputs: {
      isChecked: "isChecked",
      checkKey: [0, "checkBy", "checkKey"],
      checkedKeys: "checkedKeys",
      checkable: [0, "kendoTreeViewCheckable", "checkable"]
    },
    outputs: {
      checkedKeysChange: "checkedKeysChange"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CheckDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewCheckable]',
      standalone: true
    }]
  }], () => [{
    type: TreeViewComponent
  }, {
    type: i0.NgZone
  }], {
    isChecked: [{
      type: Input
    }],
    checkKey: [{
      type: Input,
      args: ["checkBy"]
    }],
    checkedKeys: [{
      type: Input
    }],
    checkable: [{
      type: Input,
      args: ['kendoTreeViewCheckable']
    }],
    checkedKeysChange: [{
      type: Output
    }]
  });
})();

/**
 * Represents a directive which manages the disabled in-memory state of the TreeView node
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/disabled-state)).
 *
 * @example
 * ```html
 * <kendo-treeview
 *      [nodes]="data"
 *      textField="text"
 *      [hasChildren]="hasChildren"
 *      [children]="fetchChildren"
 *      kendoTreeViewDisable
 *      [disabledKeys]="disabledKeys">
 *  </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class DisableDirective {
  treeView;
  cdr;
  /**
   * @hidden
   */
  set isDisabled(value) {
    this.treeView.isDisabled = value;
  }
  /**
   * Defines the item key that will be stored in the `disabledKeys` collection.
   */
  disableKey;
  /**
   * Defines the collection that will store the disabled keys.
   */
  disabledKeys = [];
  constructor(treeView, cdr) {
    this.treeView = treeView;
    this.cdr = cdr;
    this.treeView.isDisabled = (dataItem, index) => this.disabledKeys.indexOf(this.itemKey({
      dataItem,
      index
    })) > -1;
  }
  ngOnChanges(changes = {}) {
    const {
      disabledKeys
    } = changes;
    if (disabledKeys && !disabledKeys.firstChange) {
      this.cdr.markForCheck();
    }
  }
  itemKey(e) {
    if (!this.disableKey) {
      return e.index;
    }
    if (typeof this.disableKey === "string") {
      return e.dataItem[this.disableKey];
    }
    if (typeof this.disableKey === "function") {
      return this.disableKey(e);
    }
  }
  static ɵfac = function DisableDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DisableDirective)(i0.ɵɵdirectiveInject(TreeViewComponent), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DisableDirective,
    selectors: [["", "kendoTreeViewDisable", ""]],
    inputs: {
      isDisabled: "isDisabled",
      disableKey: [0, "kendoTreeViewDisable", "disableKey"],
      disabledKeys: "disabledKeys"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DisableDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewDisable]',
      standalone: true
    }]
  }], () => [{
    type: TreeViewComponent
  }, {
    type: i0.ChangeDetectorRef
  }], {
    isDisabled: [{
      type: Input
    }],
    disableKey: [{
      type: Input,
      args: ["kendoTreeViewDisable"]
    }],
    disabledKeys: [{
      type: Input
    }]
  });
})();

/**
 * Represents the directive that enables you to update the initially provided data array during drag-and-drop.
 *
 * Use this directive with one of the data-binding directives ([`kendoTreeViewHierarchyBinding`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/hierarchybindingdirective)
 * or [`kendoTreeViewFlatDataBinding`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/flatdatabindingdirective)), which set their own edit handlers, or provide
 * your own [`editService`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/editservice) to this directive.
 *
 * Providing a custom `editService` allows you to handle the
 * [`addItem`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#additem) and [`removeItem`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#removeitem)
 * events when they are triggered by the TreeView component.
 *
 * @example
 * ```html
 * <kendo-treeview
 *   kendoTreeViewDragAndDrop
 *   kendoTreeViewDragAndDropEditing
 *   [nodes]="nodes"
 *   textField="text">
 * </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class DragAndDropEditingDirective {
  treeview;
  /**
   * Specifies the handlers called on drag-and-drop [`addItem`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#additem)
   * and [`removeItem`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#removeitem) events.
   */
  set editService(service) {
    this.treeview.editService = service;
  }
  subscriptions = new Subscription();
  constructor(treeview) {
    this.treeview = treeview;
    this.subscriptions.add(this.treeview.addItem.subscribe(this.handleAdd.bind(this)));
    this.subscriptions.add(this.treeview.removeItem.subscribe(this.handleRemove.bind(this)));
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  handleAdd(args) {
    if (!isPresent(this.treeview.editService)) {
      throw new Error('No `editService` provided. Either provide your own implementation or use this directive in combination with one of the data binding directives (`kendoTreeViewHierarchyBinding` or `kendoTreeViewFlatDataBinding`).');
    }
    this.treeview.editService.add(args);
  }
  handleRemove(args) {
    if (!isPresent(this.treeview.editService)) {
      throw new Error('No `editService` provided. Either provide your own implementation or use this directive in combination with one of the data binding directives (`kendoTreeViewHierarchyBinding` or `kendoTreeViewFlatDataBinding`).');
    }
    this.treeview.editService.remove(args);
  }
  static ɵfac = function DragAndDropEditingDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragAndDropEditingDirective)(i0.ɵɵdirectiveInject(TreeViewComponent));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DragAndDropEditingDirective,
    selectors: [["", "kendoTreeViewDragAndDropEditing", ""]],
    inputs: {
      editService: "editService"
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragAndDropEditingDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewDragAndDropEditing]',
      standalone: true
    }]
  }], () => [{
    type: TreeViewComponent
  }], {
    editService: [{
      type: Input
    }]
  });
})();

/**
 * Represents the attempted drop action during dragging.
 * Passed as `action` value to the [`kendoTreeViewDragClueTemplate`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/dragcluetemplatedirective) directive.
 * By default, this value defines the rendered icon in the drag clue.
 */
var DropAction;
(function (DropAction) {
  /**
   * The dragged item is added as a child to the target node.
   */
  DropAction[DropAction["Add"] = 0] = "Add";
  /**
   * The dragged item is inserted above the target node at the same level.
   */
  DropAction[DropAction["InsertTop"] = 1] = "InsertTop";
  /**
   * The dragged item is inserted below the target node at the same level.
   */
  DropAction[DropAction["InsertBottom"] = 2] = "InsertBottom";
  /**
   * The dragged item is inserted in the middle position relative to the target node.
   */
  DropAction[DropAction["InsertMiddle"] = 3] = "InsertMiddle";
  /**
   * The drop action is not valid for the current target location.
   */
  DropAction[DropAction["Invalid"] = 4] = "Invalid";
})(DropAction || (DropAction = {}));

/**
 * Specifies where a dragged item is dropped relative to the target item.
 */
var DropPosition;
(function (DropPosition) {
  /**
   * The item is dropped over the target item.
   */
  DropPosition[DropPosition["Over"] = 0] = "Over";
  /**
   * The item is dropped before the target item.
   */
  DropPosition[DropPosition["Before"] = 1] = "Before";
  /**
   * The item is dropped after the target item.
   */
  DropPosition[DropPosition["After"] = 2] = "After";
})(DropPosition || (DropPosition = {}));

/**
 * @hidden
 */
var ScrollDirection;
(function (ScrollDirection) {
  ScrollDirection[ScrollDirection["Up"] = -1] = "Up";
  ScrollDirection[ScrollDirection["Down"] = 1] = "Down";
})(ScrollDirection || (ScrollDirection = {}));

/**
 * @hidden
 */
class PreventableEvent {
  prevented = false;
  /**
   * Prevents the default action for a specified event.
   * In this way, the source component suppresses the built-in behavior that follows the event.
   */
  preventDefault() {
    this.prevented = true;
  }
  /**
   * If the event is prevented by any of its subscribers, returns `true`.
   *
   * @returns `true` if the default action was prevented. Otherwise, returns `false`.
   */
  isDefaultPrevented() {
    return this.prevented;
  }
}

/**
 * Arguments for the TreeView [`nodeDrop`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedrop) event.
 */
class TreeItemDropEvent extends PreventableEvent {
  /**
   * Reference to the TreeView from which the dragged item originates.
   */
  sourceTree;
  /**
   * Reference to the TreeView onto which the dragged item is dropped.
   */
  destinationTree;
  /**
   * Lookup information for the dragged item.
   */
  sourceItem;
  /**
   * Lookup information for the item onto which the dragged item is dropped.
   */
  destinationItem;
  /**
   * Represents the drop position relative to the target item.
   */
  dropPosition;
  /**
   * The original pointer event containing details about the pointer position.
   */
  originalEvent;
  /**
   * @hidden
   */
  isValid = true;
  /**
   * @hidden
   */
  constructor(initializer, originalEvent) {
    super();
    Object.assign(this, initializer);
    this.originalEvent = originalEvent;
  }
  /**
   * Sets whether the drop action is valid.
   * If set to `false`, the `addItem` and `removeItem` events will not be fired and the drag clue
   * will animate back to the source item to indicate the action is invalid.
   */
  setValid(isValid) {
    this.isValid = isValid;
  }
}

/**
 * Arguments for the TreeView [`nodeDragStart`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedragstart) event.
 */
class TreeItemDragStartEvent extends PreventableEvent {
  /**
   * Lookup information for the dragged item.
   */
  sourceItem;
  /**
   * The original pointer event containing details about the pointer position.
   */
  originalEvent;
  /**
   * @hidden
   */
  constructor(initializer) {
    super();
    Object.assign(this, initializer);
  }
}

/**
 * Arguments for the TreeView [`nodeDrag`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedrag) and
 * [`nodeDragEnd`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedragend) events.
 */
class TreeItemDragEvent {
  /** @hidden */
  constructor() {}
  /**
   * Lookup information for the dragged item.
   */
  sourceItem;
  /**
   * Lookup information for the item currently under the dragged node, if any.
   */
  destinationItem;
  /**
   * The original pointer event containing details about the pointer position.
   */
  originalEvent;
}

/**
 * @hidden
 */
class DragClueComponent {
  cdr;
  hostClasses = true;
  /**
   * The text value of the dragged TreeView item.
   */
  text;
  /**
   * The attempted operation according to the current drop target.
   */
  action;
  /**
   * The look-up info for the currently dragged item.
   */
  sourceItem;
  /**
   * The look-up info for the destination item (if hovering a valid drop target).
   */
  destinationItem;
  /**
   * Defines the drag clue content template.
   */
  template;
  posistionStyle = 'fixed';
  get statusIconClass() {
    switch (this.action) {
      case DropAction.Add:
        return 'plus';
      case DropAction.InsertTop:
        return 'insert-top';
      case DropAction.InsertBottom:
        return 'insert-bottom';
      case DropAction.InsertMiddle:
        return 'insert-middle';
      case DropAction.Invalid:
      default:
        return 'cancel';
    }
  }
  get statusSVGIcon() {
    switch (this.action) {
      case DropAction.Add:
        return plusIcon;
      case DropAction.InsertTop:
        return insertTopIcon;
      case DropAction.InsertBottom:
        return insertBottomIcon;
      case DropAction.InsertMiddle:
        return insertMiddleIcon;
      case DropAction.Invalid:
      default:
        return cancelIcon;
    }
  }
  constructor(cdr) {
    this.cdr = cdr;
  }
  // exposed as a public method that can be called from outside as the component uses `OnPush` strategy
  detectChanges() {
    this.cdr.detectChanges();
  }
  static ɵfac = function DragClueComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragClueComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DragClueComponent,
    selectors: [["kendo-treeview-drag-clue"]],
    hostVars: 6,
    hostBindings: function DragClueComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵstyleProp("position", ctx.posistionStyle);
        i0.ɵɵclassProp("k-header", ctx.hostClasses)("k-drag-clue", ctx.hostClasses);
      }
    },
    decls: 2,
    vars: 2,
    consts: [[3, "ngTemplateOutlet", "ngTemplateOutletContext"], ["innerCssClass", "k-drag-status", 3, "name", "svgIcon"]],
    template: function DragClueComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵconditionalCreate(0, DragClueComponent_Conditional_0_Template, 3, 3);
        i0.ɵɵconditionalCreate(1, DragClueComponent_Conditional_1_Template, 1, 7, null, 0);
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.template ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.template ? 1 : -1);
      }
    },
    dependencies: [IconWrapperComponent, NgTemplateOutlet],
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragClueComponent, [{
    type: Component,
    args: [{
      changeDetection: ChangeDetectionStrategy.OnPush,
      selector: 'kendo-treeview-drag-clue',
      template: `
        @if (!template) {
          <kendo-icon-wrapper
            innerCssClass="k-drag-status"
            [name]="statusIconClass"
            [svgIcon]="statusSVGIcon"
            >
          </kendo-icon-wrapper>
          <span>{{text}}</span>
        }
        
        @if (template) {
          <ng-template
            [ngTemplateOutlet]="template"
            [ngTemplateOutletContext]="{
                text: text,
                action: action,
                sourceItem: sourceItem,
                destinationItem: destinationItem
            }"
            >
          </ng-template>
        }
        `,
      standalone: true,
      imports: [IconWrapperComponent, NgTemplateOutlet]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }], {
    hostClasses: [{
      type: HostBinding,
      args: ['class.k-header']
    }, {
      type: HostBinding,
      args: ['class.k-drag-clue']
    }],
    posistionStyle: [{
      type: HostBinding,
      args: ['style.position']
    }]
  });
})();

/**
 * @hidden
 */
class DragAndDropAssetService {
  get componentRef() {
    if (!isPresent(this._componentRef)) {
      throw new Error('The `initalize` method must be called before calling other service methods.');
    }
    return this._componentRef;
  }
  set componentRef(componentRef) {
    this._componentRef = componentRef;
  }
  get element() {
    return this.componentRef.location.nativeElement;
  }
  _componentRef;
  ngOnDestroy() {
    if (!isPresent(this._componentRef)) {
      return;
    }
    this.element.parentElement.removeChild(this.element);
    this.componentRef.destroy();
    this.componentRef = null;
  }
  show() {
    this.element.style.display = '';
  }
  hide() {
    this.element.style.display = 'none';
  }
  move(left, top, offset = 0) {
    this.element.style.left = `${left + offset}px`;
    this.element.style.top = `${top + offset}px`;
  }
  static ɵfac = function DragAndDropAssetService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragAndDropAssetService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DragAndDropAssetService,
    factory: DragAndDropAssetService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragAndDropAssetService, [{
    type: Injectable
  }], null, null);
})();

/**
 * Checks if the browser supports relative stacking context.
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context
 */
const hasRelativeStackingContext = memoize(() => {
  if (!(isDocumentAvailable() && isPresent(document.body))) {
    return false;
  }
  const top = 10;
  const parent = document.createElement("div");
  parent.style.transform = "matrix(10, 0, 0, 10, 0, 0)";
  const innerDiv = document.createElement('div');
  innerDiv.innerText = 'child';
  innerDiv.style.position = 'fixed';
  innerDiv.style.top = `${top}px`;
  parent.appendChild(innerDiv);
  document.body.appendChild(parent);
  const isDifferent = parent.children[0].getBoundingClientRect().top !== top;
  document.body.removeChild(parent);
  return isDifferent;
});
/**
 * Stores the result of the passed function's first invokation and returns it instead of invoking it again afterwards.
 */
function memoize(fn) {
  let result;
  let called = false;
  return (...args) => {
    if (called) {
      return result;
    }
    result = fn(...args);
    called = true;
    return result;
  };
}
/**
 * @hidden
 *
 * Gets the offset of the parent element if the latter has the `transform` CSS prop applied.
 * Transformed parents create new stacking context and the `fixed` children must be position based on the transformed parent.
 * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context
 *
 * If no parent container is `transform`-ed the function will return `{ left: 0, top: 0 }`;
 */
const getContainerOffset = element => {
  if (!(element && hasRelativeStackingContext())) {
    return {
      left: 0,
      top: 0
    };
  }
  let offsetParent = element.parentElement;
  while (offsetParent) {
    if (window.getComputedStyle(offsetParent).transform !== 'none') {
      break;
    }
    offsetParent = offsetParent.parentElement;
  }
  if (offsetParent) {
    const rect = offsetParent.getBoundingClientRect();
    return {
      left: rect.left - offsetParent.scrollLeft,
      top: rect.top - offsetParent.scrollTop
    };
  }
  return {
    left: 0,
    top: 0
  };
};
/**
 * @hidden
 */
const getDropAction = (dropPosition, dropTarget) => {
  if (!(isPresent(dropPosition) && isPresent(dropTarget))) {
    return DropAction.Invalid;
  }
  switch (dropPosition) {
    case DropPosition.Over:
      return DropAction.Add;
    case DropPosition.Before:
      return isPresent(closestNode(dropTarget).previousElementSibling) ? DropAction.InsertMiddle : DropAction.InsertTop;
    case DropPosition.After:
      return isPresent(closestNode(dropTarget).nextElementSibling) ? DropAction.InsertMiddle : DropAction.InsertBottom;
    default:
      return DropAction.Invalid;
  }
};
/**
 * @hidden
 */
const getDropPosition = (draggedItem, target, clientY, targetTreeView, containerOffset) => {
  if (!(isPresent(draggedItem) && isPresent(target) && isPresent(targetTreeView) && isPresent(containerOffset))) {
    return;
  }
  // the .k-treeview-mid element starts just after the checkbox/expand arrow and stretches till the end of the treeview on the right
  const item = closestWithMatch(target, '.k-treeview-item-content');
  if (!isPresent(item)) {
    return;
  }
  // the content element (.k-treeview-leaf:not(.k-treeview-load-more-button)) holds just the treeview item text
  const content = getContentElement(item);
  const targetChildOfDraggedItem = hasParent(item, closestNode(draggedItem));
  if (!isPresent(content) || content === draggedItem || targetChildOfDraggedItem) {
    return;
  }
  const itemViewPortCoords = content.getBoundingClientRect();
  /*
      if the user is hovering a treeview item, split the item height into four parts:
          - dropping into the top quarter should insert the dragged item before the drop target
          - dropping into the bottom quarter should insert the dragged item after the drop target
          - dropping into the second or third quarter should add the item as child node of the drop target
       if the user is NOT hovering a treeview item (he's dragging somewhere on the right), split the item height to just two parts:
          - dropping should insert before or after
  */
  const itemDivisionHeight = itemViewPortCoords.height / (isContent(target) ? 4 : 2);
  // clear any possible container offset created by parent elements with `transform` css property set
  const pointerPosition = clientY - containerOffset.top;
  const itemTop = itemViewPortCoords.top - containerOffset.top;
  if (pointerPosition < itemTop + itemDivisionHeight) {
    return DropPosition.Before;
  }
  if (pointerPosition >= itemTop + itemViewPortCoords.height - itemDivisionHeight) {
    return DropPosition.After;
  }
  return DropPosition.Over;
};
/**
 * @hidden
 */
const treeItemFromEventTarget = (treeView, dropTarget) => {
  if (!(isPresent(treeView) && isPresent(dropTarget))) {
    return null;
  }
  const node = closestNode(dropTarget);
  const index = nodeId(node);
  const lookup = treeView.itemLookup(index);
  if (!(isPresent(lookup) && isPresent(lookup.item.dataItem))) {
    return null;
  }
  return lookup;
};
/**
 * @hidden
 *
 * Emits `collapse` on the specified TreeView node if the latter is left empty after its last child node was dragged out.
 */
const collapseEmptyParent = (parent, parentNodes, treeview) => {
  if (isPresent(parent) && parentNodes.length === 0 && treeview.isExpanded(parent.item.dataItem, parent.item.index)) {
    treeview.collapseNode(parent.item.dataItem, parent.item.index);
  }
};
/**
 * @hidden
 *
 * Expands the node if it's dropped into and it's not yet expanded.
 */
const expandDropTarget = (dropTarget, treeView) => {
  if (!treeView.isExpanded(dropTarget.item.dataItem, dropTarget.item.index)) {
    treeView.expandNode(dropTarget.item.dataItem, dropTarget.item.index);
  }
};
/**
 * @hidden
 *
 * Extracts the event target from the viewport coords. Required for touch devices
 * where the `event.target` of a `pointermove` event is always the initially dragged item.
 */
const getDropTarget = event => {
  if (!(isDocumentAvailable() && isPresent(document.elementFromPoint))) {
    return event.target;
  }
  return document.elementFromPoint(event.clientX, event.clientY);
};
/**
 * @hidden
 *
 * Checks if the original index is before the new one and corrects the new one by decrementing the index for the level, where the original item stood.
 */
const updateMovedItemIndex = (newIndex, originalIndex) => {
  const movedItemNewIndexParts = newIndex.split('_');
  const originalItemIndexParts = originalIndex.split('_');
  // if the original item was moved from a deeper level, there's no need for index correction
  // e.g. 4_0_1 is moved to 5_0 => removing 4_0_1 will not cause 5_0 to be moved
  if (movedItemNewIndexParts.length < originalItemIndexParts.length) {
    return newIndex;
  }
  // check if the parent item paths are the same - index correction is not required when the original item path differs from the path of the moved item - they belong to different hierarchies
  // e.g. 4_1 is moved to 5_1 - the parent item paths are differen (4 compared to 5) => removing 4_1 will not cause 5_1 to be moved
  // e.g 4_1 is moved to 4_3 - the parent paths are the same (both 4) => removing 4_1 will cause 4_3 to be moved
  const originalItemParentPathLength = originalItemIndexParts.length - 1;
  const originalItemParentPath = originalItemIndexParts.slice(0, originalItemParentPathLength).join('_');
  const movedItemParentPath = movedItemNewIndexParts.slice(0, originalItemParentPathLength).join('_');
  // check if the the index of the level where the original item is taken from is greater than the one of the moved item
  // e.g. 4_5 is moved to 4_1 (comapre 5 and 1) => removing 4_5 will not cause 4_1 to be moved
  // e.g. 4_1 is moved to 4_5 (comapre 1 and 5) => removing 4_1 will cause 4_5 to be moved
  const originalItemIndexLevel = originalItemIndexParts.length - 1;
  const originalItemLevelIndex = Number(originalItemIndexParts[originalItemIndexLevel]);
  const movedItemLevelIndex = Number(movedItemNewIndexParts[originalItemIndexLevel]);
  if (originalItemParentPath === movedItemParentPath && movedItemLevelIndex > originalItemLevelIndex) {
    // if the removed item causes the dropped item to be moved a position up - decrement the index at that level
    movedItemNewIndexParts[originalItemIndexLevel] = String(movedItemLevelIndex - 1);
    return movedItemNewIndexParts.join('_');
  }
  return newIndex;
};
/**
 * @hidden
 */
const SCROLLBAR_REG_EXP = new RegExp('(auto|scroll)');
/**
 * @hidden
 *
 * Retrives the first scrollable element starting the search from the provided one, traversing to the top of the DOM tree.
 */
const getScrollableContainer = node => {
  while (isPresent(node) && node.nodeName !== 'HTML') {
    const hasOverflow = node.scrollHeight > node.clientHeight;
    const hasScrollbar = SCROLLBAR_REG_EXP.test(getComputedStyle(node).overflowY);
    if (hasOverflow && hasScrollbar) {
      return node;
    }
    node = node.parentNode;
  }
  return node;
};
/**
 * @hidden
 *
 * Checks if the top of the scrollable element is reached.
 * Floors the scrollTop value.
 */
const isTopReached = element => Math.floor(element.scrollTop) <= 0;
/**
 * @hidden
 *
 * Checks if the bottom of the scrollable element is reached.
 * Ceils the scrollTop value.
 */
const isBottomReached = element => Math.ceil(element.scrollTop) >= element.scrollHeight - element.clientHeight;
/**
 * @hidden
 *
 * Scrolls the element in the given direction by the provided step.
 *
 * If the targeted scroll incrementation doesn't yield any result due to device pixel ratio issues (https://github.com/dimitar-pechev/RenderingIndependentScrollOffsets#readme),
 * increments the step with 1px and again attempts to change the scrollTop of the element, until the content is actually scrolled.
 *
 * Cuts the operation short after 20 unsuccessful attempts to prevent infinite loops in possible corner-case scenarios.
 */
const scrollElementBy = (element, step, direction) => {
  if (!(isPresent(element) && isDocumentAvailable())) {
    return;
  }
  const initialScrollTop = element.scrollTop;
  let currentStep = step;
  let iterations = 0;
  while (initialScrollTop === element.scrollTop && !(direction === ScrollDirection.Up && isTopReached(element)) && !(direction === ScrollDirection.Down && isBottomReached(element)) && iterations < 20 // as the bulgarian saying goes - to ties our underpants
  ) {
    element.scrollTop += currentStep * direction;
    currentStep += 1;
    iterations += 1;
  }
};

/**
 * @hidden
 */
const CLUE_OFFSET = 10;
/**
 * @hidden
 */
const RETURN_ANIMATION_DURATION = 200;
/**
 * @hidden
 */
class DragClueService extends DragAndDropAssetService {
  returnAnimation;
  scrollInterval;
  constructor() {
    super();
  }
  initialize(container, template) {
    if (isPresent(this._componentRef)) {
      this.ngOnDestroy();
    }
    this.componentRef = container.createComponent(DragClueComponent);
    this.hide();
    this.componentRef.instance.template = template;
    this.componentRef.changeDetectorRef.detectChanges();
  }
  ngOnDestroy() {
    this.cancelReturnAnimation();
    this.cancelScroll();
    super.ngOnDestroy();
  }
  move(left, top) {
    super.move(left, top, CLUE_OFFSET);
  }
  animateDragClueToElementPosition(target) {
    if (!(isPresent(target) && isPresent(this.element.animate))) {
      this.hide();
      return;
    }
    const targetElementViewPortCoords = target.getBoundingClientRect();
    const clueElementViewPortCoords = this.element.getBoundingClientRect();
    this.returnAnimation = this.element.animate([{
      transform: 'translate(0, 0)'
    }, {
      transform: `translate(${targetElementViewPortCoords.left - clueElementViewPortCoords.left}px, ${targetElementViewPortCoords.top - clueElementViewPortCoords.top}px)`
    }], RETURN_ANIMATION_DURATION);
    this.returnAnimation.onfinish = () => this.hide();
  }
  cancelReturnAnimation() {
    if (!isPresent(this.returnAnimation)) {
      return;
    }
    this.returnAnimation.cancel();
    this.returnAnimation = null;
  }
  updateDragClueData(action, sourceItem, destinationItem) {
    const dragClue = this.componentRef.instance;
    if (action === dragClue.action && dataItemsEqual(sourceItem, dragClue.sourceItem) && dataItemsEqual(destinationItem, dragClue.destinationItem)) {
      return;
    }
    dragClue.action = action;
    dragClue.sourceItem = sourceItem;
    dragClue.destinationItem = destinationItem;
    dragClue.detectChanges();
  }
  updateText(text) {
    if (text === this.componentRef.instance.text) {
      return;
    }
    this.componentRef.instance.text = text;
    this.componentRef.instance.detectChanges();
  }
  /**
   * Triggers the first scrollable parent to scroll upwards or downwards.
   * Uses setInterval, so should be called outside the angular zone.
   */
  scrollIntoView({
    step,
    interval
  }) {
    this.cancelScroll();
    const scrollableContainer = getScrollableContainer(this.element);
    if (!isPresent(scrollableContainer)) {
      return;
    }
    const containerRect = scrollableContainer.getBoundingClientRect();
    const clueRect = this.element.getBoundingClientRect();
    // if the beginning of the scrollable container is above the current viewport, fall-back to 0
    const firstVisibleClientTopPart = Math.max(containerRect.top, 0);
    // start scrolling up when the first visible item is dragged over
    const topLimit = firstVisibleClientTopPart + clueRect.height;
    // if the end of the scrollable container is beneath the current viewport, fall-back to its client height
    // add the distance from the start of the viewport to the beginning of the container to ensure scrolling bottom begins when the actual end of the container is reached
    const bottomLimit = firstVisibleClientTopPart + Math.min(containerRect.bottom, scrollableContainer.clientHeight);
    if (clueRect.top < topLimit) {
      this.scrollInterval = setInterval(() => scrollElementBy(scrollableContainer, step, ScrollDirection.Up), interval);
    } else if (clueRect.bottom > bottomLimit) {
      this.scrollInterval = setInterval(() => scrollElementBy(scrollableContainer, step, ScrollDirection.Down), interval);
    }
  }
  /**
   * Cancels out the on-going scroll animation, if present.
   */
  cancelScroll() {
    if (isPresent(this.scrollInterval)) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }
  static ɵfac = function DragClueService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragClueService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DragClueService,
    factory: DragClueService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragClueService, [{
    type: Injectable
  }], () => [], null);
})();

/**
 * @hidden
 */
class DropHintComponent {
  changeDetectorRef;
  hostClass = true;
  /**
   * Defines the drag hint content template.
   */
  template;
  position = 'fixed';
  pointerEvents = 'none';
  /**
   * The attempted operation according to the current drop target.
   */
  action;
  /**
   * The look-up info for the currently dragged item.
   */
  sourceItem;
  /**
   * The look-up info for the destination item.
   */
  destinationItem;
  constructor(changeDetectorRef) {
    this.changeDetectorRef = changeDetectorRef;
  }
  // exposed as a public method that can be called from outside as the component uses `OnPush` strategy
  detectChanges() {
    this.changeDetectorRef.detectChanges();
  }
  static ɵfac = function DropHintComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DropHintComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DropHintComponent,
    selectors: [["kendo-treeview-drop-hint"]],
    hostVars: 6,
    hostBindings: function DropHintComponent_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵstyleProp("position", ctx.position)("pointer-events", ctx.pointerEvents);
        i0.ɵɵclassProp("k-drop-hint-container", ctx.hostClass);
      }
    },
    decls: 2,
    vars: 2,
    consts: [[1, "k-drop-hint", "k-drop-hint-h"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [1, "k-drop-hint-start"], [1, "k-drop-hint-line"]],
    template: function DropHintComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵconditionalCreate(0, DropHintComponent_Conditional_0_Template, 3, 0, "div", 0);
        i0.ɵɵconditionalCreate(1, DropHintComponent_Conditional_1_Template, 1, 6, null, 1);
      }
      if (rf & 2) {
        i0.ɵɵconditional(!ctx.template ? 0 : -1);
        i0.ɵɵadvance();
        i0.ɵɵconditional(ctx.template ? 1 : -1);
      }
    },
    dependencies: [NgTemplateOutlet],
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DropHintComponent, [{
    type: Component,
    args: [{
      changeDetection: ChangeDetectionStrategy.OnPush,
      selector: 'kendo-treeview-drop-hint',
      template: `
        @if (!template) {
          <div
              class="k-drop-hint k-drop-hint-h"
          >
              <div class='k-drop-hint-start'></div>
              <div class='k-drop-hint-line'></div>
          </div>
        }

        @if (template) {
          <ng-template
              [ngTemplateOutlet]="template"
              [ngTemplateOutletContext]="{
                  action: action,
                  sourceItem: sourceItem,
                  destinationItem: destinationItem
              }"
          >
          </ng-template>
        }
    `,
      standalone: true,
      imports: [NgTemplateOutlet]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }], {
    hostClass: [{
      type: HostBinding,
      args: ['class.k-drop-hint-container']
    }],
    position: [{
      type: HostBinding,
      args: ['style.position']
    }],
    pointerEvents: [{
      type: HostBinding,
      args: ['style.pointer-events']
    }]
  });
})();

/**
 * @hidden
 */
class DropHintService extends DragAndDropAssetService {
  constructor() {
    super();
  }
  initialize(container, template) {
    if (isPresent(this._componentRef)) {
      this.ngOnDestroy();
    }
    this.componentRef = container.createComponent(DropHintComponent);
    this.hide();
    this.componentRef.instance.template = template;
    this.componentRef.changeDetectorRef.detectChanges();
  }
  updateDropHintData(action, sourceItem, destinationItem) {
    const dropHint = this.componentRef.instance;
    if (action === dropHint.action && dataItemsEqual(sourceItem, dropHint.sourceItem) && dataItemsEqual(destinationItem, dropHint.destinationItem)) {
      return;
    }
    dropHint.action = action;
    dropHint.sourceItem = sourceItem;
    dropHint.destinationItem = destinationItem;
    dropHint.detectChanges();
  }
  static ɵfac = function DropHintService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DropHintService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DropHintService,
    factory: DropHintService.ɵfac
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DropHintService, [{
    type: Injectable
  }], () => [], null);
})();

/**
 * Represents the template for the TreeView drag clue when you drag an item.
 *
 * To define the drag clue template, nest an `<ng-template>` tag with the `kendoTreeViewDragClueTemplate` directive inside a `<kendo-treeview>` tag
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#templates)).
 *
 * The template context provides the following variables:
 * - `let-text="text"` (`string`)&mdash;The display text of the item being dragged.
 * - `let-action="action"` ([`DropAction`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/dropaction))&mdash;The type of drop action that will occur.
 * - `let-sourceItem="sourceItem"` ([`TreeItemLookup`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeitemlookup))&mdash;The TreeView item that is being dragged from its original position.
 * - `let-destinationItem="destinationItem"` ([`TreeItemLookup`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeitemlookup))&mdash;The TreeView item that serves as the target for the drop operation.
 *
 * @example
 * ```html
 * <kendo-treeview>
 *   <ng-template kendoTreeViewDragClueTemplate let-text="text">
 *     Dragging: {{ text }}
 *   </ng-template>
 * </kendo-treeview>
 * ```
 */
class DragClueTemplateDirective {
  templateRef;
  constructor(templateRef) {
    this.templateRef = templateRef;
  }
  static ɵfac = function DragClueTemplateDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragClueTemplateDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef, 8));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DragClueTemplateDirective,
    selectors: [["", "kendoTreeViewDragClueTemplate", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragClueTemplateDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewDragClueTemplate]',
      standalone: true
    }]
  }], () => [{
    type: i0.TemplateRef,
    decorators: [{
      type: Optional
    }]
  }], null);
})();

/**
 * Represents the template for the TreeView drop hint when you drag an item.
 *
 * To define the hint template, nest an `<ng-template>` tag with the `kendoTreeViewDropHintTemplate` directive inside a `<kendo-treeview>` tag
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#templates)).
 *
 * The template context provides the following variables:
 * - `let-action="action"` ([`DropAction`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/dropaction))&mdash;The drop action being performed.
 * - `let-sourceItem="sourceItem"` ([`TreeItemLookup`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeitemlookup))&mdash;The item being dragged.
 * - `let-destinationItem="destinationItem"` ([`TreeItemLookup`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeitemlookup))&mdash;The target item for the drop operation.
 *
 * @example
 * ```html
 * <kendo-treeview>
 *   <ng-template kendoTreeViewDropHintTemplate let-action="action">
 *     Drop action: {{ action }}
 *   </ng-template>
 * </kendo-treeview>
 * ```
 */
class DropHintTemplateDirective {
  templateRef;
  constructor(templateRef) {
    this.templateRef = templateRef;
  }
  static ɵfac = function DropHintTemplateDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DropHintTemplateDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef, 8));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DropHintTemplateDirective,
    selectors: [["", "kendoTreeViewDropHintTemplate", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DropHintTemplateDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewDropHintTemplate]',
      standalone: true
    }]
  }], () => [{
    type: i0.TemplateRef,
    decorators: [{
      type: Optional
    }]
  }], null);
})();
const DEFAULT_SCROLL_SETTINGS = {
  enabled: true,
  step: 1,
  interval: 1
};
/**
 * Represents the directive that enables you to drag and drop items inside the current TreeView or between multiple linked TreeView component instances
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop)).
 *
 * Triggers the [`nodeDragStart`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedragstart),
 * [`nodeDrag`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedrag),
 * [`nodeDrop`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedrop),
 * [`nodeDragEnd`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#nodedragend),
 * [`addItem`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#additem) and
 * [`removeItem`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/treeviewcomponent#removeitem)
 * events when you perform the corresponding actions.
 *
 * @example
 * ```html
 * <kendo-treeview
 *    kendoTreeViewDragAndDrop
 *    [dropZoneTreeViews]="treeView"
 * >
 * </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class DragAndDropDirective {
  element;
  zone;
  treeview;
  dragClueService;
  dropHintService;
  /**
   * Specifies whether the `removeItem` event fires after the user drops an item while pressing the `ctrl` key.
   * If enabled, the `removeItem` event does not fire on the source TreeView
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#multiple-treeviews)).
   *
   * @default false
   */
  allowCopy = false;
  /**
   * Specifies the `TreeViewComponent` instances into which the user can drop dragged items from the current `TreeViewComponent`
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#multiple-treeviews)).
   */
  dropZoneTreeViews = [];
  /**
   * Specifies the distance in pixels from the initial item `pointerdown` event before dragging starts.
   * The `nodeDragStart` and all subsequent TreeView drag events do not fire until dragging begins.
   *
   * @default 5
   */
  startDragAfter = 5;
  /**
   * Controls the auto-scrolling behavior during drag-and-drop ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/drag-and-drop#auto-scrolling)).
   * Enabled by default. To turn off auto-scrolling, set this property to `false`.
   *
   * By default, scrolling occurs by 1 pixel every 1 millisecond when the dragged item reaches the top or bottom of the scrollable container.
   * You can override the `step` and `interval` by providing a `DragAndDropScrollSettings` object.
   *
   * @default true
   */
  autoScroll = true;
  /**
   * @hidden
   */
  dragClueTemplate;
  /**
   * @hidden
   */
  dropHintTemplate;
  /**
   * @hidden
   */
  userSelectStyle = 'none';
  draggable;
  draggedItem;
  /**
   * The pointer event of the last successful item pointerdown event (the draggable `press` event).
   * Used for determining whether the `startDragAfter` distance is covered and for the `nodeDragStart` event args.
   * Used also as a flag for whether a drag attempt is pending. Should be set to `null` once the dragging begins.
   */
  pendingDragStartEvent;
  get scrollSettings() {
    const userProvidedSettings = typeof this.autoScroll === 'boolean' ? {
      enabled: this.autoScroll
    } : this.autoScroll;
    return Object.assign({}, DEFAULT_SCROLL_SETTINGS, userProvidedSettings);
  }
  /**
   * Describes the offset of the parent element if the latter has the `transform` CSS prop applied.
   * Transformed parents create new stacking context and the fixed children must be position based on the transformed parent.
   * https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Positioning/Understanding_z_index/The_stacking_context
   */
  containerOffset = {
    top: 0,
    left: 0
  };
  constructor(element, zone, treeview, dragClueService, dropHintService) {
    this.element = element;
    this.zone = zone;
    this.treeview = treeview;
    this.dragClueService = dragClueService;
    this.dropHintService = dropHintService;
    this.treeview.touchActions = false;
  }
  ngAfterContentInit() {
    this.initalizeDraggable();
    this.dragClueService.initialize(this.treeview.assetsContainer, this.dragClueTemplate && this.dragClueTemplate.templateRef);
    this.dropHintService.initialize(this.treeview.assetsContainer, this.dropHintTemplate && this.dropHintTemplate.templateRef);
  }
  ngOnDestroy() {
    this.draggable.destroy();
  }
  /**
   * @hidden
   */
  handlePress({
    originalEvent
  }) {
    if (!isContent(originalEvent.target)) {
      return;
    }
    // store the drag target on press, show it only when it's actually dragged
    this.draggedItem = closestWithMatch(originalEvent.target, '.k-treeview-item-content');
    // record the current pointer down coords - copared to the `startDragAfter` value to calculate whether to initiate dragging
    this.pendingDragStartEvent = originalEvent;
  }
  /**
   * @hidden
   */
  handleDrag({
    originalEvent,
    clientX,
    clientY
  }) {
    if (this.shouldInitiateDragStart({
      clientX,
      clientY
    })) {
      this.initiateDragStart();
    }
    if (!isPresent(this.draggedItem) || isPresent(this.pendingDragStartEvent)) {
      return;
    }
    const dropTarget = getDropTarget(originalEvent);
    if (hasObservers(this.treeview.nodeDrag)) {
      this.zone.run(() => this.notifyDrag(originalEvent, dropTarget));
    }
    const targetTreeView = this.getTargetTreeView(dropTarget);
    const dropPosition = getDropPosition(this.draggedItem, dropTarget, clientY, targetTreeView, this.containerOffset);
    const dropHintAnchor = closestWithMatch(dropTarget, '.k-treeview-item-content');
    const dropAction = getDropAction(dropPosition, dropTarget);
    const sourceItem = treeItemFromEventTarget(this.treeview, this.draggedItem);
    const destinationItem = treeItemFromEventTarget(targetTreeView, dropTarget);
    this.updateDropHintState(dropPosition, dropHintAnchor, dropAction, sourceItem, destinationItem);
    this.updateDragClueState(dropAction, clientX, clientY, sourceItem, destinationItem);
    if (this.scrollSettings.enabled) {
      this.dragClueService.scrollIntoView(this.scrollSettings);
    }
  }
  /**
   * @hidden
   */
  handleRelease({
    originalEvent,
    clientY
  }) {
    if (this.scrollSettings.enabled) {
      this.dragClueService.cancelScroll();
    }
    if (!isPresent(this.draggedItem) || isPresent(this.pendingDragStartEvent)) {
      this.pendingDragStartEvent = null;
      this.draggedItem = null;
      return;
    }
    const dropTarget = getDropTarget(originalEvent);
    const sourceTree = this.treeview;
    const destinationTree = this.getTargetTreeView(dropTarget);
    const dropPosition = getDropPosition(this.draggedItem, dropTarget, clientY, this.getTargetTreeView(dropTarget), this.containerOffset);
    const sourceItem = treeItemFromEventTarget(sourceTree, this.draggedItem);
    const destinationItem = treeItemFromEventTarget(destinationTree, dropTarget);
    if (isPresent(destinationItem) && isPresent(dropPosition)) {
      this.zone.run(() => this.notifyDrop({
        sourceItem,
        destinationItem,
        dropPosition,
        sourceTree,
        destinationTree
      }, originalEvent));
    } else {
      this.dragClueService.animateDragClueToElementPosition(this.draggedItem);
    }
    if (hasObservers(this.treeview.nodeDragEnd)) {
      this.zone.run(() => this.notifyDragEnd({
        sourceItem,
        destinationItem,
        originalEvent
      }));
    }
    this.dropHintService.hide();
    this.draggedItem = null;
  }
  updateDropHintState(dropPosition, dropHintAnchor, dropAction, sourceItem, destinationItem) {
    if (!isPresent(dropHintAnchor) || dropPosition === DropPosition.Over || !isPresent(dropPosition)) {
      this.dropHintService.hide();
      return;
    }
    const anchorViewPortCoords = dropHintAnchor.getBoundingClientRect();
    const insertBefore = dropPosition === DropPosition.Before;
    const top = insertBefore ? anchorViewPortCoords.top : anchorViewPortCoords.top + anchorViewPortCoords.height;
    this.dropHintService.updateDropHintData(dropAction, sourceItem, destinationItem);
    // clear any possible container offset created by parent elements with `transform` css property set
    this.dropHintService.move(anchorViewPortCoords.left - this.containerOffset.left, top - this.containerOffset.top);
    this.dropHintService.show();
  }
  updateDragClueState(dropAction, clientX, clientY, sourceItem, destinationItem) {
    // clear any possible container offset created by parent elements with `transform` css property set
    this.dragClueService.move(clientX - this.containerOffset.left, clientY - this.containerOffset.top);
    this.dragClueService.updateDragClueData(dropAction, sourceItem, destinationItem);
    this.dragClueService.show();
  }
  initalizeDraggable() {
    this.draggable = new Draggable({
      press: this.handlePress.bind(this),
      drag: this.handleDrag.bind(this),
      release: this.handleRelease.bind(this)
    });
    this.zone.runOutsideAngular(() => this.draggable.bindTo(this.element.nativeElement));
  }
  notifyDragStart(originalEvent, dropTarget) {
    const sourceItem = treeItemFromEventTarget(this.treeview, dropTarget);
    const event = new TreeItemDragStartEvent({
      sourceItem,
      originalEvent
    });
    this.treeview.nodeDragStart.emit(event);
    return event;
  }
  notifyDrag(originalEvent, dropTarget) {
    const dragEvent = {
      sourceItem: treeItemFromEventTarget(this.treeview, this.draggedItem),
      destinationItem: treeItemFromEventTarget(this.getTargetTreeView(dropTarget), dropTarget),
      originalEvent
    };
    this.treeview.nodeDrag.emit(dragEvent);
  }
  notifyDrop(args, originalEvent) {
    const event = new TreeItemDropEvent(args, originalEvent);
    args.destinationTree.nodeDrop.emit(event);
    // disable the animations on drop and restore them afterwards (if they were initially turned on)
    this.disableAnimationsForNextTick(args.destinationTree);
    if (args.sourceTree !== args.destinationTree) {
      this.disableAnimationsForNextTick(args.sourceTree);
    }
    if (!event.isDefaultPrevented() && event.isValid) {
      this.dragClueService.hide();
      // order matters in a flat data binding scenario (first add, then remove)
      args.destinationTree.addItem.emit(args);
      if (!(originalEvent.ctrlKey && this.allowCopy)) {
        args.sourceTree.removeItem.emit(args);
      }
    } else if (event.isDefaultPrevented()) {
      // directly hide the clue if the default is prevented
      this.dragClueService.hide();
    } else if (!event.isValid) {
      // animate the clue back to the source item position if marked as invalid
      this.dragClueService.animateDragClueToElementPosition(this.draggedItem);
    }
  }
  notifyDragEnd(dragEndEvent) {
    this.treeview.nodeDragEnd.emit(dragEndEvent);
  }
  getTargetTreeView(dropTarget) {
    const treeViewTagName = this.treeview.element.nativeElement.tagName;
    const targetTreeView = closestWithMatch(dropTarget, treeViewTagName);
    return [this.treeview, ...this.dropZoneTreeViews].find(treeView => isPresent(treeView) && treeView.element.nativeElement === targetTreeView);
  }
  disableAnimationsForNextTick(treeView) {
    // the treeView.animate getter returns `true` when the animations are turned off
    // confusing, but seems on purpose (the `animate` prop sets the value of the @.disabled host-bound attribute)
    if (treeView.animate) {
      return;
    }
    treeView.animate = false;
    this.zone.runOutsideAngular(() => setTimeout(() => treeView.animate = true));
  }
  shouldInitiateDragStart(currentPointerCoords) {
    if (!isPresent(this.pendingDragStartEvent)) {
      return false;
    }
    const distanceFromPointerDown = Math.sqrt(Math.pow(this.pendingDragStartEvent.clientX - currentPointerCoords.clientX, 2) + Math.pow(this.pendingDragStartEvent.clientY - currentPointerCoords.clientY, 2));
    return distanceFromPointerDown >= this.startDragAfter;
  }
  initiateDragStart() {
    if (hasObservers(this.treeview.nodeDragStart)) {
      const dragStartEvent = this.zone.run(() => this.notifyDragStart(this.pendingDragStartEvent, getDropTarget(this.pendingDragStartEvent)));
      if (dragStartEvent.isDefaultPrevented()) {
        this.pendingDragStartEvent = null;
        this.draggedItem = null;
        return;
      }
    }
    this.dragClueService.cancelReturnAnimation();
    this.dragClueService.updateText(this.draggedItem.innerText);
    this.containerOffset = getContainerOffset(this.draggedItem);
    this.pendingDragStartEvent = null;
  }
  static ɵfac = function DragAndDropDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DragAndDropDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(TreeViewComponent), i0.ɵɵdirectiveInject(DragClueService), i0.ɵɵdirectiveInject(DropHintService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DragAndDropDirective,
    selectors: [["", "kendoTreeViewDragAndDrop", ""]],
    contentQueries: function DragAndDropDirective_ContentQueries(rf, ctx, dirIndex) {
      if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, DragClueTemplateDirective, 5)(dirIndex, DropHintTemplateDirective, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dragClueTemplate = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.dropHintTemplate = _t.first);
      }
    },
    hostVars: 8,
    hostBindings: function DragAndDropDirective_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵstyleProp("user-select", ctx.userSelectStyle)("-ms-user-select", ctx.userSelectStyle)("-moz-user-select", ctx.userSelectStyle)("-webkit-user-select", ctx.userSelectStyle);
      }
    },
    inputs: {
      allowCopy: "allowCopy",
      dropZoneTreeViews: "dropZoneTreeViews",
      startDragAfter: "startDragAfter",
      autoScroll: "autoScroll"
    },
    features: [i0.ɵɵProvidersFeature([DragClueService, DropHintService])]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DragAndDropDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewDragAndDrop]',
      providers: [DragClueService, DropHintService],
      standalone: true
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.NgZone
  }, {
    type: TreeViewComponent
  }, {
    type: DragClueService
  }, {
    type: DropHintService
  }], {
    allowCopy: [{
      type: Input
    }],
    dropZoneTreeViews: [{
      type: Input
    }],
    startDragAfter: [{
      type: Input
    }],
    autoScroll: [{
      type: Input
    }],
    dragClueTemplate: [{
      type: ContentChild,
      args: [DragClueTemplateDirective, {
        static: false
      }]
    }],
    dropHintTemplate: [{
      type: ContentChild,
      args: [DropHintTemplateDirective, {
        static: false
      }]
    }],
    userSelectStyle: [{
      type: HostBinding,
      args: ['style.user-select']
    }, {
      type: HostBinding,
      args: ['style.-ms-user-select']
    }, {
      type: HostBinding,
      args: ['style.-moz-user-select']
    }, {
      type: HostBinding,
      args: ['style.-webkit-user-select']
    }]
  });
})();
const DEFAULT_FILTER_EXPAND_SETTINGS = {
  maxAutoExpandResults: -1,
  expandMatches: false,
  expandedOnClear: "none"
};
/**
 * Represents a directive that manages the expanded state of the TreeView
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treelist/expanded-state)).
 *
 * @example
 * ```html
 * <kendo-treeview
 *     kendoTreeViewExpandable
 *     expandBy="id"
 *     [(expandedKeys)]="expandedKeys"
 *     [expandOnFilter]="true"
 *     [nodes]="data">
 * </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class ExpandDirective {
  component;
  /**
   * @hidden
   */
  set isExpanded(value) {
    this.component.isExpanded = value;
  }
  /**
   * Defines the item key stored in the `expandedKeys` collection.
   */
  expandKey;
  /**
   * Specifies whether to auto-expand the nodes leading from the root node to each filter result.
   * To fine-tune this behavior, pass a [`FilterExpandSettings`](https://www.telerik.com/kendo-angular-ui/components/treeview/api/filterexpandsettings) object to this input.
   * @default false
   */
  expandOnFilter = false;
  get filterExpandSettings() {
    const settings = isBoolean(this.expandOnFilter) ? {
      enabled: this.expandOnFilter
    } : {
      ...this.expandOnFilter,
      enabled: true
    };
    return Object.assign({}, DEFAULT_FILTER_EXPAND_SETTINGS, settings);
  }
  /**
   * Fires when the `expandedKeys` collection updates.
   */
  expandedKeysChange = new EventEmitter();
  /**
   * Defines the collection that stores the expanded keys.
   */
  expandedKeys;
  subscriptions = new Subscription();
  /**
   * Reflects the internal `expandedKeys` state.
   */
  state = new Set();
  originalExpandedKeys = new Set();
  isFiltered = false;
  /**
   * Holds the last emitted `expandedKeys` collection.
   */
  lastChange;
  constructor(component) {
    this.component = component;
    this.subscriptions.add(merge(this.component.expand.pipe(map(e => ({
      expand: true,
      ...e
    }))), this.component.collapse.pipe(map(e => ({
      expand: false,
      ...e
    })))).subscribe(this.toggleExpand.bind(this)));
    if (this.component.filterStateChange) {
      this.subscriptions.add(this.component.filterStateChange.subscribe(this.handleAutoExpand.bind(this)));
    }
    this.component.isExpanded = (dataItem, index) => this.state.has(this.itemKey({
      dataItem,
      index
    }));
  }
  ngOnChanges(changes) {
    if (isChanged('expandedKeys', changes, false) && changes['expandedKeys'].currentValue !== this.lastChange) {
      this.state = new Set(changes['expandedKeys'].currentValue);
    }
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  /**
   * @hidden
   */
  itemKey(e) {
    if (this.expandKey) {
      if (typeof this.expandKey === "string") {
        return e.dataItem[this.expandKey];
      }
      if (typeof this.expandKey === "function") {
        return this.expandKey(e);
      }
    }
    return e.index;
  }
  toggleExpand({
    index,
    dataItem,
    expand
  }) {
    const key = this.itemKey({
      index,
      dataItem
    });
    const isExpanded = this.state.has(key);
    let notify = false;
    if (isExpanded && !expand) {
      this.state.delete(key);
      notify = true;
    } else if (!isExpanded && expand) {
      this.state.add(key);
      notify = true;
    }
    if (notify) {
      this.notify();
    }
  }
  handleAutoExpand({
    nodes,
    matchCount,
    term
  }) {
    if (!this.filterExpandSettings.enabled) {
      return;
    }
    const {
      maxAutoExpandResults,
      expandMatches: autoExpandMatches,
      expandedOnClear
    } = this.filterExpandSettings;
    if (!this.isFiltered) {
      this.originalExpandedKeys = new Set(this.state);
    }
    const exitingFilteredState = this.isFiltered && !term;
    const maxExceeded = maxAutoExpandResults !== -1 && matchCount > maxAutoExpandResults;
    const exitAutoExpandedState = exitingFilteredState || maxExceeded;
    if (exitAutoExpandedState) {
      switch (expandedOnClear) {
        case "initial":
          {
            if (!sameValues(this.state, this.originalExpandedKeys)) {
              this.state = this.originalExpandedKeys;
              this.notify();
            }
            break;
          }
        case "all":
          {
            this.state = new Set(nodes.reduce((acc, rootNode) => {
              this.getEveryExpandKey(acc, rootNode);
              return acc;
            }, []));
            this.notify();
            break;
          }
        case "unchanged":
          {
            break;
          }
        case "none":
        default:
          {
            if (this.state.size !== 0) {
              this.state.clear();
              this.notify();
            }
            break;
          }
      }
      this.isFiltered = false;
      return;
    }
    const indicesToExpand = new Set(nodes.reduce((acc, rootNode) => {
      this.updateExpandedNodes(acc, rootNode, autoExpandMatches);
      return acc;
    }, []));
    if (!sameValues(this.state, indicesToExpand)) {
      this.state = indicesToExpand;
      this.notify();
    }
    this.isFiltered = true;
  }
  /**
   * Fills array with the correct expand keys according to wrapper metadata.
   */
  updateExpandedNodes = (collection, node, autoExpandMatches) => {
    if (node.containsMatches || node.isMatch && autoExpandMatches && isArrayWithAtLeastOneItem(node.children)) {
      collection.push(this.itemKey({
        dataItem: node.dataItem,
        index: node.index
      }));
    }
    if (isArrayWithAtLeastOneItem(node.children)) {
      node.children.forEach(child => {
        this.updateExpandedNodes(collection, child, autoExpandMatches);
      });
    }
  };
  /**
   * Fills array with the expand key of every node.
   */
  getEveryExpandKey = (collection, node) => {
    if (isArrayWithAtLeastOneItem(node.children)) {
      collection.push(this.itemKey({
        dataItem: node.dataItem,
        index: node.index
      }));
    }
    if (isArrayWithAtLeastOneItem(node.children)) {
      node.children.forEach(child => {
        this.getEveryExpandKey(collection, child);
      });
    }
  };
  notify() {
    this.lastChange = Array.from(this.state);
    this.expandedKeysChange.emit(this.lastChange);
  }
  static ɵfac = function ExpandDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ExpandDirective)(i0.ɵɵdirectiveInject(ExpandableComponent));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: ExpandDirective,
    selectors: [["", "kendoTreeViewExpandable", ""]],
    inputs: {
      isExpanded: "isExpanded",
      expandKey: [0, "expandBy", "expandKey"],
      expandOnFilter: "expandOnFilter",
      expandedKeys: "expandedKeys"
    },
    outputs: {
      expandedKeysChange: "expandedKeysChange"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExpandDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewExpandable]',
      standalone: true
    }]
  }], () => [{
    type: ExpandableComponent
  }], {
    isExpanded: [{
      type: Input
    }],
    expandKey: [{
      type: Input,
      args: ["expandBy"]
    }],
    expandOnFilter: [{
      type: Input
    }],
    expandedKeysChange: [{
      type: Output
    }],
    expandedKeys: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 * Performs the right-to-left function composition. Functions must have a unary.
 */
const compose = (...args) => data => args.reduceRight((acc, curr) => curr(acc), data);

/**
 * @hidden
 */
const copyPageSize = (treeview, source, target) => {
  if (!isPresent(treeview.loadMoreService)) {
    return;
  }
  const sourceGroupSize = treeview.getNodePageSize(source);
  treeview.setNodePageSize(target, sourceGroupSize);
};
/**
 * @hidden
 */
const incrementPageSize = (treeview, dataItem) => {
  if (!isPresent(treeview.loadMoreService)) {
    return;
  }
  const currentPageSize = treeview.getNodePageSize(dataItem);
  treeview.setNodePageSize(dataItem, currentPageSize + 1);
};
/**
 * @hidden
 */
const decrementPageSize = (treeview, dataItem) => {
  if (!isPresent(treeview.loadMoreService)) {
    return;
  }
  const currentPageSize = treeview.getNodePageSize(dataItem);
  treeview.setNodePageSize(dataItem, currentPageSize - 1);
};

/**
 * @hidden
 */
class FlatEditingService {
  flatBinding;
  movedItemNewIndex;
  constructor(flatBinding) {
    this.flatBinding = flatBinding;
  }
  add({
    sourceItem,
    destinationItem,
    dropPosition,
    sourceTree,
    destinationTree
  }) {
    // shallow clone the item as not to mistake it for its 'older' version when the remove handler kicks in to splice the item at its old position
    const clonedSourceDataItem = Object.assign({}, getDataItem(sourceItem));
    if (dropPosition === DropPosition.Over) {
      // expand the item that was dropped into
      expandDropTarget(destinationItem, destinationTree);
      const destinationItemId = getter(this.flatBinding.idField)(getDataItem(destinationItem));
      setter(this.flatBinding.parentIdField)(clonedSourceDataItem, destinationItemId);
      const lastChildNodeIndex = this.getLastVisibleChildNodeIndex(destinationTree, this.flatBinding.originalData, getDataItem(destinationItem));
      // insert after the last visible child
      const targetIndex = lastChildNodeIndex + 1;
      this.flatBinding.originalData.splice(targetIndex, 0, clonedSourceDataItem);
      // rebind the treeview data before searching for the focus target index
      this.rebindData();
      const focusTarget = this.fetchChildNodes(getDataItem(destinationItem), destinationTree).indexOf(clonedSourceDataItem);
      this.movedItemNewIndex = buildTreeIndex(destinationItem.item.index, focusTarget);
    } else {
      const shiftIndex = dropPosition === DropPosition.After ? 1 : 0;
      const targetIndex = this.flatBinding.originalData.indexOf(getDataItem(destinationItem)) + shiftIndex;
      this.flatBinding.originalData.splice(targetIndex, 0, clonedSourceDataItem);
      const destinationItemParentId = getter(this.flatBinding.parentIdField)(getDataItem(destinationItem));
      setter(this.flatBinding.parentIdField)(clonedSourceDataItem, destinationItemParentId);
      // rebind the treeview data before searching for the focus target index
      this.rebindData();
      const parentIndex = destinationItem.parent ? destinationItem.parent.item.index : null;
      const parentContainer = destinationItem.parent ? this.fetchChildNodes(getDataItem(destinationItem.parent), destinationTree) : destinationTree.nodes;
      const focusTarget = parentContainer.indexOf(clonedSourceDataItem);
      this.movedItemNewIndex = buildTreeIndex(parentIndex, focusTarget);
    }
    if (sourceTree !== destinationTree) {
      this.addChildNodes(clonedSourceDataItem, sourceTree);
    }
    // increment the parent page size => an item is moved into it
    const updatedParent = dropPosition === DropPosition.Over ? getDataItem(destinationItem) : getDataItem(destinationItem.parent);
    incrementPageSize(destinationTree, updatedParent);
    // the page sizes are stored by data-item reference => copy the old item ref page size to the new item reference
    copyPageSize(destinationTree, getDataItem(sourceItem), clonedSourceDataItem);
    // the source tree nodes are reloaded on `removeItem` - reload the destination tree nodes if the soruce and the destination tree are different
    if (sourceTree !== destinationTree && !destinationTree.loadOnDemand) {
      destinationTree.preloadChildNodes();
    }
    // if the source and destination trees are the same, focusing the moved item here will not have the desired effect
    // as the `remove` handler has not yet kicked-in to remove the item from its old position
    if (sourceTree !== destinationTree) {
      // ensure the focus target is rendered and registered
      destinationTree.changeDetectorRef.detectChanges();
      destinationTree.focus(this.movedItemNewIndex);
    }
  }
  remove({
    sourceItem,
    sourceTree,
    destinationTree
  }) {
    const sourceDataItem = getDataItem(sourceItem);
    const sourceItemIndex = this.flatBinding.originalData.indexOf(sourceDataItem);
    this.flatBinding.originalData.splice(sourceItemIndex, 1);
    if (sourceTree !== destinationTree) {
      this.removeChildNodes(sourceDataItem, sourceTree);
    }
    this.rebindData();
    // emit collapse for the parent node if its last child node was spliced
    const parentChildren = sourceItem.parent ? sourceItem.parent.children : [];
    collapseEmptyParent(sourceItem.parent, parentChildren, sourceTree);
    // decrement source item parent page size => an item has been removed from it
    decrementPageSize(sourceTree, getDataItem(sourceItem.parent));
    // reload the treeview nodes
    if (!sourceTree.loadOnDemand) {
      sourceTree.preloadChildNodes();
    }
    // if the source and destination trees are different we want to focus only the moved item in the destination tree
    if (sourceTree === destinationTree) {
      // ensure the focus target is rendered and registered
      destinationTree.changeDetectorRef.detectChanges();
      // after the source item is removed from its original position, the candidate index might have to be corrected
      const index = updateMovedItemIndex(this.movedItemNewIndex, sourceItem.item.index);
      destinationTree.focus(index);
    }
  }
  addChildNodes(dataItem, source) {
    const itemChildren = this.fetchAllDescendantNodes(dataItem, source);
    this.flatBinding.originalData.push(...itemChildren);
  }
  removeChildNodes(dataItem, source) {
    const sourceChildren = this.fetchAllDescendantNodes(dataItem, source);
    sourceChildren.forEach(item => {
      const index = this.flatBinding.originalData.indexOf(item);
      this.flatBinding.originalData.splice(index, 1);
    });
  }
  fetchAllDescendantNodes(node, treeview) {
    let nodes = this.fetchChildNodes(node, treeview);
    nodes.forEach(node => nodes = nodes.concat(this.fetchAllDescendantNodes(node, treeview) || []));
    return nodes;
  }
  fetchChildNodes(node, treeview) {
    if (!node) {
      return [];
    }
    let nodes = [];
    treeview.children(node).pipe(take(1)).subscribe(children => nodes = nodes.concat(children || []));
    return nodes;
  }
  getLastVisibleChildNodeIndex(treeview, data, node) {
    if (!isPresent(treeview.loadMoreService) || !treeview.hasChildren(node)) {
      return data.length;
    }
    const visibleNodesCount = treeview.loadMoreService.getGroupSize(node);
    const visibleChildren = this.fetchChildNodes(node, treeview).slice(0, visibleNodesCount);
    const lastNode = visibleChildren[visibleChildren.length - 1];
    const lastNodeIndex = data.indexOf(lastNode);
    return lastNodeIndex;
  }
  rebindData() {
    this.flatBinding.nodes = this.flatBinding.originalData;
    this.flatBinding.updateNodes(this.flatBinding.originalData);
  }
}

/**
 * @hidden
 */
const DEFAULT_FILTER_SETTINGS = {
  operator: 'contains',
  ignoreCase: true,
  mode: "lenient"
};

/**
 * @hidden
 */
class FilteringBase {
  component;
  /**
   * Stores the unfiltered nodes
   */
  filterData;
  visibleNodes = new Set();
  currentFilterTerm = '';
  /**
   * The settings which are applied when performing a filter on the component's data.
   */
  set filterSettings(settings) {
    this._filterSettings = {
      ...DEFAULT_FILTER_SETTINGS,
      ...settings
    };
    this.handleFilterChange(this.currentFilterTerm);
  }
  get filterSettings() {
    return this._filterSettings;
  }
  _filterSettings = DEFAULT_FILTER_SETTINGS;
  /**
   * Applies a filter and changes the visibility of the component's nodes accordingly.
   */
  set filter(term) {
    this.handleFilterChange(term);
  }
  constructor(component) {
    this.component = component;
  }
  /**
   * @hidden
   */
  handleFilterChange(term) {
    this.currentFilterTerm = term || '';
    if (!this.filterData) {
      return;
    }
    this.currentFilterTerm = term;
    this.resetNodesVisibility(this.filterData);
    if (term) {
      filterTree(this.filterData, term, this.filterSettings, this.component.textField);
    }
    this.updateVisibleNodes(this.filterData);
    if (isPresent(this.component.filterStateChange)) {
      this.component.filterStateChange.emit({
        nodes: this.filterData,
        matchCount: this.visibleNodes.size,
        term,
        filterSettings: this.filterSettings
      });
    }
  }
  updateVisibleNodes(items) {
    items.forEach(wrapper => {
      if (wrapper.visible) {
        this.visibleNodes.add(wrapper.dataItem);
      }
      if (wrapper.children) {
        this.updateVisibleNodes(wrapper.children);
      }
    });
  }
  resetNodesVisibility(items) {
    this.visibleNodes.clear();
    items.forEach(wrapper => {
      wrapper.visible = true;
      if (wrapper.children) {
        this.resetNodesVisibility(wrapper.children);
      }
    });
  }
  static ɵfac = function FilteringBase_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || FilteringBase)(i0.ɵɵdirectiveInject(DataBoundComponent));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: FilteringBase,
    inputs: {
      filterSettings: "filterSettings",
      filter: "filter"
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FilteringBase, [{
    type: Directive
  }], () => [{
    type: DataBoundComponent
  }], {
    filterSettings: [{
      type: Input
    }],
    filter: [{
      type: Input
    }]
  });
})();
const findChildren = (prop, nodes, value) => nodes.filter(x => prop(x) === value);
const indexBuilder$1 = new IndexBuilderService();
const mapToTree = (currentLevelNodes, allNodes, parentIdField, idField, parent = null, parentIndex = '') => {
  if (!isArrayWithAtLeastOneItem(currentLevelNodes)) {
    return [];
  }
  return currentLevelNodes.map((node, idx) => {
    const index = indexBuilder$1.nodeIndex(idx.toString(), parentIndex);
    const wrapper = {
      dataItem: node,
      index,
      parent,
      visible: true
    };
    wrapper.children = mapToTree(findChildren(getter(parentIdField), allNodes || [], getter(idField)(node)), allNodes, parentIdField, idField, wrapper, index);
    return wrapper;
  });
};
/**
 * Represents a directive that handles flat data binding and provides built-in filtering functionality for the TreeView.
 *
 * Use this directive to bind flat data where parent-child relationships are defined by ID fields.
 * The directive also enables the built-in filter input and automatic filter handling when used with the `filterable` property.
 *
 * @example
 * ```html
 * <kendo-treeview
 *     kendoTreeViewFlatDataBinding
 *     idField="id"
 *     parentIdField="parentId"
 *     textField="text"
 *     [nodes]="data"
 *     [filterable]="true">
 *  </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class FlatDataBindingDirective extends FilteringBase {
  component;
  /**
   * Specifies the flat data collection that renders as nodes in the TreeView.
   */
  set nodes(nodes) {
    // Needs to be a setter so that it can be accessed via `super` call (typescript v5)
    this._nodes = nodes;
  }
  get nodes() {
    return this._nodes;
  }
  _nodes;
  /**
   * Specifies the field name that contains the parent node identifier.
   */
  parentIdField;
  /**
   * Specifies the field name that contains the unique node identifier.
   */
  idField;
  /**
   * @hidden
   */
  loadOnDemand = true;
  /**
   * @hidden
   * A callback which determines whether a TreeView node should be rendered as hidden.
   */
  set isVisible(fn) {
    this.component.isVisible = fn;
  }
  /**
   * @hidden
   */
  originalData = [];
  constructor(component) {
    super(component);
    this.component = component;
    this.component.isVisible = node => this.visibleNodes.has(node);
  }
  /**
   * @hidden
   */
  ngOnInit() {
    if (isPresent(this.parentIdField) && isPresent(this.idField)) {
      const fetchChildren = node => findChildren(getter(this.parentIdField), this.originalData || [], getter(this.idField)(node));
      this.component.hasChildren = node => fetchChildren(node).length > 0;
      this.component.children = node => of(fetchChildren(node));
      this.component.editService = new FlatEditingService(this);
      this.component.filterChange.subscribe(this.handleFilterChange.bind(this));
      if (this.component.filter) {
        this.handleFilterChange(this.component.filter);
      }
      if (!this.loadOnDemand && isPresent(this.component.preloadChildNodes)) {
        this.component.preloadChildNodes();
      }
    }
  }
  /**
   * @hidden
   */
  ngOnChanges(changes) {
    if (isChanged('parentIdField', changes, false)) {
      this.nodes = this.originalData;
      this.updateNodes(this.originalData);
    }
    if (isChanged('nodes', changes, false)) {
      this.updateNodes(changes['nodes'].currentValue);
    }
    // should react to changes.loadOnDemand as well - should preload the data or clear the already cached items
    if (anyChanged(['nodes', 'loadOnDemand'], changes) && !this.loadOnDemand && isPresent(this.component.preloadChildNodes)) {
      this.component.preloadChildNodes();
    }
  }
  /**
   * @hidden
   */
  updateNodes(values) {
    this.originalData = values || [];
    if (!isNullOrEmptyString(this.parentIdField)) {
      const prop = getter(this.parentIdField);
      this.component.nodes = this.originalData.filter(compose(isBlank, prop));
      this.filterData = mapToTree(this.component.nodes, this.originalData, this.parentIdField, this.idField);
      if (this.currentFilterTerm) {
        this.handleFilterChange(this.currentFilterTerm);
      } else {
        this.updateVisibleNodes(this.filterData);
      }
    } else {
      this.component.nodes = this.originalData.slice(0);
    }
  }
  static ɵfac = function FlatDataBindingDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || FlatDataBindingDirective)(i0.ɵɵdirectiveInject(DataBoundComponent));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: FlatDataBindingDirective,
    selectors: [["", "kendoTreeViewFlatDataBinding", ""]],
    inputs: {
      nodes: "nodes",
      parentIdField: "parentIdField",
      idField: "idField",
      loadOnDemand: "loadOnDemand",
      isVisible: "isVisible"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FlatDataBindingDirective, [{
    type: Directive,
    args: [{
      selector: "[kendoTreeViewFlatDataBinding]",
      standalone: true
    }]
  }], () => [{
    type: DataBoundComponent
  }], {
    nodes: [{
      type: Input
    }],
    parentIdField: [{
      type: Input
    }],
    idField: [{
      type: Input
    }],
    loadOnDemand: [{
      type: Input
    }],
    isVisible: [{
      type: Input
    }]
  });
})();

/**
 * @hidden
 */
class HierarchyEditingService {
  hierarchyBinding;
  movedItemNewIndex;
  constructor(hierarchyBinding) {
    this.hierarchyBinding = hierarchyBinding;
  }
  add({
    sourceItem,
    destinationItem,
    dropPosition,
    sourceTree,
    destinationTree
  }) {
    // shallow clone the item as not to mistake it for its 'older' version when the remove handler kicks in to splice the item at its old position
    const clonedSourceDataItem = Object.assign({}, getDataItem(sourceItem));
    if (dropPosition === DropPosition.Over) {
      // expand the item that was dropped into
      expandDropTarget(destinationItem, destinationTree);
      const destinationChildren = this.childrenFor(getDataItem(destinationItem));
      // add the moved node just before the load more button if load more is enabled
      const targetIndex = isPresent(destinationTree.loadMoreService) ? Math.min(destinationTree.loadMoreService.getGroupSize(getDataItem(destinationItem)), destinationChildren.length) :
      // the page size might be greater than the actual children array length
      destinationChildren.length;
      destinationChildren.splice(targetIndex, 0, clonedSourceDataItem);
      setter(this.hierarchyBinding.childrenField)(getDataItem(destinationItem), destinationChildren);
      this.movedItemNewIndex = buildTreeIndex(destinationItem.item.index, targetIndex);
      this.rebindData();
    } else {
      const destinationParentNodes = this.getParentNodes(destinationItem, destinationTree);
      const shiftIndex = dropPosition === DropPosition.After ? 1 : 0;
      const targetIndex = destinationParentNodes.indexOf(getDataItem(destinationItem)) + shiftIndex;
      destinationParentNodes.splice(targetIndex, 0, clonedSourceDataItem);
      const parentIndex = destinationItem.parent ? destinationItem.parent.item.index : null;
      this.movedItemNewIndex = buildTreeIndex(parentIndex, targetIndex);
      this.rebindData();
    }
    // increment the parent page size => an item is moved into it
    const updatedParent = dropPosition === DropPosition.Over ? getDataItem(destinationItem) : getDataItem(destinationItem.parent);
    incrementPageSize(destinationTree, updatedParent);
    // the page sizes are stored by data-item reference => copy the old item ref page size to the new item reference
    copyPageSize(destinationTree, getDataItem(sourceItem), clonedSourceDataItem);
    // the source tree nodes are reloaded on `removeItem` - reload the destination tree nodes if the soruce and the destination tree are different
    if (sourceTree !== destinationTree && !destinationTree.loadOnDemand) {
      destinationTree.preloadChildNodes();
    }
    // if the source and destination trees are the same, focusing the moved item here will not have the desired effect
    // as the `remove` handler has not yet kicked-in to remove the item from its old position
    if (sourceTree !== destinationTree) {
      // ensure the focus target is rendered and registered
      destinationTree.changeDetectorRef.detectChanges();
      destinationTree.focus(this.movedItemNewIndex);
    }
  }
  remove({
    sourceItem,
    sourceTree,
    destinationTree
  }) {
    const sourceParentNodes = this.getParentNodes(sourceItem, sourceTree);
    const sourceItemIndex = sourceParentNodes.indexOf(getDataItem(sourceItem));
    sourceParentNodes.splice(sourceItemIndex, 1);
    this.rebindData();
    // emit collapse for the parent node if its last child node was spliced
    collapseEmptyParent(sourceItem.parent, sourceParentNodes, sourceTree);
    // decrement source item parent page size => an item has been removed from it
    decrementPageSize(sourceTree, getDataItem(sourceItem.parent));
    // reload the treeview nodes
    if (!sourceTree.loadOnDemand) {
      sourceTree.preloadChildNodes();
    }
    // if the source and destination trees are different we want to focus only the moved item in the destination tree
    if (sourceTree === destinationTree) {
      // ensure the focus target is rendered and registered
      destinationTree.changeDetectorRef.detectChanges();
      // after the source item is removed from its original position, the candidate index might have to be corrected
      const index = updateMovedItemIndex(this.movedItemNewIndex, sourceItem.item.index);
      destinationTree.focus(index);
    }
  }
  getParentNodes(node, treeView) {
    return isPresent(node.parent) ? this.childrenFor(getDataItem(node.parent)) : treeView.nodes;
  }
  childrenFor(dataItem) {
    return getter(this.hierarchyBinding.childrenField)(dataItem) || [];
  }
  rebindData() {
    this.hierarchyBinding.updateNodes(this.hierarchyBinding.originalData);
  }
}
const indexBuilder = new IndexBuilderService();
const mapToWrappers = (currentLevelNodes, childrenField, parent = null, parentIndex = '') => {
  if (!isArrayWithAtLeastOneItem(currentLevelNodes)) {
    return [];
  }
  return currentLevelNodes.map((node, idx) => {
    const index = indexBuilder.nodeIndex(idx.toString(), parentIndex);
    const wrapper = {
      dataItem: node,
      index,
      parent,
      visible: true
    };
    wrapper.children = mapToWrappers(getter(childrenField)(node), childrenField, wrapper, index);
    return wrapper;
  });
};
/**
 * Represents a directive that handles hierarchical data binding and provides built-in filtering functionality for the TreeView.
 *
 * Use this directive to bind hierarchical data where child nodes are nested within parent nodes.
 * The directive also enables the built-in filter input and automatic filter handling when used with the `filterable` property.
 *
 * @example
 * ```html
 * <kendo-treeview
 *     kendoTreeViewHierarchyBinding
 *     childrenField="items"
 *     textField="text"
 *     [nodes]="data"
 *     [filterable]="true">
 *  </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class HierarchyBindingDirective extends FilteringBase {
  component;
  dragAndDropDirective;
  /**
   * Specifies the field name of the parent node that holds the child data items.
   */
  set childrenField(value) {
    if (!value) {
      throw new Error("'childrenField' cannot be empty");
    }
    this._childrenField = value;
  }
  /**
   * Specifies the field name of the parent node that holds the child data items.
   */
  get childrenField() {
    return this._childrenField;
  }
  /**
   * Specifies the hierarchical data displayed by the TreeView.
   */
  set nodes(nodes) {
    // Needs to be a setter so that it can be accessed via `super` call (typescript v5)
    this._nodes = nodes;
  }
  get nodes() {
    return this._nodes;
  }
  _nodes;
  /**
   * @hidden
   * A callback which determines whether a TreeView node should be rendered as hidden.
   */
  set isVisible(fn) {
    this.component.isVisible = fn;
  }
  /**
   * @hidden
   */
  loadOnDemand = true;
  /**
   * @hidden
   */
  originalData = [];
  _childrenField;
  constructor(component, dragAndDropDirective) {
    super(component);
    this.component = component;
    this.dragAndDropDirective = dragAndDropDirective;
    this.component.isVisible = node => !this.currentFilterTerm || this.visibleNodes.has(node);
  }
  ngOnInit() {
    if (isPresent(this.childrenField)) {
      this.component.children = item => of(getter(this.childrenField)(item));
      this.component.hasChildren = item => {
        const children = getter(this.childrenField)(item);
        return Boolean(children && children.length);
      };
      this.component.editService = new HierarchyEditingService(this);
      this.component.filterChange.subscribe(this.handleFilterChange.bind(this));
      if (this.component.filter) {
        this.handleFilterChange(this.component.filter);
      }
      if (!this.loadOnDemand && isPresent(this.component.preloadChildNodes)) {
        this.component.preloadChildNodes();
      }
    }
  }
  ngOnChanges(changes) {
    if (isChanged('childrenField', changes, false)) {
      this.nodes = this.originalData;
      this.updateNodes(this.originalData);
    }
    if (isChanged('nodes', changes, false)) {
      this.updateNodes(changes['nodes'].currentValue);
    }
    // should react to changes.loadOnDemand as well - should preload the data or clear the already cached items
    if (anyChanged(['nodes', 'loadOnDemand'], changes) && !this.loadOnDemand && isPresent(this.component.preloadChildNodes)) {
      this.component.preloadChildNodes();
    }
  }
  /**
   * @hidden
   */
  updateNodes(values) {
    this.originalData = values || [];
    this.filterData = mapToWrappers(values, this.childrenField) || [];
    if (this.currentFilterTerm) {
      this.handleFilterChange(this.currentFilterTerm);
    } else {
      this.updateVisibleNodes(this.filterData);
    }
  }
  static ɵfac = function HierarchyBindingDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || HierarchyBindingDirective)(i0.ɵɵdirectiveInject(DataBoundComponent), i0.ɵɵdirectiveInject(DragAndDropDirective, 9));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: HierarchyBindingDirective,
    selectors: [["", "kendoTreeViewHierarchyBinding", ""]],
    inputs: {
      childrenField: "childrenField",
      nodes: "nodes",
      isVisible: "isVisible",
      loadOnDemand: "loadOnDemand"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HierarchyBindingDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewHierarchyBinding]',
      standalone: true
    }]
  }], () => [{
    type: DataBoundComponent
  }, {
    type: DragAndDropDirective,
    decorators: [{
      type: Optional
    }, {
      type: Host
    }]
  }], {
    childrenField: [{
      type: Input
    }],
    nodes: [{
      type: Input
    }],
    isVisible: [{
      type: Input
    }],
    loadOnDemand: [{
      type: Input
    }]
  });
})();
const LOAD_MORE_DOC_LINK = 'https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button';
/**
 * Represents the directive that enables you to display only a limited number of nodes per level
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button)).
 *
 * @example
 * ```html
 * <kendo-treeview
 *    ...
 *    kendoTreeViewLoadMore
 *    [pageSize]="10"
 *    [totalRootNodes]="100"
 *    totalField="totalChildren">
 * </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class LoadMoreDirective {
  treeview;
  /**
   * Sets the callback function that runs when the load more button is clicked.
   * Provide a function when you fetch additional nodes on demand
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button#remote-data)).
   *
   */
  set loadMoreNodes(loadMoreNodes) {
    if (typeof loadMoreNodes === 'string') {
      return;
    }
    this.treeview.loadMoreService.loadMoreNodes = loadMoreNodes;
  }
  /**
   * Sets the initial number of nodes to render on each level.
   * Each time the load more button is clicked, the page size increases by this number.
   */
  pageSize;
  /**
   * Sets the total number of root nodes.
   * Use this property when you fetch additional nodes on demand
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button#remote-data)).
   */
  totalRootNodes;
  /**
   * Sets the field that contains the total number of child nodes for the data item.
   * Use this property when you fetch additional nodes on demand
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/load-more-button#remote-data)).
   */
  totalField;
  /**
   * Keeps track of the current page size of each node over expand/collapse cycles.
   */
  pageSizes = new Map();
  /**
   * Used as an identifier for the root page size as the root collection of nodes is not associated with a data item.
   */
  rootLevelId = guid();
  constructor(treeview) {
    this.treeview = treeview;
    this.treeview.loadMoreService = {
      getInitialPageSize: this.getInitalPageSize.bind(this),
      getGroupSize: this.getGroupSize.bind(this),
      setGroupSize: this.setGroupSize.bind(this),
      getTotalNodesCount: this.getTotalNodesCount.bind(this)
    };
  }
  ngOnChanges() {
    this.verifySettings();
  }
  verifySettings() {
    if (!isDevMode()) {
      return;
    }
    if (!isPresent(this.pageSize)) {
      throw new Error(`To use the TreeView \`kendoTreeViewLoadMore\` directive, you need to assign a \`pageSize\` value. See ${LOAD_MORE_DOC_LINK}.`);
    }
    const loadMoreNodes = this.treeview.loadMoreService.loadMoreNodes;
    if (isPresent(loadMoreNodes) && typeof loadMoreNodes !== 'function') {
      throw new Error(`The passed value to the \`kendoTreeViewLoadMore\` directive must be a function that retrieves additional nodes. See ${LOAD_MORE_DOC_LINK}.`);
    }
    if (isPresent(loadMoreNodes) && !isPresent(this.totalField)) {
      throw new Error(`When a function to fetch additional nodes is provided to the \`kendoTreeViewLoadMore\` directive, the \`totalField\` and \`totalRootNodes\` values must also be provided. See ${LOAD_MORE_DOC_LINK}.`);
    }
  }
  getGroupSize(dataItem) {
    const itemKey = dataItem || this.rootLevelId;
    return this.pageSizes.has(itemKey) ? this.pageSizes.get(itemKey) : this.pageSize;
  }
  setGroupSize(dataItem, pageSize) {
    const itemKey = dataItem || this.rootLevelId;
    const normalizedSizeValue = pageSize > 0 ? pageSize : 0;
    this.pageSizes.set(itemKey, normalizedSizeValue);
  }
  getTotalNodesCount(dataItem, loadedNodesCount) {
    if (isPresent(dataItem) && isPresent(this.totalField)) {
      return dataItem[this.totalField];
    } else if (!isPresent(dataItem) && isPresent(this.totalRootNodes)) {
      return this.totalRootNodes;
    } else {
      return loadedNodesCount;
    }
  }
  getInitalPageSize() {
    return this.pageSize;
  }
  static ɵfac = function LoadMoreDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || LoadMoreDirective)(i0.ɵɵdirectiveInject(TreeViewComponent));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: LoadMoreDirective,
    selectors: [["", "kendoTreeViewLoadMore", ""]],
    inputs: {
      loadMoreNodes: [0, "kendoTreeViewLoadMore", "loadMoreNodes"],
      pageSize: "pageSize",
      totalRootNodes: "totalRootNodes",
      totalField: "totalField"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LoadMoreDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewLoadMore]',
      standalone: true
    }]
  }], () => [{
    type: TreeViewComponent
  }], {
    loadMoreNodes: [{
      type: Input,
      args: ['kendoTreeViewLoadMore']
    }],
    pageSize: [{
      type: Input
    }],
    totalRootNodes: [{
      type: Input
    }],
    totalField: [{
      type: Input
    }]
  });
})();

/**
 * Represents the custom messages component of the TreeView.
 *
 * Use this component to override default messages for the TreeView.
 *
 * @example
 * ```html
 * <kendo-treeview>
 *      <kendo-treeview-messages
 *          loadMore="Load more items">
 *      </kendo-treeview-messages>
 * </kendo-treeview>
 * ```
 */
class CustomMessagesComponent extends TreeViewMessages {
  service;
  constructor(service) {
    super();
    this.service = service;
  }
  get override() {
    return true;
  }
  static ɵfac = function CustomMessagesComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || CustomMessagesComponent)(i0.ɵɵdirectiveInject(i1.LocalizationService));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: CustomMessagesComponent,
    selectors: [["kendo-treeview-messages"]],
    features: [i0.ɵɵProvidersFeature([{
      provide: TreeViewMessages,
      useExisting: forwardRef(() => CustomMessagesComponent)
    }]), i0.ɵɵInheritDefinitionFeature],
    decls: 0,
    vars: 0,
    template: function CustomMessagesComponent_Template(rf, ctx) {},
    encapsulation: 2
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CustomMessagesComponent, [{
    type: Component,
    args: [{
      providers: [{
        provide: TreeViewMessages,
        useExisting: forwardRef(() => CustomMessagesComponent)
      }],
      selector: 'kendo-treeview-messages',
      template: ``,
      standalone: true
    }]
  }], () => [{
    type: i1.LocalizationService
  }], null);
})();

/**
 * Manages the in-memory selection state of TreeView nodes
 * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/selection)).
 *
 * @example
 * ```html
 * <kendo-treeview
 *    ...
 *    kendoTreeViewSelectable
 *    selectBy="id"
 *    [(selectedKeys)]="selectedKeys"
 * >
 * </kendo-treeview>
 * ```
 *
 * @remarks
 * Applied to: {@link TreeViewComponent}
 */
class SelectDirective {
  treeView;
  navigationService;
  /**
   * @hidden
   */
  set isSelected(value) {
    this.treeView.isSelected = value;
  }
  /**
   * Sets the item key stored in the `selectedKeys` collection.
   */
  selectKey;
  /**
   * Sets the current selection mode
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/selection)).
   */
  selection;
  /**
   * Defines the collection that stores the selected keys
   * ([see example](https://www.telerik.com/kendo-angular-ui/components/treeview/selection)).
   */
  selectedKeys;
  /**
   * Emits when the `selectedKeys` collection updates.
   */
  selectedKeysChange = new EventEmitter();
  get getAriaMultiselectable() {
    return this.options.mode === 'multiple';
  }
  subscriptions = new Subscription();
  get options() {
    const defaultOptions = {
      enabled: true,
      mode: 'single'
    };
    if (!isPresent(this.selection) || typeof this.selection === 'string') {
      return defaultOptions;
    }
    const selectionSettings = isBoolean(this.selection) ? {
      enabled: this.selection
    } : this.selection;
    return Object.assign(defaultOptions, selectionSettings);
  }
  selectActions = {
    'multiple': e => this.selectMultiple(e),
    'single': e => this.selectSingle(e)
  };
  /**
   * Reflects the internal `selectedKeys` state.
   */
  state = new Set();
  /**
   * Holds the last emitted `selectedKeys` collection.
   */
  lastChange;
  constructor(treeView, navigationService) {
    this.treeView = treeView;
    this.navigationService = navigationService;
    this.subscriptions.add(this.treeView.selectionChange.subscribe(this.select.bind(this)));
    this.treeView.isSelected = (dataItem, index) => this.state.has(this.itemKey({
      dataItem,
      index
    }));
    this.navigationService.deselectAllButCurrentItem.subscribe(node => {
      this.selectSingle(node);
    });
  }
  ngOnChanges(changes) {
    if (isChanged('selectedKeys', changes, false) && changes['selectedKeys'].currentValue !== this.lastChange) {
      this.state = new Set(changes['selectedKeys'].currentValue);
    }
    const isSelectionBooleanTrue = typeof this.selection === 'boolean' && this.selection;
    this.navigationService.selection = isSelectionBooleanTrue ? 'single' : this.selection?.mode;
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  itemKey(e) {
    if (!this.selectKey) {
      return e.index;
    }
    if (typeof this.selectKey === 'string') {
      return e.dataItem[this.selectKey];
    }
    if (typeof this.selectKey === 'function') {
      return this.selectKey(e);
    }
  }
  select(e) {
    const {
      enabled,
      mode
    } = this.options;
    const performSelection = this.selectActions[mode] || noop;
    if (!enabled) {
      return;
    }
    performSelection(e);
  }
  selectSingle(node) {
    const key = this.itemKey(node);
    if (!this.state.has(key)) {
      this.state.clear();
      this.state.add(key);
      this.notify();
    }
  }
  selectMultiple(node) {
    const key = this.itemKey(node);
    const isSelected = this.state.has(key);
    if (!isPresent(key)) {
      return;
    }
    if (isSelected) {
      this.state.delete(key);
    } else {
      this.state.add(key);
    }
    this.notify();
  }
  notify() {
    this.lastChange = Array.from(this.state);
    this.selectedKeysChange.emit(this.lastChange);
  }
  static ɵfac = function SelectDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || SelectDirective)(i0.ɵɵdirectiveInject(TreeViewComponent), i0.ɵɵdirectiveInject(NavigationService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: SelectDirective,
    selectors: [["", "kendoTreeViewSelectable", ""]],
    hostVars: 1,
    hostBindings: function SelectDirective_HostBindings(rf, ctx) {
      if (rf & 2) {
        i0.ɵɵattribute("aria-multiselectable", ctx.getAriaMultiselectable);
      }
    },
    inputs: {
      isSelected: "isSelected",
      selectKey: [0, "selectBy", "selectKey"],
      selection: [0, "kendoTreeViewSelectable", "selection"],
      selectedKeys: "selectedKeys"
    },
    outputs: {
      selectedKeysChange: "selectedKeysChange"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SelectDirective, [{
    type: Directive,
    args: [{
      selector: '[kendoTreeViewSelectable]',
      standalone: true
    }]
  }], () => [{
    type: TreeViewComponent
  }, {
    type: NavigationService
  }], {
    isSelected: [{
      type: Input
    }],
    selectKey: [{
      type: Input,
      args: ['selectBy']
    }],
    selection: [{
      type: Input,
      args: ['kendoTreeViewSelectable']
    }],
    selectedKeys: [{
      type: Input
    }],
    selectedKeysChange: [{
      type: Output
    }],
    getAriaMultiselectable: [{
      type: HostBinding,
      args: ['attr.aria-multiselectable']
    }]
  });
})();

/**
 * Use the `KENDO_TREEVIEW` utility array to add all `@progress/kendo-angular-treeview`-related components and directives to a standalone Angular component.
 *
 * @example
 * ```typescript
 * import { Component } from '@angular/core';
 * import { KENDO_TREEVIEW } from '@progress/kendo-angular-treeview';
 *
 * @Component({
 *   selector: 'my-app',
 *   standalone: true,
 *   imports: [KENDO_TREEVIEW],
 *   template: `<kendo-treeview></kendo-treeview>`
 * })
 * export class AppComponent {}
 * ```
 */
const KENDO_TREEVIEW = [TreeViewComponent, NodeTemplateDirective, CheckDirective, DisableDirective, ExpandDirective, SelectDirective, HierarchyBindingDirective, FlatDataBindingDirective, DragAndDropDirective, DragClueTemplateDirective, DropHintTemplateDirective, DragAndDropEditingDirective, LoadMoreDirective, LoadMoreButtonTemplateDirective, CustomMessagesComponent];

//IMPORTANT: NgModule export kept for backwards compatibility
/**
 * Represents the [NgModule](link:site.data.urls.angular['ngmoduleapi']) definition for the TreeView component.
 *
 * @example
 * ```ts
 * import { NgModule } from '@angular/core';
 * import { BrowserModule } from '@angular/platform-browser';
 * import { TreeViewModule } from '@progress/kendo-angular-treeview';
 * import { AppComponent } from './app.component';
 *
 * @NgModule({
 *   imports: [BrowserModule, TreeViewModule],
 *   declarations: [AppComponent],
 *   bootstrap: [AppComponent]
 * })
 * export class AppModule {}
 * ```
 */
class TreeViewModule {
  static ɵfac = function TreeViewModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || TreeViewModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: TreeViewModule,
    imports: [TreeViewComponent, NodeTemplateDirective, CheckDirective, DisableDirective, ExpandDirective, SelectDirective, HierarchyBindingDirective, FlatDataBindingDirective, DragAndDropDirective, DragClueTemplateDirective, DropHintTemplateDirective, DragAndDropEditingDirective, LoadMoreDirective, LoadMoreButtonTemplateDirective, CustomMessagesComponent],
    exports: [TreeViewComponent, NodeTemplateDirective, CheckDirective, DisableDirective, ExpandDirective, SelectDirective, HierarchyBindingDirective, FlatDataBindingDirective, DragAndDropDirective, DragClueTemplateDirective, DropHintTemplateDirective, DragAndDropEditingDirective, LoadMoreDirective, LoadMoreButtonTemplateDirective, CustomMessagesComponent]
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [IconsService, PopupService, ResizeBatchService, DialogContainerService, DialogService, WindowService, WindowContainerService],
    imports: [TreeViewComponent]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TreeViewModule, [{
    type: NgModule,
    args: [{
      imports: [...KENDO_TREEVIEW],
      exports: [...KENDO_TREEVIEW],
      providers: [IconsService, PopupService, ResizeBatchService, DialogContainerService, DialogService, WindowService, WindowContainerService]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { CheckDirective, CustomMessagesComponent, DataBoundComponent, DisableDirective, DragAndDropDirective, DragAndDropEditingDirective, DragClueService, DragClueTemplateDirective, DropAction, DropHintService, DropHintTemplateDirective, DropPosition, ExpandDirective, ExpandableComponent, FlatDataBindingDirective, HierarchyBindingDirective, KENDO_TREEVIEW, LoadMoreButtonTemplateDirective, LoadMoreDirective, LocalizedMessagesDirective, NodeTemplateDirective, SelectDirective, TreeItemDragEvent, TreeItemDragStartEvent, TreeItemDropEvent, TreeViewComponent, TreeViewModule };